/* eslint-disable no-unused-vars, no-useless-escape, react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';
import Select from 'react-select';
import SearchableDropdown from './SearchableDropdown';
import './Pretest.css';
import MathText from './MathText';
import MathToolbar from './MathToolbar';
import MathField from './MathField';
import { isReadOnly, getSelectedYear } from '../config/academicYear';
import { pretestPreview, pretestPayload, pretestSignature } from './pretestTransfer';

const EXAM_MODULE_URL = 'http://213.145.94.110:5106/';

const QUESTION_CATEGORY_OPTIONS = [
  { value: 'study', label: 'Çalışma Sorusu' },
  { value: 'quiz', label: 'Kısa Sınav Sorusu' },
  { value: 'exam', label: 'Sınav Sorusu' },
  { value: 'homework', label: 'Ödev Sorusu' },
];

function ExamCourseDropdown({ id, value, onChange, loading, courses, placeholder, loadingPlaceholder, label }) {
  return (
    <SearchableDropdown
      id={id}
      flowMenu
      value={value}
      onChange={onChange}
      disabled={loading || courses.length === 0}
      placeholder={loading ? loadingPlaceholder : placeholder}
      searchPlaceholder="Ders adı veya koduyla ara…"
      ariaLabel={label}
      options={courses.map((course) => ({
        value: course.courseId,
        label: `${[course.courseCode, course.courseName].filter(Boolean).join(' - ')}${course.programName ? ` (${course.programName})` : ''}`,
        searchText: `${course.courseCode || ''} ${course.courseName || ''} ${course.programName || ''}`,
      }))}
    />
  );
}

const loadOpenSansFont = async (doc) => {
  try {
    const res = await axios.get(API_ENDPOINTS.FONT_OPENSANS);
    const base64 = res?.data?.base64;
    if (base64 && typeof base64 === 'string') {
      doc.addFileToVFS('OpenSans-Regular.ttf', base64);
      doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
      doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'bold');
      return true;
    }
  } catch (error) {
    console.warn('OpenSans base64 API bulunamadı, public klasörden denenecek.', error?.message || error);
  }

  try {
    const resp = await fetch('/OpenSans-Regular.ttf');
    if (resp.ok) {
      const blob = await resp.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i += 1) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64FromTtf = btoa(binary);
      doc.addFileToVFS('OpenSans-Regular.ttf', base64FromTtf);
      doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
      doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'bold');
      return true;
    }
  } catch (error) {
    console.error('OpenSans font yüklenemedi:', error);
  }
  return false;
};

const setFontSafe = (doc, fontName, fontStyle = 'normal') => {
  try {
    if (fontName === 'opensans-regular' || fontName === 'OpenSans' || fontName === 'OpenSans-Regular') {
      doc.setFont('OpenSans', fontStyle);
    } else {
      doc.setFont(fontName, fontStyle);
    }
  } catch (error) {
    console.warn(`Font ${fontName} kullanılamadı, helvetica ile devam ediliyor.`);
    doc.setFont('helvetica', fontStyle);
  }
};

const sanitizeFilename = (name) => {
  try {
    if (!name) return 'Odev';
    // eslint-disable-next-line no-control-regex
    const illegal = /[<>:"/\\|?*\u0000-\u001F]/g;
    let safe = String(name).normalize('NFC').replace(illegal, ' ');
    safe = safe.replace(/\s+/g, ' ').trim();
    safe = safe.replace(/\s/g, '_');
    safe = safe.replace(/_+/g, '_');
    return safe || 'Odev';
  } catch {
    return 'Odev';
  }
};

const normalizeList = (value) => {
  if (!value) return [];
  const result = [];

  const pushValue = (item) => {
    if (item == null) return;
    if (typeof item === 'string') {
      item
        .split('\n')
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => result.push(part));
      return;
    }
    if (typeof item === 'number') {
      result.push(String(item));
      return;
    }
    if (Array.isArray(item)) {
      item.forEach((nested) => pushValue(nested));
      return;
    }
    if (typeof item === 'object') {
      if (item.week !== undefined) {
        const base = `Hafta ${item.week}`;
        if (item.topic) {
          result.push(`${base} - ${item.topic}`);
        } else {
          result.push(base);
        }
        return;
      }
      if (item.title || item.name) {
        result.push(item.title || item.name);
        return;
      }
      if (item.description) {
        result.push(item.description);
        return;
      }
      Object.values(item).forEach((nested) => pushValue(nested));
    }
  };

  pushValue(value);

  const seen = new Set();
  return result
    .map((item) => item.trim())
    .filter((item) => {
      if (!item) return false;
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
};

const buildOutcomeMappingContext = (mappedOutcomes) => {
  if (!Array.isArray(mappedOutcomes) || mappedOutcomes.length === 0) {
    return '';
  }

  const sections = [
    '## Dersin öğrenme çıktıları VE PROGRAM ÇIKTILARI EŞLEŞTİRMESİ',
    '',
    'Bu ders için tanımlanan Dersin öğrenme çıktıları aşağıdaki program çıktıları ile ilişkilidir:',
    '',
    'ZORUNLU SORU ÜRETİM KURALI:',
    '- Her soruyu aşağıdaki ÖÇlerden en az birini doğrudan ölçecek şekilde üret.',
    '- Soruları ÖÇlere dengeli dağıt; ilgisiz veya eşleştirilemeyecek soru üretme.',
    '- PÇleri uydurma; PÇler yalnızca hedef ÖÇnin güncel matrisinden türetilir.',
    '',
  ];

  mappedOutcomes.forEach((outcome) => {
    if (!outcome) return;
    const loLabel = [outcome.code, outcome.description].filter(Boolean).join(' - ');
    if (loLabel) {
      sections.push(`**${loLabel}**`);
    }

    const programList = Array.isArray(outcome.program_outcomes)
      ? outcome.program_outcomes
        .map((po) => {
          if (!po) return null;
          const poLabel = [po.code, po.description].filter(Boolean).join(': ');
          return poLabel || null;
        })
        .filter(Boolean)
      : [];

    if (programList.length > 0) {
      sections.push(`  → Eşleşen Program Çıktıları: ${programList.join(', ')}`);
      sections.push('');
    } else {
      sections.push('  → Bu Dersin Öğrenme Çıktısı için program çıktısı eşleştirmesi bulunamadı.');
      sections.push('');
    }
  });

  return sections.join('\n');
};

const Pretest = () => {
  const [archiveReadOnly, setArchiveReadOnly] = useState(isReadOnly);
  useEffect(() => {
    const updateReadOnly = () => setArchiveReadOnly(isReadOnly());
    window.addEventListener('academic-year-meta-changed', updateReadOnly);
    return () => window.removeEventListener('academic-year-meta-changed', updateReadOnly);
  }, []);
  const { t } = useTranslation();
  const [pretestEnabled, setPretestEnabled] = useState(true);
  const [pretestFlagLoaded, setPretestFlagLoaded] = useState(false);
  const pretestBlocked = pretestFlagLoaded && pretestEnabled === false;

  // Ana form state'leri
  const [subject, setSubject] = useState('');
  const [questionCount, setQuestionCount] = useState(10);
  const [optionCount, setOptionCount] = useState(4);

  // Dosya yönetimi
  const [allSelectedFiles, setAllSelectedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const manualAiControllerRef = useRef(null);

  // Loading ve error state'leri
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Sonuç state'leri
  const [result, setResult] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiSelectedQuestion, setAiSelectedQuestion] = useState(null);
  const [aiMode, setAiMode] = useState('easy'); // easy | hard | replace | manual
  const [aiOutputText, setAiOutputText] = useState('');
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [aiModifying, setAiModifying] = useState(false);
  const [manualPrompt, setManualPrompt] = useState('');
  const [manualEditData, setManualEditData] = useState(null);

  // Zorluk seviyeli sorular
  const [creatingQuestions, setCreatingQuestions] = useState(false);
  const [questionsResult, setQuestionsResult] = useState(null);

  // Quiz
  const [creatingQuiz, setCreatingQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  // Midterm
  const [creatingMidterm, setCreatingMidterm] = useState(false);
  const [midtermResult, setMidtermResult] = useState(null);
  const [midtermInstructions, setMidtermInstructions] = useState('');
  const [midtermQuestionType, setMidtermQuestionType] = useState('multiple_choice'); // 'multiple_choice', 'classic', 'mixed'
  const [sinavTuru, setSinavTuru] = useState('vize'); // 'vize' | 'donem_sonu'
  const [multipleChoiceCount, setMultipleChoiceCount] = useState(6);
  const [classicCount, setClassicCount] = useState(4);
  const [classicPoints, setClassicPoints] = useState(10);
  const [includeSampleAnswer, setIncludeSampleAnswer] = useState(true);
  const [editingPoints, setEditingPoints] = useState(false);
  const [questionPoints, setQuestionPoints] = useState({});

  // Homework
  const [creatingHomework, setCreatingHomework] = useState(false);
  const [homeworkResult, setHomeworkResult] = useState(null);
  const [homeworkInstructions, setHomeworkInstructions] = useState('');
  const [downloadingHomework, setDownloadingHomework] = useState(false);

  // Ek talimatlar - öntest, çalışma konuları, kısa sınav
  const [pretestInstructions, setPretestInstructions] = useState('');
  const [studyTopicsInstructions, setStudyTopicsInstructions] = useState('');
  const [quizInstructions, setQuizInstructions] = useState('');


  // Sayfa durumu - hangi ekran gösterilecek
  const [currentView, setCurrentView] = useState('difficulty'); // default: Çalışma Konuları

  // Toplu silme için seçim state'leri
  const [selectedPretests, setSelectedPretests] = useState([]);
  const [selectedStudyTopics, setSelectedStudyTopics] = useState([]);
  const [selectedQuizzes, setSelectedQuizzes] = useState([]);
  const [selectedMidterms, setSelectedMidterms] = useState([]);
  const [selectedHomeworks, setSelectedHomeworks] = useState([]);

  // Akademisyenin dersleri
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [generateInEnglish, setGenerateInEnglish] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseError, setCourseError] = useState('');
  const [loadingOutcomeMappings, setLoadingOutcomeMappings] = useState(false);
  const [outcomeMappingsError, setOutcomeMappingsError] = useState('');
  const [outcomeMappings, setOutcomeMappings] = useState([]);
  const [outcomeMappingContext, setOutcomeMappingContext] = useState('');

  const hasOutcomeContext = useMemo(
    () => Boolean(outcomeMappingContext && outcomeMappingContext.trim().length > 0),
    [outcomeMappingContext]
  );

  // Kaydedilmiş öğeler
  const [savedPretests, setSavedPretests] = useState([]);
  const [savedStudyTopics, setSavedStudyTopics] = useState([]);
  const [savedQuizzes, setSavedQuizzes] = useState([]);
  const [savedMidterms, setSavedMidterms] = useState([]);
  const [savedHomeworks, setSavedHomeworks] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [, setSelectedPretest] = useState(null);
  const [, setSelectedStudyTopic] = useState(null);
  const [, setSelectedQuiz] = useState(null);
  const [, setSelectedMidterm] = useState(null);
  const [, setSelectedHomework] = useState(null);

  // Kısa sınav ÖÇ/PÇ eşleşme sonuçları
  const [quizQuestionMatches, setQuizQuestionMatches] = useState([]);

  // Haftalık konular
  const [weeklyTopics, setWeeklyTopics] = useState([]);
  const [selectedWeeklyTopics, setSelectedWeeklyTopics] = useState([]);
  const [loadingWeeklyTopics, setLoadingWeeklyTopics] = useState(false);

  // Kaydedilmişler için dropdown
  const [selectedSavedType, setSelectedSavedType] = useState('');

  const [studySaving, setStudySaving] = useState(false);
  const [studySaveMsg, setStudySaveMsg] = useState('');
  const [studySaveSuccess, setStudySaveSuccess] = useState(false);

  // Sınav havuzuna aktarma ('pretest' | 'quiz' - hangi ekrandan açıldığı)
  const [examPoolSource, setExamPoolSource] = useState(null);
  const [examPoolModalOpen, setExamPoolModalOpen] = useState(false);
  const [examPoolLoading, setExamPoolLoading] = useState(false);
  const [examPoolProgress, setExamPoolProgress] = useState(0);
  const [examPoolProgressMsg, setExamPoolProgressMsg] = useState('');
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState('Öntest');
  const [examPoolError, setExamPoolError] = useState('');
  const [examPoolSuccess, setExamPoolSuccess] = useState('');

  // Manuel Soru Ekleme state'leri
  const [manualQuestionPanelOpen, setManualQuestionPanelOpen] = useState(false);
  const [manualExcelFile, setManualExcelFile] = useState(null);
  const [manualExcelDragOver, setManualExcelDragOver] = useState(false);
  const [manualQuestionSaving, setManualQuestionSaving] = useState(false);
  const [manualQuestionCategory, setManualQuestionCategory] = useState('');
  const [manualExamWeightId, setManualExamWeightId] = useState('');
  const [manualQuestionError, setManualQuestionError] = useState('');
  const [manualQuestionSuccess, setManualQuestionSuccess] = useState('');
  const [savedManualQuestions, setSavedManualQuestions] = useState([]);
  const [expandedManualQuestion, setExpandedManualQuestion] = useState(null);
  const [manualLearningOutcomes, setManualLearningOutcomes] = useState([]);
  const [manualLoadingOutcomes, setManualLoadingOutcomes] = useState(false);
  const [manualQuestionAiMatches, setManualQuestionAiMatches] = useState({});
  const [manualQuestionAiLoading, setManualQuestionAiLoading] = useState({});
  const [bulkManualAiLoading, setBulkManualAiLoading] = useState(false);

  // Öntest veritabanına kaydet (manuel Kaydet butonu)
  const [savingPretest, setSavingPretest] = useState(false);
  const [savePretestMessage, setSavePretestMessage] = useState('');
  const [savePretestSuccess, setSavePretestSuccess] = useState(false);
  const [pretestTransfer, setPretestTransfer] = useState(null);
  const [checkingPretestTransfer, setCheckingPretestTransfer] = useState(false);
  const pretestWriteBusy = useRef(false);
  const pretestDraftKey = `pretest-draft:${sessionStorage.getItem('akademisyen_id') || sessionStorage.getItem('admin_id')}:${getSelectedYear()}:${selectedCourseId}`;
  const pretestChanged = pretestTransfer?.transferred &&
    pretestSignature(previewQuestions) !== pretestTransfer.snapshot;

  useEffect(() => {
    if (result && String(result.course_id) === String(selectedCourseId)) {
      try {
        sessionStorage.setItem(pretestDraftKey, JSON.stringify({ result, questions: previewQuestions, transfer: pretestTransfer }));
      } catch { /* Storage quota does not compromise backend duplicate protection. */ }
    }
  }, [result, previewQuestions, pretestTransfer, pretestDraftKey, selectedCourseId]);

  useEffect(() => {
    if (currentView !== 'pretest' || result || !selectedCourseId) return;
    let draft;
    try { draft = JSON.parse(sessionStorage.getItem(pretestDraftKey) || 'null'); } catch { return; }
    if (!draft?.result || String(draft.result.course_id) !== String(selectedCourseId)) return;
    try {
      const expectedCount = Number(draft.result.question_count || 0);
      const expectedOptions = Number(draft.result.option_count || 4);
      if (!expectedCount || draft.questions?.length !== expectedCount) {
        throw new Error(`${expectedCount || 'İstenen'} soru yerine ${draft.questions?.length || 0} geçerli soru bulundu.`);
      }
      pretestPayload(draft.questions, expectedOptions);
    } catch (draftError) {
      sessionStorage.removeItem(pretestDraftKey);
      setError(`Önceki eksik öntest kaldırıldı: ${draftError.message} Lütfen yeniden oluşturun.`);
      return;
    }
    let cancelled = false;
    setResult(draft.result);
    setPreviewQuestions(draft.questions || []);
    setOptionCount(draft.result.option_count || 4);
    setPretestTransfer(draft.transfer || null);
    setSavePretestMessage('');
    setSavePretestSuccess(false);
    const key = draft.transfer?.id || draft.result.session_id;
    if (key) {
      setCheckingPretestTransfer(true);
      const query = draft.transfer?.pretestId
        ? axios.get(API_ENDPOINTS.GET_PRETEST(draft.transfer.pretestId))
        : axios.get(`${API_BASE_URL}/api/pretest-transfers/${key}`, { params: { course_id: selectedCourseId } });
      query.then(({ data }) => {
        if (cancelled) return;
        const info = data.pretest || data;
        if (info.transferred) {
          setPretestTransfer({ ...draft.transfer, id: info.transfer_id, pretestId: info.id || info.pretest_id,
            version: draft.transfer?.version || info.version,
            snapshot: draft.transfer?.snapshot || pretestSignature(draft.questions || []), transferred: true });
          setSavePretestSuccess(true);
        }
        setCheckingPretestTransfer(false);
      }).catch(err => {
        if (!cancelled) {
          // Fail closed until the user reopens the saved set or retries status via reload.
          setSavePretestMessage(err.response?.data?.error || 'Aktarım durumu doğrulanamadı. Sayfayı yenileyip tekrar deneyin.');
        }
      });
    }
    return () => { cancelled = true; };
  }, [currentView, selectedCourseId, pretestDraftKey]);


  // Pretest flagini getir
  useEffect(() => {
    const fetchFlag = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.PRETEST_PERMISSION);
        const data = await res.json();
        if (data?.success) {
          setPretestEnabled(data.enabled !== false);
        }
      } catch (e) {
        // hata durumunda varsayılan açık kalsın
      } finally {
        setPretestFlagLoaded(true);
      }
    };
    fetchFlag();
  }, []);

  const selectedCourse = useMemo(() => {
    return assignedCourses.find(
      (course) => String(course.courseId) === String(selectedCourseId)
    ) || null;
  }, [assignedCourses, selectedCourseId]);

  useEffect(() => {
    const akademisyenId = sessionStorage.getItem('akademisyen_id');

    if (!akademisyenId) {
      setCourseError(t('pretest.selectCourseError'));
      return;
    }

    const fetchCourses = async () => {
      setLoadingCourses(true);
      setCourseError('');
      try {
        const response = await axios.get(
          API_ENDPOINTS.AKADEMISYEN_ASSIGNED_COURSES_DETAILED(akademisyenId)
        );

        let rawCourses = [];
        if (response.data?.success && Array.isArray(response.data.courses)) {
          rawCourses = response.data.courses;
        } else if (Array.isArray(response.data)) {
          rawCourses = response.data;
        }

        const normalized = rawCourses
          .map((course) => {
            const courseId =
              course.courseId ??
              course.course_id ??
              course.id ??
              '';
            const programId =
              course.programId ??
              course.program_id ??
              '';
            const courseCode =
              course.courseCode ??
              course.course_code ??
              '';
            const courseName =
              course.courseName ??
              course.course_name ??
              '';
            return {
              courseId: String(courseId),
              programId: programId !== null && programId !== undefined && programId !== '' ? String(programId) : '',
              courseCode,
              courseName,
              programName:
                course.programName ??
                course.program_name ??
                '',
              facultyId:
                course.facultyId ??
                course.faculty_id ??
                '',
              facultyName:
                course.facultyName ??
                course.faculty_name ??
                '',
              educationTypeId:
                course.educationTypeId ??
                course.education_type_id ??
                '',
              educationTypeName:
                course.educationTypeName ??
                course.education_type_name ??
                '',
            };
          })
          .filter((course) => course.courseId && course.courseName)
          .filter((course, index, self) =>
            index === self.findIndex(c => c.courseId === course.courseId)
          );

        setAssignedCourses(normalized);

        // Eğer daha önce seçilmiş bir ders varsa ve listede mevcutsa subject'i güncelle
        if (normalized.length > 0 && selectedCourseId) {
          const existing = normalized.find(
            (c) => String(c.courseId) === String(selectedCourseId)
          );
          if (existing) {
            const label = [existing.courseCode, existing.courseName].filter(Boolean).join(' - ');
            setSubject(label);
          }
        }
      } catch (err) {
        console.error('Dersler yüklenirken hata:', err);
        setCourseError(t('pretest.loading'));
        setAssignedCourses([]);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCourseId || !selectedCourse) {
      setSubject('');
      setAllSelectedFiles([]);
      setOutcomeMappings([]);
      setOutcomeMappingContext('');
      setOutcomeMappingsError('');
      setLoadingOutcomeMappings(false);
      return;
    }

    const label = [selectedCourse.courseCode, selectedCourse.courseName].filter(Boolean).join(' - ');
    setSubject(label);
    setAllSelectedFiles([]);
    setOutcomeMappings([]);
    setOutcomeMappingContext('');
    setOutcomeMappingsError('');

    const courseIdForMapping = selectedCourse.courseId || selectedCourseId;

    if (!selectedCourse.programId || !courseIdForMapping) {
      setOutcomeMappingsError(t('pretest.selectCourseError'));
      return;
    }

    let isCancelled = false;

    const fetchOutcomeMappings = async () => {
      setLoadingOutcomeMappings(true);
      setOutcomeMappingsError('');
      try {
        const response = await axios.get(
          API_ENDPOINTS.CHECK_OUTCOME_MAPPINGS(selectedCourse.programId, courseIdForMapping)
        );
        if (isCancelled) return;

        const mapped = Array.isArray(response.data?.mapped_outcomes)
          ? response.data.mapped_outcomes
          : [];

        setOutcomeMappings(mapped);
        setOutcomeMappingContext(buildOutcomeMappingContext(mapped));

        if (mapped.length === 0) {
          setOutcomeMappingsError(t('pretest.loadingOutcomes'));
        }
      } catch (err) {
        console.error('Dersin Öğrenme Çıktısı eşleştirmeleri alınamadı:', err);
        if (!isCancelled) {
          setOutcomeMappings([]);
          setOutcomeMappingContext('');
          setOutcomeMappingsError(t('pretest.loadingOutcomes'));
        }
      } finally {
        if (!isCancelled) {
          setLoadingOutcomeMappings(false);
        }
      }
    };

    fetchOutcomeMappings();

    return () => {
      isCancelled = true;
    };
  }, [selectedCourseId, selectedCourse]);

  // Kaydedilmiş öğeleri yükle
  // Haftalık konuları yükle
  const loadWeeklyTopics = useCallback(async () => {
    if (!selectedCourseId) return;

    try {
      setLoadingWeeklyTopics(true);
      const response = await axios.get(API_ENDPOINTS.WEEKLY_TOPICS(selectedCourseId));
      if (response.data && typeof response.data === 'object') {
        // Haftalık konuları dizi formatına çevir
        const topicsArray = Object.keys(response.data)
          .filter(key => !isNaN(key))
          .map(key => ({
            week: parseInt(key),
            topic: response.data[key].topic || '',
            ders_notlari: response.data[key].ders_notlari || response.data[key].course_notes || null
          }))
          .sort((a, b) => a.week - b.week);
        setWeeklyTopics(topicsArray);
      } else {
        setWeeklyTopics([]);
      }
    } catch (err) {
      console.error('Haftalık konular yüklenirken hata:', err);
      setWeeklyTopics([]);
    } finally {
      setLoadingWeeklyTopics(false);
    }
  }, [selectedCourseId]);

  // Haftalık konu seçimi değiştiğinde PDF'leri yükle
  const handleWeeklyTopicChange = async (week, isChecked) => {
    if (isChecked) {
      // Hafta seçildi - sadece eğer listede yoksa ekle
      setSelectedWeeklyTopics(prev => {
        // Eğer zaten listede varsa, değişiklik yapma
        if (prev.includes(week)) {
          return prev;
        }
        // Yoksa ekle
        return [...prev, week];
      });

      // Yeni haftalık notlar endpoint'ini kullanarak bu haftanın tüm PDF'lerini çek
      if (!selectedCourse || !selectedCourse.programId || !selectedCourse.courseCode) {
        console.warn('Ders bilgileri eksik, haftalık notlar yüklenemiyor');
        return;
      }

      try {
        console.log(`Hafta ${week} için haftalık notlar yükleniyor...`);
        const response = await axios.get(
          API_ENDPOINTS.WEEKLY_NOTES(selectedCourse.programId, selectedCourse.courseCode)
        );

        const normalizeWeekNumber = (value) => {
          if (value === undefined || value === null) return null;
          const weekValue = parseInt(value);
          return isNaN(weekValue) ? null : weekValue;
        };

        // Backend direkt array döndürüyor, response.data direkt array
        const allNotes = Array.isArray(response.data) ? response.data : [];

        if (allNotes.length > 0) {
          // Bu haftaya ait tüm notları filtrele
          const normalizedWeek = normalizeWeekNumber(week);

          if (normalizedWeek === null) {
            console.warn(`Hafta ${week} geçersiz bir değer`);
            return;
          }

          const weekNotes = allNotes.filter(note => {
            const noteWeek = normalizeWeekNumber(note.week_number ?? note.week ?? note.number);
            return noteWeek !== null && noteWeek === normalizedWeek;
          });

          if (weekNotes.length === 0) {
            console.warn(`Hafta ${week} için ders notu bulunamadı`);
            return;
          }

          console.log(`Hafta ${week} için ${weekNotes.length} adet ders notu bulundu`);

          // Her bir PDF'i yükle
          const loadedFiles = [];
          for (const note of weekNotes) {
            try {
              let pdfUrl = note.url || note.file_path;

              if (pdfUrl && pdfUrl.startsWith('PDF/')) {
                pdfUrl = `${API_BASE_URL}/uploads/${pdfUrl}`;
              }

              // URL yoksa veya hala tam URL değilse view endpoint'ini kullan
              if (!pdfUrl || !pdfUrl.startsWith('http')) {
                pdfUrl = `${API_BASE_URL}/api/programs/${selectedCourse.programId}/courses/${encodeURIComponent(selectedCourse.courseCode)}/weekly-notes/${note.id}/view`;
              }

              console.log(`Hafta ${week} PDF yükleniyor: ${pdfUrl}`);

              // PDF'i axios ile yükle
              const pdfResponse = await axios.get(pdfUrl, {
                responseType: 'blob',
                headers: {
                  'Accept': 'application/pdf'
                },
                timeout: 30000
              });

              if (pdfResponse.data && pdfResponse.data instanceof Blob) {
                const blob = pdfResponse.data;
                const fileName = note.name || note.file_name || `Hafta_${week}_${loadedFiles.length + 1}.pdf`;
                const file = new File([blob], fileName, { type: 'application/pdf' });
                // Dosyaya hafta bilgisi ekle (metadata olarak)
                file._weekNumber = week;
                loadedFiles.push(file);
                console.log(`Hafta ${week} PDF başarıyla yüklendi: ${fileName}`);
              }
            } catch (pdfErr) {
              console.error(`Hafta ${week} PDF yüklenirken hata (${note.name || note.id}):`, pdfErr);
              // Bir PDF yüklenemezse diğerlerini yüklemeye devam et
            }
          }

          // Yüklenen tüm dosyaları listeye ekle
          if (loadedFiles.length > 0) {
            setAllSelectedFiles(prev => {
              // Bu haftaya ait eski dosyaları kaldır
              const filtered = prev.filter(f => {
                // Önce metadata'dan hafta bilgisini kontrol et
                if (f._weekNumber !== undefined) {
                  return f._weekNumber !== week;
                }
                // Metadata yoksa dosya isminden kontrol et
                const fileName = f.name || '';
                const weekStr = String(week);
                // Hem "Hafta_X" hem de "hafta_X" formatlarını kontrol et
                const hasWeekPattern = fileName.includes(`Hafta_${weekStr}`) ||
                  fileName.includes(`hafta_${weekStr}`) ||
                  fileName.includes(`Hafta ${weekStr}`) ||
                  fileName.includes(`hafta ${weekStr}`) ||
                  fileName.match(new RegExp(`[^a-zA-Z]${weekStr}[^a-zA-Z]`, 'i'));
                return !hasWeekPattern;
              });
              // Yeni dosyaları ekle
              return [...filtered, ...loadedFiles];
            });
            console.log(`Hafta ${week} için ${loadedFiles.length} adet PDF başarıyla eklendi`);
          }
        } else {
          console.warn(`Hafta ${week} için haftalık notlar bulunamadı`);
        }
      } catch (err) {
        console.error(`Hafta ${week} haftalık notlar yüklenirken hata:`, err);
        setError(`Hafta ${week} ders notları yüklenirken hata oluştu: ${err.message}`);
      }
    } else {
      // Hafta seçimi kaldırıldı - sadece eğer listede varsa kaldır
      setSelectedWeeklyTopics(prev => {
        // Eğer listede yoksa, değişiklik yapma
        if (!prev.includes(week)) {
          return prev;
        }
        // Varsa kaldır
        return prev.filter(w => w !== week);
      });

      // Bu haftanın tüm PDF'lerini listeden kaldır
      setAllSelectedFiles(prev => {
        const filtered = prev.filter(f => {
          // Önce metadata'dan hafta bilgisini kontrol et
          if (f._weekNumber !== undefined) {
            return f._weekNumber !== week;
          }
          // Metadata yoksa dosya isminden kontrol et
          const fileName = f.name || '';
          const weekStr = String(week);
          // Hem "Hafta_X" hem de "hafta_X" formatlarını kontrol et
          const hasWeekPattern = fileName.includes(`Hafta_${weekStr}`) ||
            fileName.includes(`hafta_${weekStr}`) ||
            fileName.includes(`Hafta ${weekStr}`) ||
            fileName.includes(`hafta ${weekStr}`) ||
            fileName.match(new RegExp(`[^a-zA-Z]${weekStr}[^a-zA-Z]`, 'i'));
          return !hasWeekPattern;
        });
        console.log(`Hafta ${week} kaldırıldı, kalan dosyalar:`, filtered.map(f => f.name));
        return filtered;
      });
    }
  };

  const loadSavedItems = useCallback(async () => {
    if (!selectedCourseId) return;

    try {
      setLoadingSaved(true);
      const [pretestsRes, studyTopicsRes, quizzesRes, midtermsRes, homeworksRes] = await Promise.all([
        axios.get(API_ENDPOINTS.GET_PRETESTS(selectedCourseId)),
        axios.get(API_ENDPOINTS.GET_STUDY_TOPICS(selectedCourseId)),
        axios.get(API_ENDPOINTS.GET_QUIZZES(selectedCourseId)),
        axios.get(API_ENDPOINTS.GET_MIDTERMS(selectedCourseId)),
        axios.get(API_ENDPOINTS.GET_HOMEWORKS(selectedCourseId))
      ]);

      if (pretestsRes.data.success) {
        setSavedPretests(pretestsRes.data.pretests || []);
      }
      if (studyTopicsRes.data.success) {
        setSavedStudyTopics(studyTopicsRes.data.study_topics || []);
      }
      if (quizzesRes.data.success) {
        setSavedQuizzes(quizzesRes.data.quizzes || []);
      }
      if (midtermsRes.data.success) {
        setSavedMidterms(midtermsRes.data.midterms || []);
      }
      if (homeworksRes.data.success) {
        setSavedHomeworks(homeworksRes.data.homeworks || []);
      }
    } catch (err) {
      console.error('Kaydedilmiş öğeler yüklenirken hata:', err);
    } finally {
      setLoadingSaved(false);
    }
  }, [selectedCourseId]);

  useEffect(() => {
    if (selectedCourseId) {
      loadSavedItems();
      loadWeeklyTopics();
    } else {
      setSavedPretests([]);
      setSavedStudyTopics([]);
      setSavedQuizzes([]);
      setSavedMidterms([]);
      setSavedHomeworks([]);
      setWeeklyTopics([]);
      setSelectedWeeklyTopics([]);
      setSelectedSavedType(''); // Dropdown'ı sıfırla
    }
  }, [selectedCourseId, loadSavedItems, loadWeeklyTopics]);

  // Ders seçildiğinde İngilizce programsa otomatik tikle
  useEffect(() => {
    if (selectedCourse) {
      const label = [selectedCourse.courseName, selectedCourse.programName].filter(Boolean).join(' ').toUpperCase();
      setGenerateInEnglish(label.includes('İNGİLİZCE') || label.includes('INGILIZCE') || label.includes('ENGLISH'));
    } else {
      setGenerateInEnglish(false);
    }
  }, [selectedCourse]);


  // Klasik sınav tipinde soru puanlarını otomatik hesapla (100 / soru sayısı)
  useEffect(() => {
    if (midtermQuestionType === 'classic' && questionCount > 0) {
      const calculatedPoints = Math.round(100 / questionCount);
      setClassicPoints(calculatedPoints);
    }
  }, [midtermQuestionType, questionCount]);

  const handleCourseSelect = (event) => {
    if (pretestWriteBusy.current) return;
    const value = event.target.value;
    setResult(null);
    setPreviewQuestions([]);
    setPretestTransfer(null);
    setCheckingPretestTransfer(false);
    setSavePretestMessage('');
    setSavePretestSuccess(false);
    setStudySaveMsg('');
    setStudySaveSuccess(false);
    setSelectedCourseId(value);
    setSelectedWeeklyTopics([]);
  };

  const buildCustomInstructions = (base, requireOutcomeMapping = true) => {
    const parts = [];
    if (generateInEnglish) parts.push('Generate all questions and content in English.');
    if (requireOutcomeMapping) {
      parts.push(
        'Her soruyu dersin verilen öğrenme çıktılarından (ÖÇ) en az birini doğrudan ölçecek şekilde üret. ' +
        'ÖÇ ile açık anlamsal ilişkisi olmayan soru üretme; soru kökü kısa ise ilişkiyi seçeneklerde de açıkça koru.'
      );
    }
    if (base && base.trim()) parts.push(base.trim());
    return parts.join('\n');
  };

  const questionForOutcomeMatch = (question) => {
    const options = question?.options || ['A', 'B', 'C', 'D', 'E'].reduce((acc, key) => {
      if (question?.[key]) acc[key] = question[key];
      return acc;
    }, {});
    return {
      question: question?.question || question?.question_text || '',
      options,
      correct_answer: question?.correct_answer || question?.answer || question?.sample_answer || ''
    };
  };

  const validateGeneratedQuestions = async (questions) => {
    if (!selectedCourse?.programId || !selectedCourse?.courseId || !Array.isArray(questions) || questions.length === 0) {
      throw new Error('Ders, program ve soru listesi ÖÇ/PÇ doğrulaması için zorunludur.');
    }
    const { data } = await axios.post(API_ENDPOINTS.MATCH_QUESTIONS_TO_OUTCOMES, {
      program_id: selectedCourse.programId,
      course_id: selectedCourse.courseId,
      questions: questions.map(questionForOutcomeMatch)
    });
    const matches = data?.matches || [];
    if (!data?.success || matches.length !== questions.length ||
        matches.some((item) => !(item.learning_outcomes?.length) || !(item.program_outcomes?.length))) {
      throw new Error(data?.error || 'Üretilen her soru için en az bir ÖÇ ve bağlı PÇ doğrulanamadı.');
    }
    return matches;
  };

  const attachOutcomeMatches = (questions, matches) => questions.map((question, index) => ({
    ...question,
    learning_outcome_ids: matches[index].learning_outcomes.map((item) => item.lo_id),
    program_outcome_ids: matches[index].program_outcomes.map((item) => item.id)
  }));

  const generateUntilOutcomesMatch = async (requestFactory, extractQuestions, expectedCount = null) => {
    let lastError;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      const response = await requestFactory();
      if (!response?.data?.success) {
        throw new Error(response?.data?.error || 'Sorular oluşturulamadı.');
      }
      const questions = extractQuestions(response.data);
      try {
        if (expectedCount !== null && questions.length !== expectedCount) {
          throw new Error(`${expectedCount} soru istendi ancak ${questions.length} geçerli soru üretildi.`);
        }
        const matches = await validateGeneratedQuestions(questions);
        return { response, matches, questions: attachOutcomeMatches(questions, matches) };
      } catch (error) {
        lastError = error;
        if (attempt === 3) break;
      }
    }
    throw new Error(
      `Üç üretim denemesinde tüm sorular için güvenli ÖÇ/PÇ ilişkisi kurulamadı; hiçbir soru kabul edilmedi. ${lastError?.response?.data?.error || lastError?.message || ''}`
    );
  };

  // Dosya yükleme işlevleri
  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).filter(file => {
      const validTypes = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/pdf'];
      return validTypes.includes(file.type);
    });

    setAllSelectedFiles(prev => {
      const existingNames = new Set(prev.map(file => file.name));
      const uniqueNewFiles = newFiles.filter(file => !existingNames.has(file.name));
      return [...prev, ...uniqueNewFiles];
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const removeFile = (index) => {
    setAllSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const buildSelectedSourcesSummary = () => {
    const fileNames = Array.from(
      new Set(
        allSelectedFiles
          .map(file => file?.name)
          .filter(name => typeof name === 'string' && name.trim() !== '')
      )
    );

    const weekDetails = weeklyTopics
      .filter(topic => selectedWeeklyTopics.includes(topic.week))
      .map(topic => ({
        week: topic.week,
        topic: topic.topic || '',
        notes: topic.ders_notlari || topic.course_notes || ''
      }));

    if (fileNames.length === 0 && weekDetails.length === 0) {
      return null;
    }

    return {
      files: fileNames,
      weeks: weekDetails
    };
  };

  const renderOutcomeMappingSection = useCallback(
    (helperText) => {
      if (!selectedCourseId) return null;

      return (
        <>
          {loadingOutcomeMappings && (
            <small className="form-helper-text">
              {t('pretest.loadingOutcomes')}
            </small>
          )}
          {outcomeMappingsError && (
            <small className="form-error-text">{outcomeMappingsError}</small>
          )}
          {!loadingOutcomeMappings && !outcomeMappingsError && outcomeMappings.length > 0 && (
            <>
              {helperText && (
                <small className="form-helper-text">
                  {helperText.replace('{count}', outcomeMappings.length)}
                </small>
              )}
              <div
                className="outcome-mapping-preview"
                style={{
                  marginTop: '12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '16px',
                  backgroundColor: '#f8fafc'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong style={{ color: '#1d3a5f' }}>{t('pretest.matchedOutcomes')}</strong>
                  <span style={{ fontSize: '13px', color: '#475569' }}>{outcomeMappings.length} adet</span>
                </div>
                <div style={{ maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
                  {outcomeMappings.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: '8px 0',
                        borderBottom: '1px dashed #cbd5f5'
                      }}
                    >
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.code} <span style={{ fontWeight: 400 }}>- {item.description}</span>
                      </div>
                      {Array.isArray(item.program_outcomes) && item.program_outcomes.length > 0 && (
                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {item.program_outcomes.map((po) => (
                            <span
                              key={`${item.id}-${po.id}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: '999px',
                                backgroundColor: '#e0f2fe',
                                color: '#0369a1',
                                fontSize: '12px',
                                fontWeight: 600
                              }}
                            >
                              {po.code}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      );
    },
    [selectedCourseId, loadingOutcomeMappings, outcomeMappingsError, outcomeMappings]
  );

  // Ana öntest oluşturma fonksiyonu
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPreviewQuestions([]);

    try {
      if (!selectedCourseId) {
        throw new Error(t('pretest.selectCourseError'));
      }

      if (loadingOutcomeMappings) {
        throw new Error(t('pretest.loadingOutcomes'));
      }

      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('question_count', questionCount);
      formData.append('option_count', optionCount);
      const pretestCustom = buildCustomInstructions(pretestInstructions);
      if (pretestCustom) formData.append('custom_instructions', pretestCustom);

      // Program ve ders bilgilerini ekle (Dersin öğrenme çıktıları eşleştirmeleri için)
      if (selectedCourse) {
        if (selectedCourse.programId) {
          formData.append('program_id', selectedCourse.programId);
        }
        if (selectedCourse.courseCode) {
          formData.append('course_code', selectedCourse.courseCode);
        }
        if (selectedCourse.courseId) {
          formData.append('course_id', selectedCourse.courseId);
        }
      }
      if (hasOutcomeContext) {
        formData.append('structured_context', outcomeMappingContext);
      }

      const { response, matches, questions: parsedQs } = await generateUntilOutcomesMatch(
        () => axios.post(API_ENDPOINTS.PRETEST_GENERATE, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
        (data) => {
          const structuredQs = pretestPreview(data.parsed_questions);
          return structuredQs.length ? structuredQs : parseTestContent(data.test_content);
        },
        questionCount
      );

      if (response.data.success) {
        if (parsedQs.length !== questionCount) {
          throw new Error(`${questionCount} soru istendi ancak ${parsedQs.length} geçerli soru alındı. Lütfen yeniden deneyin.`);
        }
        // Backend kontrolüne ek olarak eski/önbellekteki bir backend yanıtının da
        // eksik şık veya bozuk cevap anahtarıyla ekrana gelmesini engelle.
        pretestPayload(parsedQs, optionCount);
        setPretestTransfer(null);
        setSavePretestSuccess(false);
        setSavePretestMessage('');
        setCheckingPretestTransfer(false);
        setResult({
          ...response.data,
          course_id: selectedCourse.courseId,
          option_count: optionCount,
          parsed_questions: { ...(response.data.parsed_questions || {}), questions: parsedQs }
        });
        setError('');
        setPreviewQuestions(parsedQs);
        setQuizQuestionMatches(matches);
        // Otomatik kaydetme yok: Kullanıcı "Veritabanına Kaydet" butonuna basınca kaydedilir.
      } else {
        throw new Error(response.data.error || 'Öntest oluşturulamadı');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Zorluk seviyeli sorular oluşturma (Açık Uçlu)
  // Zorluk seviyeli form submit handler
  const handleDifficultySubmit = async (e) => {
    e.preventDefault();
    setCreatingQuestions(true);
    setError('');
    setStudySaveMsg('');
    setStudySaveSuccess(false);

    try {
      if (!selectedCourseId) {
        throw new Error(t('pretest.selectCourseError'));
      }

      if (weeklyTopics.length > 0 && selectedWeeklyTopics.length === 0) {
        throw new Error(t('pretest.selectWeeklyTopicFirst'));
      }

      // Dosyalardan içerik çıkar
      const formData = new FormData();
      allSelectedFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('subject', subject);
      formData.append('question_type', 'open_ended');
      formData.append('skip_outcome_matching', 'true');
      formData.append('selected_weeks', JSON.stringify(selectedWeeklyTopics));
      const difficultyCustom = buildCustomInstructions(studyTopicsInstructions, false);
      if (difficultyCustom) formData.append('custom_instructions', difficultyCustom);

      // Program ve ders bilgilerini ekle (Dersin öğrenme çıktıları eşleştirmeleri için)
      if (selectedCourse) {
        if (selectedCourse.programId) {
          formData.append('program_id', selectedCourse.programId);
        }
        if (selectedCourse.courseCode) {
          formData.append('course_code', selectedCourse.courseCode);
        }
        if (selectedCourse.courseId) {
          formData.append('course_id', selectedCourse.courseId);
        }
      }
      const selectedSourcesSummary = buildSelectedSourcesSummary();
      if (selectedSourcesSummary) {
        formData.append('selected_sources', JSON.stringify(selectedSourcesSummary));
      }

      const response = await axios.post(API_ENDPOINTS.DIFFICULTY_CREATE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const questions = response.data?.parsed_questions?.questions || [];

      if (response.data.success) {
        setQuestionsResult({
          ...response.data,
          parsed_questions: { ...(response.data.parsed_questions || {}), questions }
        });
        setQuizQuestionMatches([]);
        // Otomatik kaydetme kaldırıldı - sadece txt indir butonuna basıldığında kaydedilecek
      } else {
        throw new Error(response.data.error || 'Sorular oluşturulamadı');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Bir hata oluştu');
    } finally {
      setCreatingQuestions(false);
    }
  };

  const transferStudyQuestionsToExamModule = async () => {
    const questions = questionsResult?.parsed_questions?.questions || [];
    if (!selectedCourse?.programId || !selectedCourse?.courseId || questions.length === 0) {
      setStudySaveSuccess(false);
      setStudySaveMsg('Aktarım için ders ve oluşturulmuş sorular gerekli.');
      return;
    }

    setStudySaving(true);
    setStudySaveSuccess(false);
    setStudySaveMsg('Çalışma soruları öğrenciler için hazırlanıyor…');
    try {
      const normalizedWeeks = Array.from(new Set(
        (selectedWeeklyTopics || [])
          .map((week) => parseInt(week, 10))
          .filter((week) => !Number.isNaN(week))
      ));
      const levels = [
        { title: 'SEVİYE 1', values: ['SEVİYE 1', 'KOLAY'] },
        { title: 'SEVİYE 2', values: ['SEVİYE 2', 'ORTA'] },
        { title: 'SEVİYE 3', values: ['SEVİYE 3', 'ZOR'] }
      ];
      const preparedQuestions = questions.map((question, index) => ({
        question: String(question.question || question.question_text || '').trim(),
        difficulty: question.difficulty || null,
        week_number: question.week_number ?? question.week ?? (
          normalizedWeeks.length > 0 ? normalizedWeeks[index % normalizedWeeks.length] : null
        )
      }));
      if (preparedQuestions.some((question) => !question.question)) {
        throw new Error('Boş soru metni var. Aktarmadan önce soruları kontrol edin.');
      }
      const sections = levels.map(({ title, values }) => {
        const levelQuestions = preparedQuestions.filter((question) => values.includes(question.difficulty));
        return levelQuestions.length
          ? `=== ${title} ===\n${levelQuestions.map((question, index) => `${index + 1}. ${question.question}`).join('\n\n')}`
          : null;
      }).filter(Boolean);
      const ungrouped = preparedQuestions.filter((question) =>
        !levels.some(({ values }) => values.includes(question.difficulty))
      );
      if (ungrouped.length) {
        sections.push(ungrouped.map((question, index) => `${index + 1}. ${question.question}`).join('\n\n'));
      }

      setStudySaveMsg(`${preparedQuestions.length} çalışma sorusu öğrenci ekranına aktarılıyor…`);
      const { data } = await axios.post(API_ENDPOINTS.SAVE_STUDY_TOPICS, {
        program_id: selectedCourse.programId,
        course_id: selectedCourse.courseId,
        subject: subject || selectedCourse.courseName || selectedCourse.courseCode || 'Çalışma Soruları',
        questions_content: sections.join('\n\n'),
        parsed_questions: { questions: preparedQuestions },
        selected_weeks: normalizedWeeks,
      });
      if (!data?.success || !data.study_topic_id) {
        throw new Error(data?.message || 'Çalışma sorularının kaydedildiği doğrulanamadı.');
      }

      setQuizQuestionMatches([]);
      setStudySaveSuccess(true);
      setStudySaveMsg(`${preparedQuestions.length} çalışma sorusu sınav modülüne aktarıldı ✓`);
    } catch (err) {
      setStudySaveSuccess(false);
      setStudySaveMsg(
        err.response?.data?.message || err.response?.data?.error || err.message ||
        'Sorular sınav modülüne aktarılamadı.'
      );
    } finally {
      setStudySaving(false);
    }
  };

  // Quiz oluşturma
  const handleCreateQuiz = async () => {
    // Ders adı kontrolü
    if (!selectedCourseId) {
      setError(t('pretest.selectCourseError'));
      return;
    }

    setCreatingQuiz(true);
    setError('');

    try {
      const formData = new FormData();
      allSelectedFiles.forEach(file => formData.append('files', file));
      formData.append('subject', subject);
      formData.append('selected_weeks', JSON.stringify(selectedWeeklyTopics));
      formData.append('question_count', String(questionCount));
      formData.append('option_count', String(optionCount));
      const quizCustom = buildCustomInstructions(quizInstructions);
      if (quizCustom) formData.append('custom_instructions', quizCustom);

      // Program ve ders bilgilerini ekle (Dersin öğrenme çıktıları eşleştirmeleri için)
      if (selectedCourse) {
        if (selectedCourse.programId) {
          formData.append('program_id', selectedCourse.programId);
        }
        if (selectedCourse.courseCode) {
          formData.append('course_code', selectedCourse.courseCode);
        }
        if (selectedCourse.courseId) {
          formData.append('course_id', selectedCourse.courseId);
        }
      }
      if (hasOutcomeContext) {
        formData.append('structured_context', outcomeMappingContext);
      }

      const selectedSourcesSummary = buildSelectedSourcesSummary();
      if (selectedSourcesSummary) {
        formData.append('selected_sources', JSON.stringify(selectedSourcesSummary));
      }

      const { response, matches, questions: validatedQuestions } = await generateUntilOutcomesMatch(
        () => axios.post(API_ENDPOINTS.QUIZ_CREATE, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }),
        (data) => data?.parsed_questions?.questions || [],
        questionCount
      );

      if (response.data.success) {
        const normalizedSelectedWeeks = Array.isArray(selectedWeeklyTopics)
          ? selectedWeeklyTopics
              .map((w) => parseInt(w, 10))
              .filter((w) => !Number.isNaN(w))
          : [];
        const questionsWithWeek = validatedQuestions.map((q, idx) => {
          const qWeek = parseInt(q?.week_number ?? q?.week, 10);
          if (!Number.isNaN(qWeek)) return { ...q, week_number: qWeek };
          const fallbackWeek = normalizedSelectedWeeks.length > 0
            ? normalizedSelectedWeeks[idx % normalizedSelectedWeeks.length]
            : null;
          return fallbackWeek ? { ...q, week_number: fallbackWeek } : q;
        });

        const validatedResponseData = {
          ...response.data,
          selected_weeks: response.data.selected_weeks || normalizedSelectedWeeks,
          parsed_questions: {
            ...(response.data.parsed_questions || {}),
            questions: questionsWithWeek
          }
        };
        setQuizResult(validatedResponseData);
        setQuizQuestionMatches(matches);

        // Otomatik olarak veritabanına kaydet
        if (selectedCourse) {
          try {
            const saveResponse = await axios.post(API_ENDPOINTS.SAVE_QUIZ, {
              program_id: selectedCourse.programId,
              course_id: selectedCourse.courseId,
              subject: validatedResponseData.subject || subject,
              quiz_content: validatedResponseData.quiz_content || {},
              parsed_questions: validatedResponseData.parsed_questions,
              question_count: questionCount,
              option_count: optionCount,
              source_content: validatedResponseData.source_content || '',
              source_structured_content: validatedResponseData.source_structured_content || ''
            });

            if (saveResponse.data.success) {
              console.log('Kısa sınav otomatik olarak kaydedildi');
              loadSavedItems(); // Kaydedilmiş öğeleri yeniden yükle
            } else {
              console.warn('Kısa sınav otomatik kaydetme başarısız:', saveResponse.data.error);
            }
          } catch (saveErr) {
            console.error('Kısa sınav otomatik kaydetme hatası:', saveErr);
            // Kaydetme hatası kullanıcıyı engellemez, sadece log'lanır
          }
        }
      } else {
        throw new Error(response.data.error || 'Kısa Sınav oluşturulamadı');
      }
    } catch (err) {
      console.error('Kısa Sınav oluşturma hatası:', err);
      setError(err.response?.data?.error || err.message || 'Kısa sınav oluşturulurken bir hata oluştu');
    } finally {
      setCreatingQuiz(false);
    }
  };

  // Ara sınav oluşturma
  const handleCreateMidterm = async () => {
    // Ders adı kontrolü
    if (!selectedCourseId) {
      setError('Lütfen bir ders seçin');
      return;
    }

    const generationWeeks = Array.from(new Set(selectedWeeklyTopics
      .map((week) => Number(week))
      .filter((week) => Number.isInteger(week) && week > 0)));
    if (generationWeeks.length === 0) {
      setError('Soru oluşturmak için en az bir hafta seçin.');
      return;
    }
    if (allSelectedFiles.some((file) => !generationWeeks.includes(Number(file._weekNumber)))) {
      setError('Haftası belirlenmemiş bir dosya var. Yalnızca seçtiğiniz haftalara ait ders notlarını kullanın.');
      return;
    }

    setCreatingMidterm(true);
    setError('');

    try {
      const formData = new FormData();
      allSelectedFiles.forEach((file) => {
        formData.append('files', file);
        formData.append('file_weeks', String(file._weekNumber));
      });
      formData.append('subject', subject);
      formData.append('selected_weeks', JSON.stringify(generationWeeks));
      formData.append('question_count', String(questionCount));
      formData.append('option_count', String(optionCount));
      formData.append('question_type', midtermQuestionType);

      // Karışık mod için sayıları gönder
      if (midtermQuestionType === 'mixed') {
        formData.append('multiple_choice_count', String(multipleChoiceCount));
        formData.append('classic_count', String(classicCount));
      } else if (midtermQuestionType === 'classic') {
        formData.append('classic_count', String(questionCount));
      } else {
        formData.append('multiple_choice_count', String(questionCount));
      }

      // Klasik soru ayarları
      if (midtermQuestionType === 'classic' || midtermQuestionType === 'mixed') {
        formData.append('classic_points', String(classicPoints));
        formData.append('include_sample_answer', includeSampleAnswer ? 'true' : 'false');
      }

      const midtermCustom = buildCustomInstructions(midtermInstructions);
      if (midtermCustom) formData.append('custom_instructions', midtermCustom);

      // Program ve ders bilgilerini ekle (Dersin öğrenme çıktıları eşleştirmeleri için)
      if (selectedCourse) {
        if (selectedCourse.programId) {
          formData.append('program_id', selectedCourse.programId);
        }
        if (selectedCourse.courseCode) {
          formData.append('course_code', selectedCourse.courseCode);
        }
        if (selectedCourse.courseId) {
          formData.append('course_id', selectedCourse.courseId);
        }
      }
      if (hasOutcomeContext) {
        formData.append('structured_context', outcomeMappingContext);
      }

      const selectedSourcesSummary = buildSelectedSourcesSummary();
      if (selectedSourcesSummary) {
        formData.append('selected_sources', JSON.stringify(selectedSourcesSummary));
      }

      formData.append('sinav_turu', sinavTuru);

      const { response, matches, questions: validatedQuestions } = await generateUntilOutcomesMatch(
        () => axios.post(API_ENDPOINTS.MIDTERM_CREATE, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }),
        (data) => data?.parsed_questions?.questions || [],
        questionCount
      );

      if (response.data.success) {
        // parsed_questions kontrolü
        if (!response.data.parsed_questions || !response.data.parsed_questions.questions || response.data.parsed_questions.questions.length === 0) {
          setError('Sınav soruları oluşturulamadı. Lütfen ders içeriğini ve haftalık konuları kontrol edin.');
          setCreatingMidterm(false);
          return;
        }

        setMidtermResult({
          ...response.data,
          selected_weeks: generationWeeks,
          parsed_questions: {
            ...(response.data.parsed_questions || {}),
            questions: validatedQuestions.map((question) => {
              const generatedWeek = Number(question.week_number ?? question.week);
              if (!generationWeeks.includes(generatedWeek)) {
                throw new Error('Soruların hafta bilgisi seçilen haftalarla uyuşmuyor. Lütfen yeniden oluşturun.');
              }
              return {
                ...question,
                week_number: generatedWeek
              };
            })
          }
        });
        setQuizQuestionMatches(matches);

        // Otomatik kaydetme kaldırıldı - sadece indirme sırasında kaydedilecek
      } else {
        throw new Error(response.data.error || 'Sınav oluşturulamadı');
      }
    } catch (err) {
      console.error('Sınav oluşturma hatası:', err);
      setError(err.response?.data?.error || err.message || 'Sınav oluşturulurken bir hata oluştu');
    } finally {
      setCreatingMidterm(false);
    }
  };

  const handleCreateHomework = async () => {
    if (!selectedCourseId) {
      setError('Lütfen bir ders seçin');
      return;
    }

    setCreatingHomework(true);
    setError('');

    try {
      const formData = new FormData();
      allSelectedFiles.forEach((file) => formData.append('files', file));
      formData.append('subject', subject);
      formData.append('selected_weeks', JSON.stringify(selectedWeeklyTopics));
      formData.append('custom_instructions', buildCustomInstructions(homeworkInstructions));
      formData.append('question_count', String(questionCount));
      formData.append('option_count', String(optionCount));

      if (selectedCourse) {
        if (selectedCourse.programId) {
          formData.append('program_id', selectedCourse.programId);
        }
        if (selectedCourse.courseCode) {
          formData.append('course_code', selectedCourse.courseCode);
        }
        if (selectedCourse.courseId) {
          formData.append('course_id', selectedCourse.courseId);
        }
      }
      if (hasOutcomeContext) {
        formData.append('structured_context', outcomeMappingContext);
      }

      const selectedSourcesSummary = buildSelectedSourcesSummary();
      if (selectedSourcesSummary) {
        formData.append('selected_sources', JSON.stringify(selectedSourcesSummary));
      }

      const selectedWeeklyTopicDetails = weeklyTopics
        .filter((topic) => selectedWeeklyTopics.includes(topic.week))
        .map((topic) => ({
          week: topic.week,
          topic: topic.topic,
        }));

      if (selectedWeeklyTopicDetails.length > 0) {
        formData.append('weekly_topics', JSON.stringify(selectedWeeklyTopicDetails));
      }

      const response = await axios.post(API_ENDPOINTS.HOMEWORK_CREATE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setHomeworkResult(response.data);

        if (selectedCourse) {
          try {
            const saveResponse = await axios.post(API_ENDPOINTS.SAVE_HOMEWORK, {
              program_id: selectedCourse.programId,
              course_id: selectedCourse.courseId,
              subject: response.data.subject || subject,
              homework_content: response.data.homework || null,
              homework_text: response.data.homework_text || '',
              ai_prompt: response.data.ai_prompt || '',
              selected_weeks: response.data.selected_weeks || selectedWeeklyTopics,
              question_count: questionCount,
              option_count: optionCount,
              source_content: response.data.source_content || '',
              source_structured_content: response.data.source_structured_content || '',
              custom_instructions: homeworkInstructions || '',
              processed_files: response.data.processed_files || [],
            });

            if (saveResponse.data.success) {
              console.log('Ödev otomatik olarak kaydedildi');
              loadSavedItems();
            } else {
              console.warn('Ödev otomatik kaydetme başarısız:', saveResponse.data.error);
            }
          } catch (saveErr) {
            console.error('Ödev otomatik kaydetme hatası:', saveErr);
          }
        }
      } else {
        throw new Error(response.data.error || 'Ödev oluşturulamadı');
      }
    } catch (err) {
      console.error('Ödev oluşturma hatası:', err);
      setError(
        err.response?.data?.error ||
        err.message ||
        'Ödev oluşturulurken bir hata oluştu'
      );
    } finally {
      setCreatingHomework(false);
    }
  };

  const handleDownloadHomework = useCallback(async () => {
    if (!homeworkResult) {
      setError('Önce bir ödev oluşturun veya kayıtlardan seçin.');
      return;
    }

    try {
      setDownloadingHomework(true);
      const doc = new jsPDF('p', 'mm', 'a4');
      const fontLoaded = await loadOpenSansFont(doc);
      setFontSafe(doc, fontLoaded ? 'OpenSans' : 'helvetica');
      if (typeof doc.setLanguage === 'function') {
        try {
          doc.setLanguage('tr');
        } catch (err) {
          console.warn('PDF dil ayarı yapılamadı:', err);
        }
      }

      const margin = 18;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - margin * 2;
      let cursorY = margin;

      const ensureSpace = (space = 12) => {
        if (cursorY + space > pageHeight - margin) {
          doc.addPage();
          cursorY = margin;
        }
      };

      const addHeading = (text, size = 18, align = 'left') => {
        if (!text) return;
        ensureSpace(size / 2 + 6);
        doc.setFontSize(size);
        setFontSafe(doc, 'OpenSans', 'bold');
        doc.setTextColor(15, 23, 42);
        if (align === 'center') {
          doc.text(text, pageWidth / 2, cursorY, { align: 'center' });
        } else {
          doc.text(text, margin, cursorY);
        }
        cursorY += size > 16 ? 10 : 8;
      };

      const addParagraph = (text) => {
        if (!text) return;
        const lines = doc.splitTextToSize(text, contentWidth);
        lines.forEach((line) => {
          ensureSpace(6);
          doc.setFontSize(11);
          setFontSafe(doc, 'OpenSans', 'normal');
          doc.setTextColor(55, 65, 81);
          doc.text(line, margin, cursorY);
          cursorY += 6;
        });
        cursorY += 2;
      };

      const addBulletList = (items) => {
        if (!items || !items.length) return;
        items.forEach((item) => {
          const lines = doc.splitTextToSize(item, contentWidth - 10);
          const blockHeight = lines.length * 6 + 4;
          ensureSpace(blockHeight);
          doc.setFontSize(11);
          setFontSafe(doc, 'OpenSans', 'normal');
          doc.setTextColor(55, 65, 81);
          doc.text('•', margin, cursorY);
          lines.forEach((line, index) => {
            doc.text(line, margin + 6, cursorY);
            cursorY += 6;
            if (index === lines.length - 1) {
              cursorY += 2;
            }
          });
        });
      };

      const addKeyValue = (label, value) => {
        if (!value) return;
        ensureSpace(8);
        doc.setFontSize(11);
        setFontSafe(doc, 'OpenSans', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text(`${label}:`, margin, cursorY);
        const lines = doc.splitTextToSize(value, contentWidth - 40);
        setFontSafe(doc, 'OpenSans', 'normal');
        doc.setTextColor(55, 65, 81);
        doc.text(lines, margin + 32, cursorY);
        cursorY += lines.length * 6;
        cursorY += 2;
      };

      const addDivider = () => {
        ensureSpace(8);
        doc.setDrawColor(148, 163, 184);
        doc.setLineWidth(0.4);
        doc.line(margin, cursorY, margin + contentWidth, cursorY);
        cursorY += 6;
      };

      let structuredHomework = homeworkResult.homework;
      if (structuredHomework && typeof structuredHomework === 'string') {
        try {
          structuredHomework = JSON.parse(structuredHomework);
        } catch {
          structuredHomework = null;
        }
      }

      const resolvedSubject = homeworkResult.subject || subject || 'Ödev';
      const resolvedTitle = structuredHomework?.title || resolvedSubject;
      const summary = structuredHomework?.summary || structuredHomework?.overview || '';
      const scenario = structuredHomework?.scenario || '';
      const learningGoals = normalizeList(
        structuredHomework?.learning_goals ||
        structuredHomework?.learning_objectives ||
        structuredHomework?.objectives
      );
      const tasks = Array.isArray(structuredHomework?.tasks)
        ? structuredHomework.tasks.filter(Boolean)
        : [];
      const assessment = Array.isArray(structuredHomework?.assessment)
        ? structuredHomework.assessment.filter(Boolean)
        : [];
      const submissionInstructions = normalizeList(
        structuredHomework?.submission_instructions ||
        structuredHomework?.submission ||
        structuredHomework?.instructions
      );
      const academicIntegrity = normalizeList(structuredHomework?.academic_integrity);
      const selectedWeeksRaw = homeworkResult.selected_weeks || structuredHomework?.selected_weeks || [];
      const selectedWeeksLabels = normalizeList(selectedWeeksRaw).map((item) => {
        if (/^hafta\s+/i.test(item)) {
          return item;
        }
        const numeric = Number(item);
        return Number.isFinite(numeric) ? `Hafta ${numeric}` : item;
      });

      addHeading(resolvedTitle, 20, 'center');
      addParagraph(`Ders: ${resolvedSubject}`);
      if (selectedWeeksLabels.length > 0) {
        addParagraph(`İlgili Haftalar: ${selectedWeeksLabels.join(', ')}`);
      }
      addDivider();

      if (summary) {
        addHeading('Özet', 14);
        addParagraph(summary);
        addDivider();
      }

      if (scenario) {
        addHeading('Senaryo', 14);
        addParagraph(scenario);
        addDivider();
      }

      if (learningGoals.length > 0) {
        addHeading('Öğrenme Hedefleri', 14);
        addBulletList(learningGoals);
        addDivider();
      }

      if (tasks.length > 0) {
        addHeading('Görev Planı', 14);
        tasks.forEach((task, index) => {
          const title = task?.title || task?.name || `Görev ${index + 1}`;
          addHeading(`${index + 1}. ${title}`, 12);
          if (task?.description || task?.details) {
            addParagraph(task.description || task.details);
          }

          const steps = normalizeList(task?.steps);
          if (steps.length > 0) {
            addHeading('Adımlar', 12);
            addBulletList(steps);
          }

          const deliverables = normalizeList(task?.deliverables);
          if (deliverables.length > 0) {
            addHeading('Teslimatlar', 12);
            addBulletList(deliverables);
          }

          if (task?.estimated_time_hours) {
            addKeyValue('Tahmini Süre', `${task.estimated_time_hours} saat`);
          }

          const resources = normalizeList(task?.resources);
          if (resources.length > 0) {
            addHeading('Önerilen Kaynaklar', 12);
            addBulletList(resources);
          }

          addDivider();
        });
      }

      if (assessment.length > 0) {
        addHeading('Değerlendirme Kriterleri', 14);
        assessment.forEach((criteria, index) => {
          const title = criteria?.criteria || criteria?.title || `Kriter ${index + 1}`;
          addHeading(title, 12);
          if (criteria?.weight) {
            addKeyValue('Ağırlık', `%${criteria.weight}`);
          }
          if (criteria?.expectations) {
            addParagraph(criteria.expectations);
          }
          if (criteria?.excellent) addKeyValue('Üstün', criteria.excellent);
          if (criteria?.good) addKeyValue('İyi', criteria.good);
          if (criteria?.satisfactory) addKeyValue('Yeterli', criteria.satisfactory);
          if (criteria?.needs_improvement) addKeyValue('Geliştirilmesi Gereken', criteria.needs_improvement);
          addDivider();
        });
      }

      if (submissionInstructions.length > 0) {
        addHeading('Teslim Talimatları', 14);
        addBulletList(submissionInstructions);
        addDivider();
      }

      if (academicIntegrity.length > 0) {
        addHeading('Akademik Dürüstlük', 14);
        addBulletList(academicIntegrity);
        addDivider();
      }

      let fallbackText = homeworkResult.homework_text;
      if (!structuredHomework && homeworkResult.source_content) {
        fallbackText = fallbackText || homeworkResult.source_content;
      }

      if (!structuredHomework && fallbackText) {
        addHeading('Ödev İçeriği', 14);
        addParagraph(fallbackText);
      }

      if (!structuredHomework && !fallbackText) {
        addParagraph('Ödev içeriği bulunamadı.');
      }

      const filenameBase = structuredHomework?.title || resolvedSubject || 'Odev';
      const fileName = `${sanitizeFilename(filenameBase)}_Odev.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('Ödev PDF oluşturma hatası:', err);
      setError(err?.message || 'Ödev PDF oluşturulurken bir hata oluştu.');
    } finally {
      setDownloadingHomework(false);
    }
  }, [homeworkResult, subject, setError]);

  // Sınav havuzuna aktarma fonksiyonları (source: 'pretest' | 'quiz' - hangi ekrandan açıldığı)
  const openExamPoolModal = async (source = 'pretest') => {
    if (!selectedCourse) {
      setError('Lütfen önce bir ders seçin');
      return;
    }
    setExamPoolSource(source);
    setExamPoolModalOpen(true);
    setExamPoolError('');
    setExamPoolSuccess('');

    // Manuel source: sınav türü zaten panelde seçili, fetch gerekmez
    if (source === 'manual') {
      return;
    }

    // Öntestler her zaman özel Öntest havuzuna aktarılır.
    if (source === 'pretest') {
      setSelectedExamType('Öntest');
      return;
    }

    setExamPoolLoading(true);

    try {
      // Önce sınav türlerini yükle (hızlı, dropdown hemen dolsun)
      const response = await axios.get(API_ENDPOINTS.GET_EXAM_TYPES_FOR_POOL(selectedCourse.courseId));

      // Arka planda ÖÇ/PÇ eşleştirmesini başlat (await etme — yavaş AI çağrısı)
      if (quizQuestionMatches.length === 0 && selectedCourse?.programId && selectedCourse?.courseId) {
        let questionsToMatch = [];
        if (source === 'quiz' && quizResult?.parsed_questions?.questions?.length) {
          questionsToMatch = quizResult.parsed_questions.questions;
        } else if (source === 'midterm' && midtermResult?.parsed_questions?.questions?.length) {
          questionsToMatch = midtermResult.parsed_questions.questions;
        } else if (source === 'pretest') {
          const qs = previewQuestions.length > 0
            ? previewQuestions
            : parseTestContent(result?.test_content || '');
          questionsToMatch = qs;
        }
        if (questionsToMatch.length > 0) {
          axios.post(API_ENDPOINTS.MATCH_QUESTIONS_TO_OUTCOMES, {
            program_id: selectedCourse.programId,
            course_id: selectedCourse.courseId,
            questions: questionsToMatch.map(questionForOutcomeMatch)
          }).then(matchResp => {
            if (matchResp.data.success) setQuizQuestionMatches(matchResp.data.matches || []);
          }).catch(e => {
            console.error('ÖÇ/PÇ eşleştirme hatası:', e);
          });
        }
      }
      if (response.data.success && response.data.exam_types) {
        setExamTypes(response.data.exam_types);
        const validExamTypes = response.data.exam_types.filter(et => et.exam_type);

        if (validExamTypes.length > 0) {
          if (source === 'quiz') {
            const quizType = validExamTypes.find(et =>
              et.exam_type.toLowerCase().includes('kısa') ||
              et.exam_type.toLowerCase().includes('kisa') ||
              et.exam_type.toLowerCase().includes('quiz')
            );
            setSelectedExamType(quizType ? quizType.exam_type : validExamTypes[0].exam_type);
          } else if (source === 'midterm') {
            const midtermType = validExamTypes.find(et =>
              et.exam_type.toLowerCase().includes('ara') ||
              et.exam_type.toLowerCase().includes('vize') ||
              et.exam_type.toLowerCase().includes('midterm')
            );
            setSelectedExamType(midtermType ? midtermType.exam_type : validExamTypes[0].exam_type);
          } else {
            setSelectedExamType(validExamTypes[0].exam_type);
          }
        } else {
          setSelectedExamType('');
          setExamPoolError('Bu ders için exam_weights tablosunda sınav türü tanımlı değil.');
        }
      }
    } catch (err) {
      console.error('Sınav türleri getirme hatası:', err);
      setExamPoolError('Sınav türleri yüklenemedi.');
    } finally {
      setExamPoolLoading(false);
    }
  };

  const closeExamPoolModal = () => {
    setExamPoolModalOpen(false);
    setExamPoolError('');
    setExamPoolSource(null);
    setExamPoolProgress(0);
    setExamPoolProgressMsg('');
  };

  // Manuel Soru Ekleme Fonksiyonları
  const openManualQuestionPanel = async () => {
    if (!selectedCourse) {
      setError('Lütfen önce bir ders seçin');
      return;
    }
    setManualExcelFile(null);
    setManualQuestionError('');
    setManualQuestionSuccess('');
    setManualQuestionCategory('');
    setManualExamWeightId('');
    setSavedManualQuestions([]);
    setExpandedManualQuestion(null);
    setManualQuestionAiMatches({});
    setManualQuestionAiLoading({});
    setManualQuestionPanelOpen(true);

    // ÖÇ'leri fetch et
    setManualLoadingOutcomes(true);
    try {
      const numericCourseId = parseInt(selectedCourse.courseId);
      const response = await axios.get(API_ENDPOINTS.COURSE_OUTCOMES(numericCourseId));
      if (response.data && response.data.learning) {
        const sorted = response.data.learning.sort((a, b) => {
          const aNum = parseInt(a.code.replace('ÖÇ', ''));
          const bNum = parseInt(b.code.replace('ÖÇ', ''));
          return aNum - bNum;
        });
        setManualLearningOutcomes(sorted);
      } else {
        setManualLearningOutcomes([]);
      }
    } catch (err) {
      console.error('ÖÇ yüklenirken hata:', err);
      setManualLearningOutcomes([]);
    } finally {
      setManualLoadingOutcomes(false);
    }

    // Sınav türlerini fetch et (examTypes state'i güncelle)
    try {
      const response = await axios.get(API_ENDPOINTS.GET_EXAM_TYPES_FOR_POOL(selectedCourse.courseId));
      if (response.data.success && response.data.exam_types) {
        setExamTypes(response.data.exam_types);
      }
    } catch (err) {
      console.error('Sınav türleri yüklenirken hata:', err);
    }
  };

  // Soru görseli yükle (Excel önizleme veya manuel ekleme satırından)
  const uploadQuestionImage = async (questionId, file) => {
    if (!file) return;
    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!ALLOWED.includes(file.type)) {
      alert('Sadece JPG, PNG, WEBP veya GIF yükleyebilirsin.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Görsel 5 MB sınırını aşıyor.');
      return;
    }
    // Kalıcı soru kimlikleri pozitif tamsayıdır. Excel önizleme
    // kimlikleri metindir; bunlar için backend'e istek atma.
    const persistedQuestionId = Number(questionId);
    if (!Number.isInteger(persistedQuestionId) || persistedQuestionId <= 0) {
      const previewUrl = URL.createObjectURL(file);
      setSavedManualQuestions(prev => prev.map(q => {
        if (String(q.id) !== String(questionId)) return q;
        if (q.image_url?.startsWith('blob:')) URL.revokeObjectURL(q.image_url);
        return { ...q, image_url: previewUrl, pending_image: file };
      }));
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(API_ENDPOINTS.QUESTION_IMAGE(questionId), fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (resp.data?.image_url) {
        setSavedManualQuestions(prev => prev.map(q =>
          String(q.id) === String(questionId) ? { ...q, image_url: resp.data.image_url } : q
        ));
      }
    } catch (err) {
      alert('Görsel yüklenemedi: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteQuestionImage = async (questionId) => {
    if (!window.confirm('Görseli kaldırmak istediğine emin misin?')) return;
    const persistedQuestionId = Number(questionId);
    if (!Number.isInteger(persistedQuestionId) || persistedQuestionId <= 0) {
      setSavedManualQuestions(prev => prev.map(q => {
        if (String(q.id) !== String(questionId)) return q;
        if (q.image_url?.startsWith('blob:')) URL.revokeObjectURL(q.image_url);
        return { ...q, image_url: null, pending_image: null };
      }));
      return;
    }
    try {
      await axios.delete(API_ENDPOINTS.QUESTION_IMAGE(questionId));
      setSavedManualQuestions(prev => prev.map(q =>
        String(q.id) === String(questionId) ? { ...q, image_url: null } : q
      ));
    } catch (err) {
      alert('Görsel silinemedi: ' + (err.response?.data?.error || err.message));
    }
  };

  const saveManualQuestion = async () => {
    if (!selectedCourse) {
      setManualQuestionError('Ders seçilmedi.');
      return;
    }
    if (!manualExcelFile) {
      setManualQuestionError('Lütfen bir Excel dosyası seçin.');
      return;
    }
    if (!manualQuestionCategory) {
      setManualQuestionError('Lütfen soru kategorisini seçin.');
      return;
    }
    if (!manualExamWeightId) {
      setManualQuestionError('Lütfen değerlendirme/sınav türünü seçin.');
      return;
    }
    setManualQuestionSaving(true);
    setManualQuestionError('');
    setManualQuestionSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', manualExcelFile);
      formData.append('courseId', selectedCourse.courseId);
      formData.append('programId', selectedCourse.programId);
      const manualExamWeight = examTypes.find(item => String(item.id) === String(manualExamWeightId));
      formData.append('examType', manualExamWeight?.exam_type || '');
      formData.append('category', manualQuestionCategory);
      formData.append('bookletCount', '1');

      const response = await axios.post(API_ENDPOINTS.UPLOAD_QUESTIONS_EXCEL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data && response.data.questions) {
        const uploaded = response.data.questions.map(q => ({
          id: q.id,
          text: q.text || q.question_text || '',
          outcomes: q.outcomes || [],
          options: q.options || {},
          answer: q.answer || q.correct_answer || '',
          question_type: q.question_type || 'MultipleChoice',
          score: q.score || 1,
          image_url: q.image_url || null,
        }));
        setSavedManualQuestions(prev => [...prev, ...uploaded]);
        setManualQuestionSuccess(`${uploaded.length} soru başarıyla yüklendi.`);
        setManualExcelFile(null);
      } else {
        setManualQuestionError(response.data?.error || 'Yükleme başarısız.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Bir hata oluştu.';
      setManualQuestionError(msg);
    } finally {
      setManualQuestionSaving(false);
    }
  };

  // Tek bir ÖÇ ekle/çıkar (AI öneri "Ekle" butonu için)
  const updateManualQuestionOutcomes = async (outcomeId, checked, questionId) => {
    const outcome = manualLearningOutcomes.find(o => o.id === outcomeId);
    if (!outcome) return;

    const currentQuestion = savedManualQuestions.find(q => String(q.id) === String(questionId));
    if (!currentQuestion) return;

    const currentOutcomes = currentQuestion.outcomes || [];
    const updatedOutcomes = checked
      ? [...new Set([...currentOutcomes, outcome.code])]
      : currentOutcomes.filter(c => c !== outcome.code);

    await saveManualQuestionOutcomes(questionId, currentQuestion.text || '', updatedOutcomes);
  };

  // ÖÇ listesini toplu kaydet (Select onChange için)
  const saveManualQuestionOutcomes = async (questionId, questionText, outcomeCodes) => {
    setSavedManualQuestions(prev => prev.map(q =>
      String(q.id) === String(questionId) ? { ...q, outcomes: outcomeCodes } : q
    ));
    // Excel'den yeni okunan sorular henüz veritabanı kaydı değildir.
    // ÖÇ seçimini istemcide tutup tüm zorunlu bilgiler tamamlandığında
    // transfer endpoint'i ile atomik olarak kaydederiz.
    const persistedQuestionId = Number(questionId);
    if (!Number.isInteger(persistedQuestionId) || persistedQuestionId <= 0) return;
    try {
      const res = await axios.put(API_ENDPOINTS.UPDATE_QUESTION, {
        id: questionId,
        text: questionText,
        score: 1,
        outcomes: outcomeCodes,
      });
      // Backend'den dönen PÇ listesini state'e kaydet
      if (res.data && res.data.program_outcome_labels) {
        setSavedManualQuestions(prev => prev.map(q =>
          String(q.id) === String(questionId) ? { ...q, programOutcomes: res.data.program_outcome_labels } : q
        ));
      }
    } catch (err) {
      console.error('ÖÇ güncellenirken hata:', err);
    }
  };

  // Shared pool writes are validated, atomic, and repeat-safe on the server.
  const transferPretestDirectly = async (update = false) => {
    if (pretestWriteBusy.current || checkingPretestTransfer || archiveReadOnly || !selectedCourse || !result) return;
    if (String(result.course_id) !== String(selectedCourse.courseId)) {
      setSavePretestMessage('Soru setinin dersi ile seçilen ders uyuşmuyor. Öntesti yeniden açın.');
      return;
    }
    if (!update && pretestTransfer?.transferred) return;
    let parsedQuestions;
    try { parsedQuestions = pretestPayload(previewQuestions, result.option_count || optionCount); }
    catch (err) { setSavePretestSuccess(false); setSavePretestMessage(err.message); return; }
    // Tek tıklama: eşleştir ve ardından otomatik olarak Öntest havuzuna aktar.
    pretestWriteBusy.current = true;
    setSavingPretest(true);
    setSavePretestSuccess(false);
    setSavePretestMessage('ÖÇ/PÇ eşleştirmesi yapılıyor…');
    try {
      let matches = quizQuestionMatches;
      const currentMatchesReady = matches.length === parsedQuestions.length
        && matches.every((m) => (m.learning_outcomes?.length || 0) > 0 && (m.program_outcomes?.length || 0) > 0);
      if (!currentMatchesReady) {
        const { data } = await axios.post(API_ENDPOINTS.MATCH_QUESTIONS_TO_OUTCOMES, {
          program_id: selectedCourse.programId,
          course_id: selectedCourse.courseId,
          questions: parsedQuestions.map(questionForOutcomeMatch)
        });
        matches = data?.matches || [];
        setQuizQuestionMatches(matches);
      }
      if (matches.length !== parsedQuestions.length || matches.some((m) => !(m.learning_outcomes?.length) || !(m.program_outcomes?.length))) {
        pretestWriteBusy.current = false;
        setSavingPretest(false);
        setSavePretestSuccess(false);
        setSavePretestMessage('Aktarım bekliyor: turuncu işaretli sorular için ÖÇ seçin; PÇler otomatik getirilecektir.');
        return;
      }
      parsedQuestions = parsedQuestions.map((q, i) => ({ ...q, learning_outcome_ids: matches[i].learning_outcomes.map((o) => o.lo_id), program_outcome_ids: matches[i].program_outcomes.map((o) => o.id) }));
    } catch (err) { pretestWriteBusy.current = false; setSavingPretest(false); setSavePretestSuccess(false); setSavePretestMessage('Eşleştirme servisine ulaşılamadı. Sorular korunuyor; tekrar deneyebilirsiniz.'); return; }
    const snapshot = pretestSignature(previewQuestions);
    const transferId = pretestTransfer?.id || result.session_id;
    const pending = { ...pretestTransfer, id: transferId, snapshot };
    setSavePretestMessage('ÖÇ/PÇ eşleştirmesi tamamlandı, sorular Öntest havuzuna aktarılıyor…');
    setSavePretestSuccess(false);
    // Store the submitted snapshot before sending; a lost response can be recovered.
    if (!update) setPretestTransfer(pending);
    const body = {
      program_id: selectedCourse.programId, course_id: selectedCourse.courseId,
      transfer_id: transferId, pretest_id: pretestTransfer?.pretestId,
      version: pretestTransfer?.version,
      parsed_questions: { questions: parsedQuestions },
      question_count: parsedQuestions.length, option_count: result.option_count || optionCount
    };
    try {
      const { data } = await axios({ method: update ? 'put' : 'post', url: API_ENDPOINTS.SAVE_PRETEST, data: body });
      if (!data.success || data.question_count !== parsedQuestions.length) throw new Error('Soruların tamamının kaydedildiği doğrulanamadı.');
      setPretestTransfer({ id: data.transfer_id, pretestId: data.pretest_id, version: data.version, snapshot, transferred: true });
      setSavePretestSuccess(true);
      setSavePretestMessage(update ? 'Değişiklikler sınav modülünde güncellendi ✓' : 'Sorular aktarıldı ✓');
      loadSavedItems();
    } catch (err) {
      setSavePretestMessage(err.response?.data?.error || err.message || 'Aktarım doğrulanamadı. Aynı set için yeniden deneyebilirsiniz.');
    } finally {
      pretestWriteBusy.current = false;
      setSavingPretest(false);
    }
  };

  const transferToExamPool = async () => {
    if (!selectedCourse) {
      setExamPoolError('Ders seçilmedi');
      return;
    }

    if (examPoolSource === 'manual' && !manualQuestionCategory) {
      setExamPoolError('Lütfen soru kategorisini seçin.');
      return;
    }

    if (examPoolSource === 'manual' && !manualExamWeightId) {
      setExamPoolError('Lütfen değerlendirme/sınav türünü seçin.');
      return;
    }

    if (examPoolSource !== 'manual' && !selectedExamType) {
      setExamPoolError('Lütfen aktarılacak havuz türünü seçin.');
      return;
    }

    // Seçilen sınav türünün ID'sini bul
    // Manuel source: sınav türü paneldeki examTypes'tan gelir, doğrudan kullan
    let questions = [];
    const isQuiz = examPoolSource === 'quiz';
    const isMidterm = examPoolSource === 'midterm';
    if (examPoolSource === 'manual') {
      questions = savedManualQuestions.map(q => {
        // Seçilen ÖÇ kodlarını ID'ye çevir
        const selectedCodes = q.outcomes || [];
        const loIds = selectedCodes
          .map(code => {
            const lo = manualLearningOutcomes.find(o => o.code === code);
            return lo ? lo.id : null;
          })
          .filter(id => id !== null);
        // match_percentage: seçilen ÖÇ'lerin AI önerilerinden en yüksek yüzdesi
        const aiMatchesForQ = manualQuestionAiMatches[q.id] || [];
        const relevantMatches = aiMatchesForQ.filter(m => selectedCodes.includes(m.code));
        const matchPct = relevantMatches.length > 0
          ? Math.max(...relevantMatches.map(m => m.match_percentage || 0))
          : (aiMatchesForQ.length > 0 ? Math.max(...aiMatchesForQ.map(m => m.match_percentage || 0)) : null);

        return {
          question: q.text || '',
          options: q.options || {},
          answer: q.answer || q.correct_answer || '',
          type: q.question_type || (q.options ? 'multiple_choice' : 'classic'),
          week_number: q.week_number || null,
          lo_ids: loIds.length > 0 ? loIds : null,
          match_percentage: matchPct
        };
      });
    } else if (isQuiz && quizResult?.parsed_questions?.questions?.length) {
      // Kısa sınav: parsed_questions.questions formatından API formatına çevir
      questions = quizResult.parsed_questions.questions.map((q, idx) => {
        const opts = q.options || { A: q.A, B: q.B, C: q.C, D: q.D, E: q.E };
        const matchInfo = quizQuestionMatches.find(m => m.question_index === idx);
        const loIds = matchInfo?.learning_outcomes?.map(lo => lo.lo_id) || null;
        const matchPct = matchInfo?.learning_outcomes?.length > 0
          ? Math.max(...matchInfo.learning_outcomes.map(lo => lo.match_percentage || 0))
          : null;
        return {
          question: q.question || '',
          options: opts,
          answer: q.correct_answer || '',
          week_number: q.week_number ?? q.week ?? null,
          lo_ids: loIds,
          match_percentage: matchPct
        };
      });
    } else if (isMidterm && midtermResult?.parsed_questions?.questions?.length) {
      questions = midtermResult.parsed_questions.questions.map((q, idx) => {
        const opts = q.options || { A: q.A, B: q.B, C: q.C, D: q.D, E: q.E };
        const matchInfo = quizQuestionMatches.find(m => m.question_index === idx);
        const loIds = matchInfo?.learning_outcomes?.map(lo => lo.lo_id) || null;
        const matchPct = matchInfo?.learning_outcomes?.length > 0
          ? Math.max(...matchInfo.learning_outcomes.map(lo => lo.match_percentage || 0))
          : null;
        return {
          question: q.question || '',
          options: opts,
          answer: q.correct_answer || '',
          type: q.type || (q.options || q.A ? 'multiple_choice' : 'classic'),
          week_number: q.week_number ?? q.week ?? null,
          lo_ids: loIds,
          match_percentage: matchPct
        };
      });
    } else {
      // Pretest: quizQuestionMatches index bazlı eşleştirme verisi var
      const rawQuestions = previewQuestions.length > 0
        ? previewQuestions
        : parseTestContent(result?.test_content || '');
      questions = rawQuestions.map((q, idx) => {
        const matchInfo = quizQuestionMatches.find(m => m.question_index === idx);
        const loIds = matchInfo?.learning_outcomes?.map(lo => lo.lo_id) || null;
        const matchPct = matchInfo?.learning_outcomes?.length > 0
          ? Math.max(...matchInfo.learning_outcomes.map(lo => lo.match_percentage || 0))
          : null;
        return {
          ...q,
          lo_ids: loIds,
          match_percentage: matchPct
        };
      });
    }

    if (!questions || questions.length === 0) {
      setExamPoolError('Aktarılacak soru bulunamadı');
      return;
    }

    const totalQuestions = questions.length;

    setExamPoolLoading(true);
    setExamPoolError('');
    setExamPoolSuccess('');
    setExamPoolProgress(0);
    setExamPoolProgressMsg(`0 / ${totalQuestions} soru işleniyor...`);

    let totalAdded = 0;

    try {
      // Manuel kaynak: her sorunun kendi week_number'ından hafta listesi oluştur
      const isManual = examPoolSource === 'manual';
      const weekSource = isManual
        ? questions.map(q => q.week_number).filter(w => w != null)
        : (isQuiz && Array.isArray(quizResult?.selected_weeks) && quizResult.selected_weeks.length > 0)
          ? quizResult.selected_weeks
          : (isMidterm && Array.isArray(midtermResult?.selected_weeks) && midtermResult.selected_weeks.length > 0)
            ? midtermResult.selected_weeks
            : selectedWeeklyTopics;
      let normalizedSelectedWeeks = Array.isArray(weekSource)
        ? Array.from(new Set(weekSource
            .map((w) => parseInt(w, 10))
            .filter((w) => Number.isInteger(w) && w > 0)))
        : [];

      if (isMidterm && normalizedSelectedWeeks.length === 0) {
        normalizedSelectedWeeks = Array.from(new Set(questions
          .map((q) => Number(q.week_number ?? q.week))
          .filter((week) => Number.isInteger(week) && week > 0)));
      }

      if (isQuiz && normalizedSelectedWeeks.length === 0) {
        // Modalda hafta seçimi boş görünse bile, sorularda zaten hafta atanmış olabilir.
        const weeksFromQuestions = questions
          .map((q) => parseInt(q?.week_number ?? q?.week, 10))
          .filter((w) => !Number.isNaN(w));
        normalizedSelectedWeeks = Array.from(new Set(weeksFromQuestions));
      }

      if (isQuiz && normalizedSelectedWeeks.length === 0 && weeklyTopics.length > 0) {
        // Son fallback: ekrandaki haftalık konu listesinden ilk N haftayı kullan.
        const fallbackWeeks = weeklyTopics
          .map((t) => parseInt(t?.week, 10))
          .filter((w) => !Number.isNaN(w))
          .slice(0, Math.max(1, Math.min(questions.length, 8)));
        normalizedSelectedWeeks = Array.from(new Set(fallbackWeeks));
      }

      if (isQuiz && normalizedSelectedWeeks.length === 0) {
        setExamPoolError('Kısa sınav için en az 1 hafta seçilmeli.');
        setExamPoolLoading(false);
        return;
      }

      const preparedQuestions = questions.map((question, index) => ({
        ...question,
        week_number: isManual
          ? (question.week_number || null)
          : (isMidterm && normalizedSelectedWeeks.includes(Number(question.week_number ?? question.week)))
            ? Number(question.week_number ?? question.week)
          : (normalizedSelectedWeeks.length > 0
            ? normalizedSelectedWeeks[index % normalizedSelectedWeeks.length]
            : null)
      }));
      const missingMappingIndexes = preparedQuestions
        .map((question, index) => ((question.lo_ids?.length || 0) > 0 ? null : index + 1))
        .filter(Boolean);
      if (missingMappingIndexes.length > 0) {
        setExamPoolProgress(0);
        setExamPoolProgressMsg('');
        setExamPoolError(`Aktarım bekliyor: ${missingMappingIndexes.join(', ')} numaralı sorular için ÖÇ seçin; PÇler otomatik getirilecektir.`);
        return;
      }
      const missingWeekIndexes = preparedQuestions
        .map((question, index) => {
          const week = Number(question.week_number);
          return Number.isInteger(week) && week > 0 ? null : index + 1;
        })
        .filter(Boolean);
      if (missingWeekIndexes.length > 0) {
        setExamPoolProgress(0);
        setExamPoolProgressMsg('');
        setExamPoolError(`Aktarım bekliyor: ${missingWeekIndexes.join(', ')} numaralı sorular için hafta seçin.`);
        return;
      }

      setExamPoolProgressMsg(`${totalQuestions} soru kontrol edilip sınav modülüne aktarılıyor...`);
      setExamPoolProgress(50);
      // Görünen kategori akışı değişmez; seçilen izlence türünün kimliği de
      // yeni sorularla birlikte kaydedilir.
      const selectedExamWeight = isManual
        ? examTypes.find((item) => String(item.id) === String(manualExamWeightId))
        : examTypes.find((item) => item.exam_type === selectedExamType);
      const response = await axios.post(API_ENDPOINTS.TRANSFER_TO_EXAM_POOL, {
        course_id: selectedCourse.courseId,
        program_id: selectedCourse.programId,
        category: isManual ? manualQuestionCategory : (isQuiz ? 'quiz' : 'exam'),
        exam_weight_id: selectedExamWeight?.id ?? null,
        exam_type: isManual ? (selectedExamWeight?.exam_type || '') : selectedExamType,
        creation_source: isManual ? 'import' : 'ai',
        selected_weeks: normalizedSelectedWeeks,
        questions: preparedQuestions
      });
      if (!response.data.success || response.data.statistics?.added !== totalQuestions || response.data.statistics?.skipped) {
        throw new Error(response.data.message || 'Soruların tamamının atomik olarak aktarıldığı doğrulanamadı.');
      }
      totalAdded = response.data.statistics.added;

      let imageUploadFailures = 0;
      if (isManual && Array.isArray(response.data.question_ids)) {
        const imageJobs = savedManualQuestions.map((question, index) => {
          const persistedId = response.data.question_ids[index];
          if (!question.pending_image || !persistedId) return null;
          const fd = new FormData();
          fd.append('file', question.pending_image);
          return axios.post(API_ENDPOINTS.QUESTION_IMAGE(persistedId), fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          }).catch(() => { imageUploadFailures += 1; });
        }).filter(Boolean);
        await Promise.all(imageJobs);
      }

      // Haftaları ancak soru paketinin tamamı başarıyla commit edildikten sonra kaydet.
      if (selectedCourse?.programId && selectedCourse?.courseId && normalizedSelectedWeeks.length > 0) {
        try {
          await axios.post(API_ENDPOINTS.SELECTED_WEEKLY_TOPICS, {
            program_id: selectedCourse.programId,
            course_id: selectedCourse.courseId,
            selected_weeks: normalizedSelectedWeeks
          });
        } catch (weekSaveErr) {
          console.warn('Sorular aktarıldı; seçili hafta tercihi ayrıca kaydedilemedi:', weekSaveErr);
        }
      }

      setExamPoolProgress(100);
      setExamPoolProgressMsg(`${totalAdded} / ${totalQuestions} soru tamamlandı`);
      setExamPoolSuccess(
        `${totalAdded} soru başarıyla '${isManual
          ? (QUESTION_CATEGORY_OPTIONS.find(item => item.value === manualQuestionCategory)?.label || manualQuestionCategory)
          : selectedExamType}' kategorisine aktarıldı.${imageUploadFailures ? ` ${imageUploadFailures} görsel yüklenemedi.` : ''}`
      );
    } catch (err) {
      setExamPoolProgress(0);
      setExamPoolProgressMsg('');
      console.error('Sınav havuzuna aktarma hatası:', err);
      const responseData = err.response?.data;
      const baseMessage = responseData?.message || responseData?.error || err.message || 'Aktarma sırasında bir hata oluştu';
      const invalidQuestions = Array.isArray(responseData?.invalid_questions)
        ? responseData.invalid_questions.filter(Boolean)
        : (Array.isArray(responseData?.errors) ? responseData.errors.filter(Boolean) : []);
      const uniqueDetails = Array.from(new Set(invalidQuestions))
        .filter(detail => detail !== baseMessage);
      setExamPoolError(uniqueDetails.length > 0
        ? `${baseMessage}\n${uniqueDetails.join('\n')}`
        : baseMessage);
    } finally {
      setExamPoolLoading(false);
    }
  };

  // Reset fonksiyonu
  const handleReset = () => {
    if (pretestWriteBusy.current) return;
    sessionStorage.removeItem(pretestDraftKey);
    setPretestTransfer(null);
    setPreviewQuestions([]);
    setSavePretestMessage('');
    setSavePretestSuccess(false);
    setCheckingPretestTransfer(false);
    setResult(null);
    setQuestionsResult(null);
    setStudySaveMsg('');
    setStudySaveSuccess(false);
    setQuizResult(null);
    setQuizQuestionMatches([]);
    setMidtermResult(null);
    setHomeworkResult(null);
    setCreatingHomework(false);
    setError('');
    // Aktif sekmeyi koru: "Testi Yenile" görünümü zorla Öntest'e çekmesin.
    setAllSelectedFiles([]); // Dosyaları temizle
    setHomeworkInstructions('');
    setDownloadingHomework(false);
    setManualQuestionPanelOpen(false);
    setSavedManualQuestions([]);
    setManualLearningOutcomes([]);
    setManualExamWeightId('');
    setManualQuestionAiMatches({});
    setManualQuestionAiLoading({});
    setManualExcelFile(null);
  };

  // View değiştirme fonksiyonları - her sekmeye geçerken ilgili sonuçları temizle
  const handleViewChange = (newView) => {
    if (pretestWriteBusy.current) return;
    setCurrentView(newView);

    // Her sekme değişiminde ilgili sonuçları temizle
    if (newView === 'pretest') {
      setQuestionsResult(null);
      setQuizResult(null);
      setMidtermResult(null);
      setHomeworkResult(null);
    } else if (newView === 'difficulty') {
      setResult(null);
      setQuestionsResult(null);
      setQuizResult(null);
      setMidtermResult(null);
      setHomeworkResult(null);
      // Haftalık konu seçimlerini temizleme (sadece difficulty view'ında kullanılıyor)
      // setSelectedWeeklyTopics([]); // Haftalık konuları temizleme - kullanıcı seçimlerini korumak için yorum satırı
    } else if (newView === 'quiz') {
      setResult(null);
      setQuestionsResult(null);
      setMidtermResult(null);
      setHomeworkResult(null);
    } else if (newView === 'midterm') {
      setResult(null);
      setQuestionsResult(null);
      setQuizResult(null);
      setHomeworkResult(null);
      setSelectedWeeklyTopics([]);
    } else if (newView === 'homework') {
      setResult(null);
      setQuestionsResult(null);
      setQuizResult(null);
      setMidtermResult(null);
    } else if (newView === 'saved') {
      // Kaydedilmişler görüntülenirken diğer sonuçları temizleme
      setSelectedSavedType(''); // Dropdown'ı sıfırla
      // Seçimleri temizle
      setSelectedPretests([]);
      setSelectedStudyTopics([]);
      setSelectedQuizzes([]);
      setSelectedMidterms([]);
      setSelectedHomeworks([]);
    }

    setError(''); // Hata mesajlarını da temizle
  };

  // Test içeriğini parse etme
  const parseTestContent = (content) => {
    if (!content) return [];

    const questions = [];

    // Önce **Soru:** veya **X. Soru:** formatını dene (Gemini'nin yeni formatı)
    // Format: **Cevap:** A **2. Soru:** ... **Cevap:** C
    const geminiPattern = /\*\*(\d+)\.\s*Soru:\*\*\s*(.*?)(?=\*\*(?:\d+)\.\s*Soru:\*\*|$)/gi;
    let geminiMatches = [...content.matchAll(geminiPattern)];

    if (geminiMatches.length > 0) {
      geminiMatches.forEach((match) => {
        const questionNum = parseInt(match[1]);
        let questionBlock = match[2].trim();

        // Şıkları ve cevabı ayır
        const optionPattern = /([A-E]\))\s*([^A-E\*]+?)(?=[A-E]\)|(?:\*\*Cevap:\*\*)|$)/gi;
        const answerPattern = /\*\*Cevap:\*\*\s*([A-E])/i;

        const options = [];
        let optMatch;
        const optRegex = /([A-E]\))\s*([^A-E]*?)(?=(?:[A-E]\))|(?:\*\*Cevap:\*\*)|$)/gi;
        while ((optMatch = optRegex.exec(questionBlock)) !== null) {
          const optText = optMatch[2].trim().replace(/\*\*/g, '');
          if (optText) {
            options.push(`${optMatch[1]} ${optText}`);
          }
        }

        // Cevabı bul
        const answerMatch = questionBlock.match(answerPattern);
        const answer = answerMatch ? answerMatch[1] : '';

        // Soru metnini al (şıklardan önceki kısım)
        let questionText = questionBlock.split(/[A-E]\)/)[0].trim().replace(/\*\*/g, '');

        if (questionText) {
          questions.push({
            number: questionNum,
            question: questionText,
            options: options,
            answer: answer
          });
        }
      });

      if (questions.length > 0) {
        return questions.sort((a, b) => a.number - b.number);
      }
    }

    // Alternatif format: Satır satır parse et
    const lines = content
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    let current = null;
    const isOption = (line) => /^[A-E]\)/.test(line);

    lines.forEach((line) => {
      // Soru numarası ile başlayan satır (1. veya **1. Soru:** formatı)
      const questionMatch = line.match(/^(?:\*\*)?(\d+)[\.\)]\s*(?:Soru:?\*\*)?\s*(.*)$/i);
      if (questionMatch) {
        if (current && current.question) questions.push(current);
        current = {
          question: questionMatch[2].replace(/\*\*/g, '').trim(),
          options: [],
          answer: '',
        };
        return;
      }

      if (isOption(line)) {
        if (!current) {
          current = { question: '', options: [], answer: '' };
        }
        current.options.push(line.replace(/\*\*/g, ''));
        return;
      }

      // Cevap satırı
      const answerMatch = line.match(/(?:\*\*)?(?:Doğru\s*)?Cevap(?:\*\*)?:\s*([A-E])/i);
      if (answerMatch) {
        if (current) {
          current.answer = answerMatch[1];
        }
        return;
      }

      // Soru metni devamı
      if (current && !isOption(line)) {
        const cleanLine = line.replace(/\*\*/g, '').trim();
        if (cleanLine && !cleanLine.match(/^(?:Doğru\s*)?Cevap:/i)) {
          current.question = current.question ? `${current.question} ${cleanLine}` : cleanLine;
        }
      }
    });

    if (current && current.question) questions.push(current);

    return questions.map((q, idx) => ({
      ...q,
      number: q.number || idx + 1,
    }));
  };

  const parseAiResult = (text) => {
    if (!text) return null;
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    let question = '';
    const options = [];
    let answer = '';
    lines.forEach((line) => {
      if (line.match(/^\d+\./)) {
        question = line.replace(/^\d+\.\s*/, '');
      } else if (['A)', 'B)', 'C)', 'D)', 'E)'].some((p) => line.startsWith(p))) {
        options.push(line);
      } else if (line.toLowerCase().startsWith('doğru cevap') || line.toLowerCase().startsWith('cevap')) {
        answer = line.split(':').slice(1).join(':').trim();
      } else if (!question) {
        question = line;
      }
    });
    if (!question || options.length === 0) return null;
    return { question, options, answer };
  };

  const buildAiPrompt = () => {
    if (!aiSelectedQuestion) return '';
    const { question, options = [], answer, learningOutcomeCode, programOutcomeCode, lo_code, po_code } = aiSelectedQuestion;
    const loCode = learningOutcomeCode || lo_code;
    const poCode = programOutcomeCode || po_code;
    const optionLines = options.map((opt) => `- ${opt}`).join('\n');
    const outcomeLines = [
      loCode ? `- İlgili Dersin Öğrenme Çıktısı (ÖÇ): ${loCode}` : null,
      poCode ? `- İlgili Program Çıktısı (PÇ): ${poCode}` : null,
    ].filter(Boolean).join('\n');
    const qOptionCount = options.length || optionCount || 4;
    const optionLetters = Array.from({ length: qOptionCount }, (_, i) => String.fromCharCode(65 + i)).join(',');

    return [
      `Bu çoktan seçmeli soruyu sadece daha anlaşılır hale getir (ÖÇ/PÇ bağlamını koru).`,
      `- Konu: ${subject || 'Elektrik motorları'}`,
      `- Mevcut soru: ${question}`,
      `- Mevcut şıklar:\n${optionLines || '- (yok)'}`,
      `- Doğru cevap: ${answer || 'Bilinmiyor'}`,
      outcomeLines || '- ÖÇ/PÇ bilgisi: yok',
      `Kurallar:`,
      `1) Doğru cevabı değiştirme, sorunun kapsamını daraltma/genişletme.`,
      `2) Şıkları netleştir, sayı/hacim gibi değerleri değiştirme; teknik terimleri koru.`,
      `3) TAM OLARAK ${qOptionCount} şık üret, harf sırasını koru (${optionLetters}). Hiçbir şık boş bırakma.`,
      `4) Sadece yeni soru, şıklar ve doğru cevabı döndür.`,
    ].join('\n');
  };

  // Manuel mod için kısa, ÖÇ/PÇ referanslı şablon
  const buildManualTemplate = () => {
    if (!aiSelectedQuestion) return '';
    const { options = [], learningOutcomeCode, programOutcomeCode, lo_code, po_code } = aiSelectedQuestion;
    const loCode = learningOutcomeCode || lo_code || 'ÖÇ?';
    const poCode = programOutcomeCode || po_code || 'PÇ?';
    const qOptionCount = options.length || optionCount || 4;
    const optionLetters = Array.from({ length: qOptionCount }, (_, i) => String.fromCharCode(65 + i)).join(',');
    return [
      `Soruyu ÖÇ/PÇ bağlamını koruyarak sadeleştir.`,
      `ÖÇ: ${loCode} | PÇ: ${poCode}`,
      `Konu: ${subject || 'Elektrik motorları'}`,
      ``,
      `Kurallar (değiştirme):`,
      `1) Doğru cevabı değiştirme, kapsamı bozma.`,
      `2) Teknik terimleri koru, yeni veri/sayı ekleme.`,
      `3) TAM OLARAK ${qOptionCount} şık üret, harf sırasını koru (${optionLetters}). Hiçbir şık boş bırakma.`,
      `4) Sadece yeni soru, şıklar ve doğru cevabı döndür.`,
    ].join('\n');
  };

  const openAiModal = (question, mode = null) => {
    setAiSelectedQuestion(question);
    // currentView'a göre varsayılan modu ayarla
    if (mode === null) {
      if (currentView === 'pretest' || currentView === 'difficulty') {
        setAiMode('replace');
      } else {
        setAiMode('easy');
      }
    } else {
      setAiMode(mode);
    }
    setAiModalOpen(true);
    setAiOutputText('');
    setManualPrompt(buildManualTemplate());
  };

  const closeAiModal = () => {
    setAiModalOpen(false);
    setAiSelectedQuestion(null);
    setAiOutputText('');
    setManualPrompt('');
    setManualEditData(null);
    // currentView'a göre varsayılan modu ayarla
    if (currentView === 'pretest' || currentView === 'difficulty') {
      setAiMode('replace');
    } else if (currentView === 'midterm' || currentView === 'quiz') {
      setAiMode('easy');
    } else {
      setAiMode('easy');
    }
  };

  const copyAiPrompt = async () => {
    try {
      await navigator.clipboard.writeText(buildManualTemplate());
    } catch (err) {
      console.error('Prompt kopyalanamadı:', err);
    }
  };

  const openManualEdit = () => {
    if (!aiSelectedQuestion) return;
    const { question, options, option_keys, correct_answer, answer } = aiSelectedQuestion;

    const editData = { question: question || '' };

    if (currentView === 'quiz' || currentView === 'midterm') {
      const keys = (option_keys && option_keys.length) ? option_keys : ['A', 'B', 'C', 'D', 'E'];
      const opts = (options && typeof options === 'object' && !Array.isArray(options)) ? options : {};
      editData.options = { ...opts };
      editData.option_keys = keys;
      editData.correct_answer = correct_answer || 'A';
    } else if (currentView === 'pretest') {
      editData.optionsArray = Array.isArray(options) ? [...options] : [];
      editData.answer = answer || '';
    }
    // difficulty: sadece question

    setManualEditData(editData);
    setAiMode('directEdit');
  };

  const applyManualEdit = () => {
    if (!aiSelectedQuestion || !manualEditData) return;

    if (currentView === 'quiz') {
      if (quizResult && quizResult.parsed_questions) {
        const originalIndex = aiSelectedQuestion.originalIndex;
        const updatedQuestions = quizResult.parsed_questions.questions.map((q, idx) => {
          if (idx === originalIndex) {
            return { ...q, question: manualEditData.question, options: { ...manualEditData.options }, correct_answer: manualEditData.correct_answer };
          }
          return q;
        });
        setQuizResult({ ...quizResult, parsed_questions: { ...quizResult.parsed_questions, questions: updatedQuestions } });
      }
    } else if (currentView === 'midterm') {
      if (midtermResult && midtermResult.parsed_questions) {
        const originalIndex = aiSelectedQuestion.originalIndex;
        const updatedQuestions = midtermResult.parsed_questions.questions.map((q, idx) => {
          if (idx === originalIndex) {
            return { ...q, question: manualEditData.question, options: { ...manualEditData.options }, correct_answer: manualEditData.correct_answer };
          }
          return q;
        });
        setMidtermResult({ ...midtermResult, parsed_questions: { ...midtermResult.parsed_questions, questions: updatedQuestions } });
      }
    } else if (currentView === 'difficulty') {
      if (questionsResult && questionsResult.parsed_questions) {
        const originalIndex = aiSelectedQuestion.originalIndex;
        const updatedQuestions = questionsResult.parsed_questions.questions.map((q, idx) => {
          if (idx === originalIndex) {
            return { ...q, question: manualEditData.question };
          }
          return q;
        });
        setQuestionsResult({ ...questionsResult, parsed_questions: { ...questionsResult.parsed_questions, questions: updatedQuestions } });
      }
    } else {
      // pretest
      const updated = previewQuestions.map((q) =>
        q.number === aiSelectedQuestion.number
          ? { ...q, question: manualEditData.question, options: manualEditData.optionsArray || [], answer: manualEditData.answer || '' }
          : q
      );
      setPreviewQuestions(updated);
    }

    closeAiModal();
  };

  const applyAiResultToQuestion = () => {
    if (!aiSelectedQuestion) return;

    // Manuel mod: textarea'daki metni direkt soru olarak kaydet, şıkları/cevabı boşalt
    if (aiMode === 'manual') {
      const newQuestionText = aiOutputText.trim();
      if (!newQuestionText) {
        setError('Lütfen yeni soru metnini girin.');
        return;
      }
      const rulesText = [
        '',
        'Kurallar:',
        '1)  kapsamı bozma.',
        '2) Teknik terimleri koru, yeni veri/sayı ekleme.',
        '3) 4 şık üret, harf sırasını koru (A,B,C,D).',
        '4) Sadece yeni soru, şıklar ve doğru cevabı döndür.'
      ].join('\n');
      const finalQuestion = `${newQuestionText}${rulesText}`;
      const updated = previewQuestions.map((q) =>
        q.number === aiSelectedQuestion.number
          ? { ...q, question: finalQuestion, options: [], answer: '' }
          : q
      );
      setPreviewQuestions(updated);
      setAiModalOpen(false);
      setAiSelectedQuestion(null);
      setAiOutputText('');
      setManualPrompt('');
      setError('');
      return;
    }

    // Otomatik mod: standart parse
    const parsed = parseAiResult(aiOutputText);
    if (!parsed || !aiSelectedQuestion) {
      setError('AI çıktısı okunamadı. "1. ...", şıklar ve "Doğru Cevap:" içermeli.');
      return;
    }
    const updated = previewQuestions.map((q) =>
      q.number === aiSelectedQuestion.number
        ? { ...q, question: parsed.question, options: parsed.options, answer: parsed.answer }
        : q
    );
    setPreviewQuestions(updated);
    setAiModalOpen(false);
    setAiSelectedQuestion(null);
    setAiOutputText('');
    setManualPrompt('');
    setError('');
  };

  const handleAiModify = async (mode) => {
    if (!aiSelectedQuestion) return;

    setAiMode(mode);
    setAiModifying(true);
    setError('');

    try {
      // Çalışma konuları modülü için (açık uçlu sorular)
      if (currentView === 'difficulty') {
        const { question } = aiSelectedQuestion;

        const response = await axios.post(`${API_BASE_URL}/api/modify-question-with-ai`, {
          question_text: question,
          options: [],
          correct_answer: '',
          subject: subject || questionsResult?.subject || 'Elektrik motorları',
          mode: 'replace' // Çalışma konuları için sadece replace modu
        });

        if (response.data.success) {
          const { question: newQuestion } = response.data;

          // questionsResult state'ini güncelle
          if (questionsResult && questionsResult.parsed_questions) {
            const originalIndex = aiSelectedQuestion.originalIndex;
            console.log('Çalışma konuları - Orijinal index:', originalIndex);
            console.log('Çalışma konuları - Toplam soru sayısı:', questionsResult.parsed_questions.questions.length);

            const updatedQuestions = questionsResult.parsed_questions.questions.map((q, idx) => {
              // Orijinal soruyu bulmak için index'i kontrol et
              if (idx === originalIndex) {
                console.log('Çalışma konuları - Soru güncelleniyor:', idx, q.question, '->', newQuestion);
                return { ...q, question: newQuestion };
              }
              return q;
            });

            setQuestionsResult({
              ...questionsResult,
              parsed_questions: {
                ...questionsResult.parsed_questions,
                questions: updatedQuestions
              }
            });
          }

          setAiModalOpen(false);
          setAiSelectedQuestion(null);
          setError('');
        } else {
          throw new Error(response.data.error || 'AI düzenleme başarısız');
        }
      }
      // Kısa sınav modülü için (çoktan seçmeli sorular)
      else if (currentView === 'quiz') {
        const { question, options = {}, option_keys = ['A', 'B', 'C', 'D', 'E'], correct_answer = 'A' } = aiSelectedQuestion;

        // Options objesini array'e çevir
        const optionsArray = option_keys.map(key => {
          if (options[key]) {
            return options[key];
          }
          // Eğer options objesi yoksa, question[key] formatından al
          return question[key] || '';
        }).filter(opt => opt);

        const cleanOptions = optionsArray.map(opt => {
          // Remove "A) ", "B) " etc. prefixes
          return typeof opt === 'string' ? opt.replace(/^[A-D]\)\s*/, '').trim() : opt;
        });

        const response = await axios.post(`${API_BASE_URL}/api/modify-question-with-ai`, {
          question_text: question,
          options: cleanOptions,
          correct_answer: correct_answer || 'A',
          subject: subject || quizResult?.subject || 'Elektrik motorları',
          mode: mode
        });

        if (response.data.success) {
          const { question: newQuestion, options: newOptions, answer: newAnswer } = response.data;

          // quizResult state'ini güncelle
          if (quizResult && quizResult.parsed_questions) {
            const originalIndex = aiSelectedQuestion.originalIndex;
            console.log('Kısa sınav - Orijinal index:', originalIndex);
            console.log('Kısa sınav - Toplam soru sayısı:', quizResult.parsed_questions.questions.length);

            const updatedQuestions = quizResult.parsed_questions.questions.map((q, idx) => {
              if (idx === originalIndex) {
                console.log('Kısa sınav - Soru güncelleniyor:', idx, q.question, '->', newQuestion);
                // Options formatını koru
                const updatedQuestion = {
                  ...q,
                  question: newQuestion,
                  correct_answer: newAnswer
                };

                // Options'ı doğru formata çevir
                if (q.option_keys && q.options) {
                  updatedQuestion.options = {};
                  newOptions.forEach((opt, optIdx) => {
                    const key = q.option_keys[optIdx] || ['A', 'B', 'C', 'D', 'E'][optIdx];
                    updatedQuestion.options[key] = opt.replace(/^[A-E]\)\s*/, '').trim();
                  });
                } else {
                  // Eski format için
                  newOptions.forEach((opt, optIdx) => {
                    const key = ['A', 'B', 'C', 'D', 'E'][optIdx];
                    updatedQuestion[key] = opt.replace(/^[A-D]\)\s*/, '').trim();
                  });
                }

                return updatedQuestion;
              }
              return q;
            });

            setQuizResult({
              ...quizResult,
              parsed_questions: {
                ...quizResult.parsed_questions,
                questions: updatedQuestions
              }
            });
          }

          setAiModalOpen(false);
          setAiSelectedQuestion(null);
          setError('');
        } else {
          throw new Error(response.data.error || 'AI düzenleme başarısız');
        }
      }
      // Ara sınav modülü için (çoktan seçmeli sorular)
      else if (currentView === 'midterm') {
        const { question, options = {}, option_keys = ['A', 'B', 'C', 'D', 'E'], correct_answer = 'A' } = aiSelectedQuestion;

        // Options objesini array'e çevir
        const optionsArray = option_keys.map(key => {
          if (options[key]) {
            return options[key];
          }
          // Eğer options objesi yoksa, question[key] formatından al
          return question[key] || '';
        }).filter(opt => opt);

        const cleanOptions = optionsArray.map(opt => {
          // Remove "A) ", "B) " etc. prefixes
          return typeof opt === 'string' ? opt.replace(/^[A-D]\)\s*/, '').trim() : opt;
        });

        const response = await axios.post(`${API_BASE_URL}/api/modify-question-with-ai`, {
          question_text: question,
          options: cleanOptions,
          correct_answer: correct_answer || 'A',
          subject: subject || midtermResult?.subject || 'Elektrik motorları',
          mode: mode
        });

        if (response.data.success) {
          const { question: newQuestion, options: newOptions, answer: newAnswer } = response.data;

          // midtermResult state'ini güncelle
          if (midtermResult && midtermResult.parsed_questions) {
            const originalIndex = aiSelectedQuestion.originalIndex;
            console.log('Ara sınav - Orijinal index:', originalIndex);
            console.log('Ara sınav - Toplam soru sayısı:', midtermResult.parsed_questions.questions.length);

            const updatedQuestions = midtermResult.parsed_questions.questions.map((q, idx) => {
              if (idx === originalIndex) {
                console.log('Ara sınav - Soru güncelleniyor:', idx, q.question, '->', newQuestion);
                // Options formatını koru
                const updatedQuestion = {
                  ...q,
                  question: newQuestion,
                  correct_answer: newAnswer
                };

                // Options'ı doğru formata çevir
                if (q.option_keys && q.options) {
                  updatedQuestion.options = {};
                  newOptions.forEach((opt, optIdx) => {
                    const key = q.option_keys[optIdx] || ['A', 'B', 'C', 'D', 'E'][optIdx];
                    updatedQuestion.options[key] = opt.replace(/^[A-E]\)\s*/, '').trim();
                  });
                } else {
                  // Eski format için
                  newOptions.forEach((opt, optIdx) => {
                    const key = ['A', 'B', 'C', 'D', 'E'][optIdx];
                    updatedQuestion[key] = opt.replace(/^[A-D]\)\s*/, '').trim();
                  });
                }

                return updatedQuestion;
              }
              return q;
            });

            setMidtermResult({
              ...midtermResult,
              parsed_questions: {
                ...midtermResult.parsed_questions,
                questions: updatedQuestions
              }
            });
          }

          setAiModalOpen(false);
          setAiSelectedQuestion(null);
          setError('');
        } else {
          throw new Error(response.data.error || 'AI düzenleme başarısız');
        }
      }
      // Öntest modülü için (mevcut kod)
      else {
        const { question, options = [], answer } = aiSelectedQuestion;
        const cleanOptions = options.map(opt => {
          // Remove "A) ", "B) " etc. prefixes
          return opt.replace(/^[A-E]\)\s*/, '').trim();
        });

        const response = await axios.post(`${API_BASE_URL}/api/modify-question-with-ai`, {
          question_text: question,
          options: cleanOptions,
          correct_answer: answer || 'A',
          subject: subject || result?.subject || '',
          assessment_type: 'pretest',
          mode: mode
        });

        if (response.data.success) {
          const { question: newQuestion, options: newOptions, answer: newAnswer } = response.data;

          // Update only the selected question
          const updated = previewQuestions.map((q) =>
            q.number === aiSelectedQuestion.number
              ? { ...q, question: newQuestion, options: newOptions, answer: newAnswer }
              : q
          );
          setPreviewQuestions(updated);
          setAiModalOpen(false);
          setAiSelectedQuestion(null);
          setError('');
        } else {
          throw new Error(response.data.error || 'AI düzenleme başarısız');
        }
      }
    } catch (err) {
      console.error('AI düzenleme hatası:', err);
      setError(err.response?.data?.error || err.message || 'AI ile soru düzenlenirken hata oluştu');
    } finally {
      setAiModifying(false);
    }
  };

  // Zorluk seviyeli soruları gösterme - YENİ TASARIM
  const displayQuestionsResult = () => {
    if (!questionsResult || !questionsResult.parsed_questions) return null;

    const questions = questionsResult.parsed_questions.questions || [];
    const kolay = questions.filter(q => q.difficulty === 'SEVİYE 1' || q.difficulty === 'KOLAY');
    const orta = questions.filter(q => q.difficulty === 'SEVİYE 2' || q.difficulty === 'ORTA');
    const zor = questions.filter(q => q.difficulty === 'SEVİYE 3' || q.difficulty === 'ZOR');

    const renderDifficultyLevel = (levelQuestions, levelClass, levelName, levelNumber) => (
      <div key={levelClass} className={`difficulty-level ${levelClass}`}>
        <div className={`level-title ${levelClass}`}>
          <div className={`level-number ${levelClass}`}>{levelNumber}</div>
          <h4>{levelName}</h4>
        </div>
        <div className="questions-grid">
          {levelQuestions.map((question, index) => (
            <div key={index} className={`question-item ${levelClass}`}>
              <div className="question-content-wrapper">
                <span className="question-index">{index + 1}</span>
                <p className="open-ended-text"><MathText>{question.question}</MathText></p>
              </div>
              <div className="ai-actions" style={{ marginTop: '10px' }}>
                <button
                  type="button"
                  className="ai-btn"
                  disabled={studySaving || studySaveSuccess}
                  onClick={() => {
                    // Tüm sorular içindeki gerçek index'i bul
                    const allQuestions = questionsResult.parsed_questions.questions || [];
                    const realIndex = allQuestions.findIndex(q =>
                      q.difficulty === question.difficulty &&
                      q.question === question.question
                    );
                    const questionWithContext = {
                      ...question,
                      difficulty: question.difficulty,
                      originalIndex: realIndex !== -1 ? realIndex : index,
                      levelClass: levelClass
                    };
                    openAiModal(questionWithContext, 'replace');
                  }}
                >
                  Düzenle
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    return (
      <div className="questions-result">
        <div className="questions-header">
          <h3>{t('pretest.studyTopicsSection')}</h3>
          <p>{t('pretest.studyTopicsCreated')}</p>
        </div>

        <div className="difficulty-levels-container">
          {renderDifficultyLevel(kolay, 'kolay', t('pretest.level1'), '1')}
          {renderDifficultyLevel(orta, 'orta', t('pretest.level2'), '2')}
          {renderDifficultyLevel(zor, 'zor', t('pretest.level3'), '3')}
        </div>

        <div className="download-buttons" style={{ marginTop: '20px', textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={transferStudyQuestionsToExamModule}
            className="btn btn-success"
            disabled={studySaving || studySaveSuccess || !questionsResult}
            aria-busy={studySaving}
            style={{ backgroundColor: studySaveSuccess ? '#6b7280' : '#16a34a', color: '#fff', border: 'none' }}
          >
            {studySaving ? 'Sınav Modülüne Aktarılıyor…' : studySaveSuccess ? 'Sınav Modülüne Aktarıldı ✓' : 'Sınav Modülüne Aktar'}
          </button>
          {studySaveMsg && (
            <span role="status" aria-live="polite" style={{ fontSize: '13px', color: studySaving ? '#0369a1' : (studySaveSuccess ? '#16a34a' : '#dc2626'), fontWeight: 600 }}>
              {studySaveMsg}
            </span>
          )}
          {studySaveSuccess && (
            <a href={EXAM_MODULE_URL} className="btn btn-primary" style={{ textDecoration: 'none' }}>
              Sınav Modülüne Git
            </a>
          )}
        </div>
      </div>
    );
  };

  const hasQuestionOutcomeMatch = (question, index) => {
    const match = quizQuestionMatches.find((m) => m.question_index === index);
    if (match && (match.learning_outcomes?.length || 0) > 0 && (match.program_outcomes?.length || 0) > 0) {
      return true;
    }

    const hasLo = (question?.learning_outcomes?.length || 0) > 0
      || (question?.lo_ids?.length || 0) > 0
      || (question?.learning_outcome_codes?.length || 0) > 0;
    const hasPo = (question?.program_outcomes?.length || 0) > 0
      || (question?.po_ids?.length || 0) > 0
      || (question?.program_outcome_codes?.length || 0) > 0;
    if (hasLo && hasPo) return true;

    return false;
  };

  const assignOutcomeToQuestion = (questionIndex, outcomeId) => {
    const mapping = outcomeMappings.find((item) => String(item.id) === String(outcomeId));
    if (!mapping || !Array.isArray(mapping.program_outcomes) || mapping.program_outcomes.length === 0) return;

    const manualMatch = {
      question_index: questionIndex,
      manual_review: true,
      learning_outcomes: [{
        lo_id: Number(mapping.id),
        lo_code: mapping.code,
        description: mapping.description,
        match_percentage: 100
      }],
      program_outcomes: mapping.program_outcomes.map((po) => ({
        id: Number(po.id),
        number: String(po.number || po.code || '').replace(/^PÇ/i, ''),
        code: po.code,
        name: po.description
      }))
    };

    setQuizQuestionMatches((current) => [
      ...current.filter((item) => item.question_index !== questionIndex),
      manualMatch
    ].sort((a, b) => a.question_index - b.question_index));
  };

  const renderOutcomeAssignment = (questionIndex) => {
    const match = quizQuestionMatches.find((item) => item.question_index === questionIndex);
    const selectedLoId = match?.learning_outcomes?.length === 1
      ? String(match.learning_outcomes[0].lo_id)
      : '';
    const ready = (match?.learning_outcomes?.length || 0) > 0
      && (match?.program_outcomes?.length || 0) > 0;

    return (
      <div style={{ marginTop: '10px', padding: '10px', borderRadius: '8px', background: ready ? '#f0fdf4' : '#fff7ed', border: `1px solid ${ready ? '#bbf7d0' : '#fed7aa'}` }}>
        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 700, color: ready ? '#166534' : '#9a3412' }}>
          {ready ? 'ÖÇ/PÇ eşleşmesi hazır — gerekirse ÖÇyi değiştirin' : 'Bu soru için geçerli ÖÇ seçin; PÇ otomatik getirilecektir'}
        </label>
        <select
          value={selectedLoId}
          onChange={(event) => assignOutcomeToQuestion(questionIndex, event.target.value)}
          style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff' }}
        >
          <option value="">ÖÇ seçin…</option>
          {outcomeMappings.filter((item) => Array.isArray(item.program_outcomes) && item.program_outcomes.length > 0).map((item) => (
            <option key={item.id} value={item.id}>{item.code} — {item.description}</option>
          ))}
        </select>
      </div>
    );
  };

  // Quiz sonuçlarını gösterme
  const displayQuizResult = () => {
    if (!quizResult || !quizResult.parsed_questions) return null;

    const questions = quizResult.parsed_questions.questions || [];
    const weekSource = (Array.isArray(selectedWeeklyTopics) && selectedWeeklyTopics.length > 0)
      ? selectedWeeklyTopics
      : (Array.isArray(quizResult?.selected_weeks) ? quizResult.selected_weeks : []);
    const normalizedSelectedWeeks = Array.isArray(weekSource)
      ? weekSource
          .map((w) => parseInt(w, 10))
          .filter((w) => !Number.isNaN(w))
      : [];
    const weeklyTopicMap = weeklyTopics.reduce((acc, item) => {
      const w = parseInt(item?.week, 10);
      if (!Number.isNaN(w)) {
        acc[w] = item?.topic || '';
      }
      return acc;
    }, {});
    const questionsReady = questions.length > 0;
    const weeklyTopicsWritten = questionsReady && questions.every((question, index) => {
      const qWeek = parseInt(question.week_number ?? question.week, 10);
      if (!Number.isNaN(qWeek)) return true;
      return normalizedSelectedWeeks.length > 0 || !!weeklyTopics[index]?.week;
    });
    const outcomesMatched = questionsReady && questions.every((question, index) =>
      hasQuestionOutcomeMatch(question, index)
    );
    const transferReady = questionsReady && weeklyTopicsWritten && outcomesMatched;
    const matchedCount = questions.reduce((count, question, index) => (
      hasQuestionOutcomeMatch(question, index) ? count + 1 : count
    ), 0);
    const weekMappedCount = questions.reduce((count, question, index) => {
      const qWeek = parseInt(question.week_number ?? question.week, 10);
      if (!Number.isNaN(qWeek)) return count + 1;
      return (normalizedSelectedWeeks.length > 0 || !!weeklyTopics[index]?.week) ? count + 1 : count;
    }, 0);
    const questionCoverage = questions.length > 0 ? 1 : 0;
    const weekCoverage = questions.length > 0 ? (weekMappedCount / questions.length) : 0;
    const outcomeCoverage = questions.length > 0 ? (matchedCount / questions.length) : 0;
    const stepProgress = Math.round(((questionCoverage + weekCoverage + outcomeCoverage) / 3) * 100);
    const transferButtonLabel = transferReady
      ? t('pretest.transferToExam')
      : t('pretest.completeSteps', 'Havuza Aktarma İçin Adımları Tamamla');
    const transferStatusText = transferReady
      ? t('pretest.transferReady')
      : t('pretest.transferNotReady');

    return (
      <div className="quiz-result">
        <div className="quiz-header">
          <h3>{t('pretest.quizQuestionsTitle', { count: questions.length })}</h3>
          <p>{t('pretest.quizSubtitleShort')}</p>
        </div>
        <div className="quiz-questions">
          {questions.map((question, index) => (
            <div key={index} className="quiz-card">
              <h4>{t('pretest.questionNumber')} {index + 1}</h4>
              {(() => {
                const qWeek = parseInt(question.week_number ?? question.week, 10);
                const fallbackWeek = normalizedSelectedWeeks.length > 0
                  ? normalizedSelectedWeeks[index % normalizedSelectedWeeks.length]
                  : (weeklyTopics[index]?.week || null);
                const weekToShow = !Number.isNaN(qWeek) ? qWeek : fallbackWeek;
                const weekTopicText = weeklyTopicMap[weekToShow];
                return (
                  <div style={{ marginTop: '6px', marginBottom: '8px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', margin: 0 }}>
                      Hafta: {weekToShow || '-'}
                    </p>
                    {weekTopicText ? (
                      <p style={{ fontSize: '12px', color: '#334155', margin: '4px 0 0 0' }}>
                        Haftalık Konu: {weekTopicText}
                      </p>
                    ) : null}
                  </div>
                );
              })()}
              <p><MathText>{question.question}</MathText></p>
              <div className="options">
                {(
                  question.option_keys && question.options
                    ? question.option_keys.map((key) => ({
                      key,
                      text: question.options[key]
                    }))
                    : ['A', 'B', 'C', 'D', 'E'].map((key) => ({
                      key,
                      text: question[key]
                    }))
                ).filter(({ text }) => text != null && String(text).trim() !== '').map(({ key, text }) => (
                  <p key={key} className={key === question.correct_answer ? 'correct-option' : ''}>
                    <strong>{key})</strong> <MathText>{text}</MathText>
                  </p>
                ))}
                <p className="correct-answer"><strong>{t('pretest.correctAnswer')}</strong> {question.correct_answer}</p>
              </div>
              {question.explanation && (
                <p className="explanation"><strong>{t('pretest.explanation')}</strong> <MathText>{question.explanation}</MathText></p>
              )}
              {(() => {
                const match = quizQuestionMatches.find(m => m.question_index === index);
                if (!match || (!match.learning_outcomes?.length && !match.program_outcomes?.length)) return null;
                return (
                  <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {match.learning_outcomes?.map(lo => (
                      <span key={lo.lo_id} style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '600', border: '1px solid #bfdbfe' }}>
                        {lo.lo_code}
                      </span>
                    ))}
                    {match.program_outcomes?.map(po => (
                      <span key={po.id} style={{ background: '#fef3c7', color: '#92400e', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '600', border: '1px solid #fde68a' }}>
                        {String(po.number || po.code || '').startsWith('PÇ') ? (po.number || po.code) : `PÇ${po.number || po.code || ''}`}
                      </span>
                    ))}
                  </div>
                );
              })()}
              {renderOutcomeAssignment(index)}
              <div className="ai-actions" style={{ marginTop: '10px' }}>
                <button
                  type="button"
                  className="ai-btn"
                  onClick={() => {
                    const questionWithContext = {
                      ...question,
                      number: index + 1,
                      originalIndex: index
                    };
                    openAiModal(questionWithContext, 'easy');
                  }}
                >
                  Düzenle
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="quiz-action-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
          <button
            onClick={() => openExamPoolModal('quiz')}
            className="btn btn-success"
            disabled={loading || !selectedCourse || !transferReady}
            style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
            title={transferReady ? '' : 'Önce sorular, haftalık konu ve ÖÇ/PÇ eşleştirme adımları tamamlanmalı'}
          >
            {transferButtonLabel}
          </button>
        </div>
        <div style={{ marginTop: '10px', maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
            <span style={{ color: '#334155', fontWeight: '600' }}>Havuza Aktarma Durumu</span>
            <span style={{ color: transferReady ? '#166534' : '#92400e', fontWeight: '700' }}>%{stepProgress}</span>
          </div>
          <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${stepProgress}%`,
                height: '100%',
                background: transferReady ? '#10b981' : '#2563eb',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <p style={{ textAlign: 'center', marginTop: '6px', marginBottom: 0, fontSize: '12px', color: transferReady ? '#166534' : '#92400e', fontWeight: '600' }}>
            {transferStatusText}
          </p>
        </div>
      </div>
    );
  };

  // Ara sınav sonuçlarını gösterme
  const displayMidtermResult = () => {
    if (!midtermResult) return null;

    // Debug: midtermResult yapısını kontrol et
    console.log('midtermResult:', midtermResult);

    // parsed_questions yoksa veya boşsa, alternatif yolları dene
    let questions = [];
    if (midtermResult.parsed_questions) {
      questions = midtermResult.parsed_questions.questions || [];
    } else if (midtermResult.midterm_content && midtermResult.midterm_content.questions) {
      // Eğer parsed_questions yoksa ama midterm_content içinde questions varsa
      questions = midtermResult.midterm_content.questions || [];
    } else if (midtermResult.questions) {
      // Direkt questions array'i varsa
      questions = Array.isArray(midtermResult.questions) ? midtermResult.questions : [];
    }

    console.log('Ara sınav soruları:', questions.length, 'adet');
    console.log('Sorular detayı:', questions.map((q, i) => ({
      index: i + 1,
      type: q.type || (q.options || q.A ? 'multiple_choice' : 'classic'),
      hasQuestion: !!q.question,
      hasOptions: !!(q.options || q.A),
      hasPoints: !!q.points
    })));

    // Soru tiplerini say
    const mcCount = questions.filter(q => {
      const qType = q.type || (q.options || q.A ? 'multiple_choice' : 'classic');
      return qType === 'multiple_choice';
    }).length;
    const classicCount = questions.filter(q => {
      const qType = q.type || (q.options || q.A ? 'multiple_choice' : 'classic');
      return qType === 'classic';
    }).length;
    console.log(`Soru dağılımı: ${mcCount} çoktan seçmeli, ${classicCount} klasik`);

    // Eğer sorular yoksa, kullanıcıya bilgi ver
    if (questions.length === 0) {
      return (
        <div className="quiz-result">
          <div className="quiz-header">
            <h3>{t('pretest.midtermQuestionsEmpty')}</h3>
            <p>{t('pretest.midtermCoverage')}</p>
          </div>
          <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
            <p>{t('pretest.midtermNoQuestions')}</p>
            <p>{t('pretest.midtermClickCreate')}</p>
          </div>
        </div>
      );
    }

    // Klasik soruları filtrele
    const classicQuestions = questions.filter(q => {
      const qType = q.type || (q.options || q.A ? 'multiple_choice' : 'classic');
      return qType === 'classic';
    });
    const hasClassicQuestions = classicQuestions.length > 0;
    const weekSource = (Array.isArray(midtermResult?.selected_weeks) && midtermResult.selected_weeks.length > 0)
      ? midtermResult.selected_weeks
      : selectedWeeklyTopics;
    const normalizedSelectedWeeks = Array.isArray(weekSource)
      ? weekSource
          .map((w) => parseInt(w, 10))
          .filter((w) => !Number.isNaN(w))
      : [];
    const weeklyTopicMap = weeklyTopics.reduce((acc, item) => {
      const w = parseInt(item?.week, 10);
      if (!Number.isNaN(w)) {
        acc[w] = item?.topic || '';
      }
      return acc;
    }, {});
    const questionsReady = questions.length > 0;
    const weeklyTopicsWritten = questionsReady && questions.every((question, index) => {
      const qWeek = parseInt(question.week_number ?? question.week, 10);
      if (!Number.isNaN(qWeek)) return true;
      return normalizedSelectedWeeks.length > 0;
    });
    const outcomesMatched = questionsReady && questions.every((question, index) =>
      hasQuestionOutcomeMatch(question, index)
    );
    const transferReady = questionsReady && weeklyTopicsWritten && outcomesMatched;
    const matchedCount = questions.reduce((count, question, index) => (
      hasQuestionOutcomeMatch(question, index) ? count + 1 : count
    ), 0);
    const weekMappedCount = questions.reduce((count, question, index) => {
      const qWeek = parseInt(question.week_number ?? question.week, 10);
      if (!Number.isNaN(qWeek)) return count + 1;
      return normalizedSelectedWeeks.length > 0 ? count + 1 : count;
    }, 0);
    const questionCoverage = questions.length > 0 ? 1 : 0;
    const weekCoverage = questions.length > 0 ? (weekMappedCount / questions.length) : 0;
    const outcomeCoverage = questions.length > 0 ? (matchedCount / questions.length) : 0;
    const stepProgress = Math.round(((questionCoverage + weekCoverage + outcomeCoverage) / 3) * 100);
    const transferButtonLabel = transferReady
      ? `📤 ${t('pretest.transferToExam')}`
      : t('pretest.completeSteps', 'Havuza Aktarma İçin Adımları Tamamla');
    const transferStatusText = transferReady
      ? t('pretest.transferReady')
      : t('pretest.transferNotReady');

    return (
      <div className="quiz-result">
        <div className="quiz-header">
          <h3>{t('pretest.midtermQuestionsTitle', { count: questions.length })}</h3>
          <p>{t('pretest.midtermCoverage')}</p>
        </div>

        {/* Soru Puanlarını Ayarla Bölümü - Sadece klasik sorular varsa göster */}
        {hasClassicQuestions && (
          <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h4 style={{ margin: 0, color: '#023b58', fontSize: '1.1rem' }}>{t('pretest.adjustPoints')}</h4>
              <button
                type="button"
                onClick={() => {
                  if (!editingPoints) {
                    // Edit moduna geçerken mevcut puanları state'e kopyala
                    const initialPoints = {};
                    questions.forEach((q, idx) => {
                      const qType = q.type || (q.options || q.A ? 'multiple_choice' : 'classic');
                      if (qType === 'classic') {
                        initialPoints[idx] = q.points || classicPoints;
                      }
                    });
                    setQuestionPoints(initialPoints);
                  }
                  setEditingPoints(!editingPoints);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: editingPoints ? '#6b7280' : '#023b58',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                {editingPoints ? 'İptal' : 'Düzenle'}
              </button>
            </div>

            {editingPoints && (
              <div>
                <div style={{ marginBottom: '16px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#023b58', color: 'white' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600' }}>Soru No</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '0.9rem', fontWeight: '600' }}>Soru Metni</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '0.9rem', fontWeight: '600', width: '120px' }}>Puan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.map((question, index) => {
                        const qType = question.type || (question.options || question.A ? 'multiple_choice' : 'classic');
                        if (qType !== 'classic') return null;

                        const currentPoints = questionPoints[index] !== undefined
                          ? questionPoints[index]
                          : (question.points || classicPoints);

                        return (
                          <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px', fontSize: '0.9rem', color: '#374151' }}>{index + 1}</td>
                            <td style={{ padding: '12px', fontSize: '0.9rem', color: '#374151', maxWidth: '400px' }}>
                              <div style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '400px'
                              }}>
                                {question.question || 'Soru metni yok'}
                              </div>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <input
                                type="number"
                                min="1"
                                max="100"
                                value={currentPoints}
                                onChange={(e) => {
                                  const newPoints = parseInt(e.target.value) || 1;
                                  setQuestionPoints(prev => ({
                                    ...prev,
                                    [index]: newPoints
                                  }));
                                }}
                                style={{
                                  width: '80px',
                                  padding: '6px 8px',
                                  border: '2px solid #023b58',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  textAlign: 'center'
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Toplam puan gösterimi ve validasyon */}
                {(() => {
                  const totalPoints = Object.values(questionPoints).reduce((sum, points) => sum + (points || 0), 0);
                  const isValid = totalPoints === 100;

                  return (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: isValid ? '#d1fae5' : '#fee2e2',
                        borderRadius: '8px',
                        border: `1px solid ${isValid ? '#10b981' : '#ef4444'}`
                      }}>
                        <span style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: isValid ? '#065f46' : '#991b1b'
                        }}>
                          Toplam Puan: <strong>{totalPoints}</strong> / 100
                        </span>
                        {!isValid && (
                          <span style={{
                            fontSize: '0.85rem',
                            color: '#991b1b',
                            fontStyle: 'italic'
                          }}>
                            {totalPoints < 100 ? `Eksik: ${100 - totalPoints} puan` : `Fazla: ${totalPoints - 100} puan`}
                          </span>
                        )}
                      </div>

                      <div style={{ marginTop: '12px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingPoints(false);
                            setQuestionPoints({});
                          }}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                          }}
                        >
                          İptal
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const totalPoints = Object.values(questionPoints).reduce((sum, points) => sum + (points || 0), 0);
                            if (totalPoints !== 100) {
                              alert(`Toplam puan 100 olmalıdır. Şu anki toplam: ${totalPoints}`);
                              return;
                            }

                            // midtermResult state'ini güncelle
                            const updatedQuestions = midtermResult.parsed_questions.questions.map((q, idx) => {
                              const qType = q.type || (q.options || q.A ? 'multiple_choice' : 'classic');
                              if (qType === 'classic' && questionPoints[idx] !== undefined) {
                                return { ...q, points: questionPoints[idx] };
                              }
                              return q;
                            });

                            setMidtermResult({
                              ...midtermResult,
                              parsed_questions: {
                                ...midtermResult.parsed_questions,
                                questions: updatedQuestions
                              }
                            });

                            setEditingPoints(false);
                            alert('Soru puanları başarıyla güncellendi!');
                          }}
                          disabled={(() => {
                            const totalPoints = Object.values(questionPoints).reduce((sum, points) => sum + (points || 0), 0);
                            return totalPoints !== 100;
                          })()}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: (() => {
                              const totalPoints = Object.values(questionPoints).reduce((sum, points) => sum + (points || 0), 0);
                              return totalPoints === 100 ? '#023b58' : '#9ca3af';
                            })(),
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: (() => {
                              const totalPoints = Object.values(questionPoints).reduce((sum, points) => sum + (points || 0), 0);
                              return totalPoints === 100 ? 'pointer' : 'not-allowed';
                            })(),
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            opacity: (() => {
                              const totalPoints = Object.values(questionPoints).reduce((sum, points) => sum + (points || 0), 0);
                              return totalPoints === 100 ? 1 : 0.6;
                            })()
                          }}
                        >
                          Kaydet
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        <div className="quiz-questions">
          {questions.map((question, index) => {
            // Type kontrolü - önce explicit type'a bak, sonra içeriğe göre tahmin et
            let qType = question.type;
            if (!qType) {
              // Eğer type yoksa, içeriğe göre tahmin et
              if (question.options || question.A || question.B || question.correct_answer) {
                qType = 'multiple_choice';
              } else if (question.points || question.sample_answer !== undefined) {
                qType = 'classic';
              } else {
                // Varsayılan olarak çoktan seçmeli
                qType = 'multiple_choice';
              }
            }

            console.log(`Soru ${index + 1}: type=${qType}, hasOptions=${!!(question.options || question.A)}, hasPoints=${!!question.points}`);

            if (qType === 'classic') {
              // Klasik soru görünümü
              return (
                <div key={index} className="quiz-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                  <h4>Klasik Soru {index + 1}</h4>
                  {(() => {
                    const qWeek = parseInt(question.week_number ?? question.week, 10);
                    const fallbackWeek = normalizedSelectedWeeks.length > 0
                      ? normalizedSelectedWeeks[index % normalizedSelectedWeeks.length]
                      : null;
                    const weekToShow = !Number.isNaN(qWeek) ? qWeek : fallbackWeek;
                    const weekTopicText = weeklyTopicMap[weekToShow];
                    return (
                      <div style={{ marginTop: '6px', marginBottom: '8px' }}>
                        <p style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', margin: 0 }}>
                          Hafta: {weekToShow || '-'}
                        </p>
                        {weekTopicText ? (
                          <p style={{ fontSize: '12px', color: '#334155', margin: '4px 0 0 0' }}>
                            Haftalık Konu: {weekTopicText}
                          </p>
                        ) : null}
                      </div>
                    );
                  })()}
                  <p style={{ fontSize: '16px', marginBottom: '12px' }}><MathText>{question.question}</MathText></p>
                  <div style={{ marginBottom: '12px' }}>
                    <p><strong>Puan:</strong> {question.points || 10} puan</p>
                  </div>
                  {question.sample_answer && (
                    <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                      <p><strong>{t('pretest.sampleAnswer')}</strong></p>
                      <p style={{ marginTop: '8px', color: '#1e40af' }}><MathText>{question.sample_answer}</MathText></p>
                    </div>
                  )}
                  {(() => {
                    const match = quizQuestionMatches.find(m => m.question_index === index);
                    if (!match || (!match.learning_outcomes?.length && !match.program_outcomes?.length)) return null;
                    return (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {match.learning_outcomes?.map(lo => (
                          <span key={lo.lo_id} style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '600', border: '1px solid #bfdbfe' }}>
                            {lo.lo_code}
                          </span>
                        ))}
                        {match.program_outcomes?.map(po => (
                          <span key={po.id} style={{ background: '#fef3c7', color: '#92400e', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '600', border: '1px solid #fde68a' }}>
                            {String(po.number || po.code || '').startsWith('PÇ') ? (po.number || po.code) : `PÇ${po.number || po.code || ''}`}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  {renderOutcomeAssignment(index)}
                  <div className="ai-actions" style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      className="ai-btn"
                      onClick={() => {
                        const questionWithContext = {
                          ...question,
                          number: index + 1,
                          originalIndex: index
                        };
                        openAiModal(questionWithContext, 'easy');
                      }}
                    >
                      Düzenle
                    </button>
                  </div>
                </div>
              );
            } else {
              // Çoktan seçmeli soru görünümü
              return (
                <div key={index} className="quiz-card">
                  <h4>Çoktan Seçmeli Soru {index + 1}</h4>
                  {(() => {
                    const qWeek = parseInt(question.week_number ?? question.week, 10);
                    const fallbackWeek = normalizedSelectedWeeks.length > 0
                      ? normalizedSelectedWeeks[index % normalizedSelectedWeeks.length]
                      : null;
                    const weekToShow = !Number.isNaN(qWeek) ? qWeek : fallbackWeek;
                    const weekTopicText = weeklyTopicMap[weekToShow];
                    return (
                      <div style={{ marginTop: '6px', marginBottom: '8px' }}>
                        <p style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', margin: 0 }}>
                          Hafta: {weekToShow || '-'}
                        </p>
                        {weekTopicText ? (
                          <p style={{ fontSize: '12px', color: '#334155', margin: '4px 0 0 0' }}>
                            Haftalık Konu: {weekTopicText}
                          </p>
                        ) : null}
                      </div>
                    );
                  })()}
                  <p><MathText>{question.question}</MathText></p>
                  <div className="options">
                    {(
                      question.option_keys && question.options
                        ? question.option_keys.map((key) => ({
                          key,
                          text: question.options[key]
                        }))
                        : ['A', 'B', 'C', 'D', 'E'].map((key) => ({
                          key,
                          text: question[key]
                        }))
                    ).filter(({ text }) => text && String(text).trim() !== '').map(({ key, text }) => (
                      <p key={key} className={key === question.correct_answer ? 'correct-option' : ''}>
                        <strong>{key})</strong> <MathText>{text}</MathText>
                      </p>
                    ))}
                    <p className="correct-answer"><strong>{t('pretest.correctAnswer')}</strong> {question.correct_answer}</p>
                  </div>
                  {(() => {
                    const match = quizQuestionMatches.find(m => m.question_index === index);
                    if (!match || (!match.learning_outcomes?.length && !match.program_outcomes?.length)) return null;
                    return (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {match.learning_outcomes?.map(lo => (
                          <span key={lo.lo_id} style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '600', border: '1px solid #bfdbfe' }}>
                            {lo.lo_code}
                          </span>
                        ))}
                        {match.program_outcomes?.map(po => (
                          <span key={po.id} style={{ background: '#fef3c7', color: '#92400e', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: '600', border: '1px solid #fde68a' }}>
                            {String(po.number || po.code || '').startsWith('PÇ') ? (po.number || po.code) : `PÇ${po.number || po.code || ''}`}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  {renderOutcomeAssignment(index)}
                  <div className="ai-actions" style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      className="ai-btn"
                      onClick={() => {
                        const questionWithContext = {
                          ...question,
                          number: index + 1,
                          originalIndex: index
                        };
                        openAiModal(questionWithContext, 'easy');
                      }}
                    >
                      Düzenle
                    </button>
                  </div>
                </div>
              );
            }
          })}
        </div>

        <div className="midterm-action-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
          <button 
            onClick={() => openExamPoolModal('midterm')} 
            className="btn btn-success" 
            disabled={loading || !midtermResult || !transferReady} 
            style={{ backgroundColor: '#059669', borderColor: '#059669', color: '#fff' }}
            title={transferReady ? '' : 'Önce sorular, haftalık konu ve ÖÇ/PÇ eşleştirme adımları tamamlanmalı'}
          >
            {transferButtonLabel}
          </button>
        </div>
        <div style={{ marginTop: '10px', maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
            <span style={{ color: '#334155', fontWeight: '600' }}>Havuza Aktarma Durumu</span>
            <span style={{ color: transferReady ? '#166534' : '#92400e', fontWeight: '700' }}>%{stepProgress}</span>
          </div>
          <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${stepProgress}%`,
                height: '100%',
                background: transferReady ? '#10b981' : '#2563eb',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <p style={{ textAlign: 'center', marginTop: '6px', marginBottom: 0, fontSize: '12px', color: transferReady ? '#166534' : '#92400e', fontWeight: '600' }}>
            {transferStatusText}
          </p>
        </div>
      </div>
    );
  };

  const displayHomeworkResult = () => {
    if (!homeworkResult) return null;

    let structuredHomework = homeworkResult.homework;
    if (structuredHomework && typeof structuredHomework === 'string') {
      try {
        structuredHomework = JSON.parse(structuredHomework);
      } catch (parseError) {
        console.warn('Ödev JSON parse edilemedi, metin olarak gösterilecek');
        structuredHomework = null;
      }
    }

    const homeworkText = homeworkResult.homework_text;
    const resolvedSubject = homeworkResult.subject || subject;
    const selectedWeeksInfo = homeworkResult.selected_weeks || [];
    const selectedWeeksLabel = Array.isArray(selectedWeeksInfo)
      ? selectedWeeksInfo
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            if (item.week !== undefined) {
              return `Hafta ${item.week}`;
            }
            if (item.title) {
              return item.title;
            }
            return JSON.stringify(item);
          }
          return `Hafta ${item}`;
        })
        .join(', ')
      : '';

    const getArray = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        return value
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean);
      }
      return [];
    };

    const learningGoals =
      getArray(
        structuredHomework?.learning_goals ||
        structuredHomework?.learning_objectives ||
        structuredHomework?.objectives
      );

    const tasksRaw =
      structuredHomework?.tasks ||
      structuredHomework?.steps ||
      structuredHomework?.assignments ||
      [];

    const tasks = Array.isArray(tasksRaw)
      ? tasksRaw.filter(Boolean)
      : [];

    const evaluationCriteriaRaw =
      structuredHomework?.assessment ||
      structuredHomework?.evaluation ||
      structuredHomework?.rubric ||
      [];

    const evaluationCriteria = Array.isArray(evaluationCriteriaRaw)
      ? evaluationCriteriaRaw.filter(Boolean)
      : [];

    const submissionInstructionsRaw =
      structuredHomework?.submission_instructions ||
      structuredHomework?.submission ||
      structuredHomework?.instructions ||
      [];

    const submissionInstructions = getArray(submissionInstructionsRaw);

    return (
      <div className="homework-result">
        <div className="homework-header">
          <h3>{t('pretest.homeworkPlanTitle')}</h3>
          <p>{resolvedSubject}</p>
          {selectedWeeksLabel && (
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              {t('pretest.selectedWeeks')} {selectedWeeksLabel}
            </p>
          )}
        </div>

        {structuredHomework ? (
          <div className="homework-body">
            {(structuredHomework.summary || structuredHomework.overview) && (
              <div className="homework-section">
                <h4>{t('pretest.homeworkSummary')}</h4>
                <p>{structuredHomework.summary || structuredHomework.overview}</p>
              </div>
            )}

            {learningGoals.length > 0 && (
              <div className="homework-section">
                <h4>{t('pretest.homeworkLearningGoals')}</h4>
                <ul>
                  {learningGoals.map((goal, index) => (
                    <li key={index}>{goal}</li>
                  ))}
                </ul>
              </div>
            )}

            {tasks.length > 0 && (
              <div className="homework-section">
                <h4>{t('pretest.homeworkTasks')}</h4>
                <div className="homework-tasks">
                  {tasks.map((task, index) => (
                    <div key={index} className="homework-card">
                      <h5>
                        {index + 1}. {task.title || task.name || t('pretest.homeworkTask')}
                      </h5>
                      {(task.description || task.details) && (
                        <p>{task.description || task.details}</p>
                      )}
                      {getArray(task.steps).length > 0 && (
                        <div>
                          <strong>{t('pretest.homeworkSteps')}</strong>
                          <ul>
                            {getArray(task.steps).map((step, stepIndex) => (
                              <li key={stepIndex}>{step}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {getArray(task.deliverables).length > 0 && (
                        <div>
                          <strong>{t('pretest.homeworkDeliverables')}</strong>
                          <ul>
                            {getArray(task.deliverables).map(
                              (deliverable, deliverableIndex) => (
                                <li key={deliverableIndex}>{deliverable}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                      {task.estimated_time_hours && (
                        <p>
                          <strong>{t('pretest.homeworkEstimatedTime')}</strong> {task.estimated_time_hours}{' '}
                          {t('pretest.homeworkHours')}
                        </p>
                      )}
                      {task.resources && getArray(task.resources).length > 0 && (
                        <div>
                          <strong>{t('pretest.homeworkResources')}</strong>
                          <ul>
                            {getArray(task.resources).map(
                              (resource, resourceIndex) => (
                                <li key={resourceIndex}>{resource}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {evaluationCriteria.length > 0 && (
              <div className="homework-section">
                <h4>{t('pretest.homeworkCriteria')}</h4>
                <div className="homework-rubric">
                  {evaluationCriteria.map((criteria, index) => (
                    <div key={index} className="homework-card">
                      <h5>{criteria.criteria || criteria.title || t('pretest.homeworkCriterion')}</h5>
                      {criteria.weight && (
                        <p>
                          <strong>{t('pretest.homeworkWeight')}</strong> %{criteria.weight}
                        </p>
                      )}
                      {criteria.expectations && (
                        <p>{criteria.expectations}</p>
                      )}
                      {criteria.excellent && (
                        <p>
                          <strong>{t('pretest.homeworkExcellent')}</strong> {criteria.excellent}
                        </p>
                      )}
                      {criteria.good && (
                        <p>
                          <strong>{t('pretest.homeworkGood')}</strong> {criteria.good}
                        </p>
                      )}
                      {criteria.satisfactory && (
                        <p>
                          <strong>{t('pretest.homeworkSatisfactory')}</strong> {criteria.satisfactory}
                        </p>
                      )}
                      {criteria.needs_improvement && (
                        <p>
                          <strong>{t('pretest.homeworkNeedsImprovement')}</strong> {criteria.needs_improvement}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {submissionInstructions.length > 0 && (
              <div className="homework-section">
                <h4>{t('pretest.homeworkSubmission')}</h4>
                <ul>
                  {submissionInstructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          homeworkText ? (
            <div className="homework-text">
              <pre>{homeworkText}</pre>
            </div>
          ) : null
        )}

        <div className="download-buttons">
          <button
            onClick={handleDownloadHomework}
            className="btn btn-primary"
            disabled={downloadingHomework}
          >
            {downloadingHomework ? 'PDF hazırlanıyor...' : 'Ödevi PDF Olarak İndir'}
          </button>
        </div>
      </div>
    );
  };

  // Sınav matrisi analizi gösterme


  return (
    <div className="pretest-container">
      {pretestBlocked && (
        <div className="error-message" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74' }}>
          {t('pretest.pretestDisabled')}
        </div>
      )}
      <h2>{t('pretest.title')}</h2>
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}

      {/* Ana Butonlar */}
      <div className="main-buttons">
        <div className="nav-tabs-group">
          <button
            onClick={() => { if (!pretestBlocked) handleViewChange('pretest'); }}
            className={`btn ${currentView === 'pretest' ? 'btn-primary' : 'btn-secondary'} ${pretestBlocked ? 'btn-disabled' : ''}`}
            disabled={pretestBlocked}
            title={pretestBlocked ? t('pretest.pretestDisabledTitle') : ''}
          >
            {t('pretest.createPretest')}
          </button>
          <button
            onClick={() => handleViewChange('difficulty')}
            className={`btn ${currentView === 'difficulty' ? 'btn-primary' : 'btn-secondary'}`}
          >
            {t('pretest.createStudyTopics')}
          </button>
          <button
            onClick={() => handleViewChange('quiz')}
            className={`btn ${currentView === 'quiz' ? 'btn-primary' : 'btn-secondary'}`}
          >
            {t('pretest.createQuiz')}
          </button>
          <button
            onClick={() => handleViewChange('midterm')}
            className={`btn ${currentView === 'midterm' ? 'btn-primary' : 'btn-secondary'}`}
          >
            {t('pretest.createMidterm')}
          </button>
          <button
            onClick={() => handleViewChange('homework')}
            className={`btn ${currentView === 'homework' ? 'btn-primary' : 'btn-secondary'}`}
          >
            {t('pretest.createHomework')}
          </button>
          {archiveReadOnly && (
            <button
              onClick={() => handleViewChange('saved')}
              className={`btn ${currentView === 'saved' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {t('pretest.savedItems')}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {selectedCourseId && !archiveReadOnly && (
            <button
              type="button"
              onClick={openManualQuestionPanel}
              className="btn btn-primary"
            >
              {t('pretest.manualAddQuestion')}
            </button>
          )}
          <button
            onClick={handleReset}
            className="btn btn-danger"
            disabled={archiveReadOnly}
          >
            {t('pretest.resetTest')}
          </button>
        </div>
      </div>

      {/* Manuel Soru Ekleme Paneli — butonlar satırının altında açılır */}
      {manualQuestionPanelOpen && selectedCourseId && (
        <div className="manuel-soru-panel">
          <div className="manuel-soru-panel-header">
            <h4>{t('pretest.manualAddQuestion')}</h4>
            <button className="btn btn-secondary" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }} onClick={() => setManualQuestionPanelOpen(false)}>✕ {t('common.close')}</button>
          </div>
          <div className="manuel-soru-panel-body">

            {/* Ders + zorunlu soru kategorisi + izlence değerlendirme türü */}
            <div className="manuel-soru-info-bar">
              <span><strong>{t('pretest.subjectLabel')}</strong> {selectedCourse?.courseName || t('pretest.selectCourse')}</span>
              <span style={{ color: '#cbd5e1' }}>|</span>
              <label htmlFor="manualQuestionCategory"><strong>Soru Kategorisi *</strong></label>
              <div className="form-group" style={{ margin: 0, minWidth: '210px' }}>
                <select
                  id="manualQuestionCategory"
                  value={manualQuestionCategory}
                  onChange={(e) => {
                    setManualQuestionCategory(e.target.value);
                    setManualQuestionError('');
                  }}
                  required
                >
                  <option value="">Kategori seçiniz</option>
                  {QUESTION_CATEGORY_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>
              <span style={{ color: '#cbd5e1' }}>|</span>
              <label htmlFor="manualExamWeight"><strong>Değerlendirme / Sınav Türü *</strong></label>
              <div className="form-group" style={{ margin: 0, minWidth: '250px' }}>
                <select
                  id="manualExamWeight"
                  value={manualExamWeightId}
                  onChange={(e) => {
                    setManualExamWeightId(e.target.value);
                    setManualQuestionError('');
                  }}
                  required
                >
                  <option value="">Sınav türü seçiniz</option>
                  {examTypes.filter(item => item.exam_type).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.exam_type}{item.weight != null ? ` (%${item.weight})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Excel Yükleme */}
            <div
              className={`manuel-excel-drop${manualExcelDragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setManualExcelDragOver(true); }}
              onDragLeave={() => setManualExcelDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setManualExcelDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
                  setManualExcelFile(file);
                  setManualQuestionError('');
                } else {
                  setManualQuestionError(t('pretest.excelOnlyError'));
                }
              }}
              onClick={() => document.getElementById('manualExcelInput').click()}
            >
              <input
                id="manualExcelInput"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) { setManualExcelFile(file); setManualQuestionError(''); }
                }}
              />
              {manualExcelFile ? (
                <>
                  <div className="drop-text" style={{ color: '#023b58', fontWeight: '600' }}>{manualExcelFile.name}</div>
                  <div className="drop-sub">{(manualExcelFile.size / 1024).toFixed(1)} KB</div>
                </>
              ) : (
                <>
                  <div className="drop-text">{t('pretest.dropExcelHere')}</div>
                  <div className="drop-sub">.xlsx veya .xls</div>
                </>
              )}
            </div>

            {manualQuestionError && (
              <div style={{ padding: '8px 14px', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: '8px', fontSize: '13px', border: '1px solid #fecaca' }}>
                {manualQuestionError}
              </div>
            )}
            {manualQuestionSuccess && (
              <div style={{ padding: '8px 14px', backgroundColor: '#f0fdf4', color: '#16a34a', borderRadius: '8px', fontSize: '13px', border: '1px solid #bbf7d0' }}>
                {manualQuestionSuccess}
              </div>
            )}

            <div className="manuel-soru-actions">
              <button
                onClick={saveManualQuestion}
                disabled={manualQuestionSaving || !manualExcelFile || !manualQuestionCategory || !manualExamWeightId}
                className="btn btn-primary"
                style={{ opacity: (!manualExcelFile || !manualQuestionCategory || !manualExamWeightId) ? 0.5 : 1, cursor: (manualQuestionSaving || !manualExcelFile || !manualQuestionCategory || !manualExamWeightId) ? 'not-allowed' : 'pointer' }}
              >
                {manualQuestionSaving ? 'Yükleniyor...' : 'Soruları Yükle'}
              </button>
              <a href={API_ENDPOINTS.DOWNLOAD_QUESTIONS_TEMPLATE} download className="btn btn-secondary sablon-link">
                Örnek Excel Şablonunu İndir
              </a>
            </div>

            {/* Kaydedilen Sorular ve ÖÇ Eşleştirme */}
            {savedManualQuestions.length > 0 && (
              <div className="manuel-soru-list">
                <div className="manuel-soru-list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t('pretest.loadedQuestions')} ({savedManualQuestions.length}) — {t('pretest.ocMatchingTitle')}</span>
                  <button
                    className="btn btn-primary"
                    disabled={bulkManualAiLoading || !selectedCourse?.courseId}
                    style={{ fontSize: '13px', padding: '6px 16px', whiteSpace: 'nowrap', opacity: bulkManualAiLoading ? 0.7 : 1 }}
                    onClick={async () => {
                      if (!selectedCourse?.courseId) { toast.error(t('pretest.selectCourseError')); return; }
                      setBulkManualAiLoading(true);
                      for (const q of savedManualQuestions) {
                        if (!q.text) continue;
                        setManualQuestionAiLoading(prev => ({ ...prev, [q.id]: true }));
                        try {
                          const res = await axios.post(API_ENDPOINTS.AI_MATCH_QUESTION, {
                            question_text: q.text,
                            course_id: selectedCourse.courseId
                          });
                          if (res.data.success) {
                            const matches = res.data.matches || [];
                            setManualQuestionAiMatches(prev => ({ ...prev, [q.id]: matches }));
                            // %60+ yeşil ÖÇ'leri otomatik ekle
                            const greenCodes = matches.filter(m => (m.match_percentage || 0) >= 60).map(m => m.code);
                            if (greenCodes.length > 0) {
                              const current = q.outcomes || [];
                              const merged = [...new Set([...current, ...greenCodes])];
                              await saveManualQuestionOutcomes(q.id, q.text || '', merged);
                            }
                          }
                        } catch (err) {
                          console.error('Toplu AI eşleştirme hatası:', err);
                        } finally {
                          setManualQuestionAiLoading(prev => ({ ...prev, [q.id]: false }));
                        }
                      }
                      setBulkManualAiLoading(false);
                      toast.success(t('pretest.matchingDone'));
                    }}
                  >
                    {bulkManualAiLoading ? t('pretest.analyzing') : t('pretest.bulkMatch')}
                  </button>
                </div>
                <table className="manuel-soru-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('pretest.questionNumber')}</th>
                      <th>Görsel</th>
                      <th>Hafta</th>
                      <th>{t('pretest.matchedOCs')}</th>
                      <th>{t('pretest.selectChangeOC')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedManualQuestions.map((question, index) => {
                      const yellowMatches = (manualQuestionAiMatches[question.id] || [])
                        .filter(m => (m.match_percentage || 0) >= 45 && (m.match_percentage || 0) < 60 && !(question.outcomes || []).includes(m.code));
                      return (
                        <React.Fragment key={question.id}>
                          <tr className="soru-row" style={{ background: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <td className="td-no">
                              {manualQuestionAiLoading[question.id] ? '⏳' : index + 1}
                            </td>
                            <td className="td-soru">
                              {question.text.length > 120 ? (
                                <>
                                  <MathText>{expandedManualQuestion === question.id ? question.text : question.text.substring(0, 120) + '... '}</MathText>
                                  <button 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setExpandedManualQuestion(expandedManualQuestion === question.id ? null : question.id);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#4c1d95',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      padding: 0,
                                      fontWeight: 600,
                                      fontSize: '13px',
                                      marginLeft: '4px'
                                    }}
                                  >
                                    {expandedManualQuestion === question.id ? t('pretest.closeQuestion') : t('pretest.showQuestion')}
                                  </button>
                                </>
                              ) : <MathText>{question.text}</MathText>}
                            </td>
                            <td className="td-image" style={{ width: 120, textAlign: 'center' }}>
                              {question.image_url ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                  <img
                                    src={/^(https?:|blob:|data:)/.test(question.image_url || '') ? question.image_url : `${API_BASE_URL}${question.image_url}`}
                                    alt="soru"
                                    style={{ maxWidth: 100, maxHeight: 80, borderRadius: 6, border: '1px solid #cbd5e1', cursor: 'zoom-in', objectFit: 'cover' }}
                                    onClick={() => window.open(/^(https?:|blob:|data:)/.test(question.image_url || '') ? question.image_url : `${API_BASE_URL}${question.image_url}`, '_blank')}
                                  />
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <label style={{ fontSize: 11, color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}>
                                      Değiştir
                                      <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                          const f = e.target.files?.[0];
                                          if (f) uploadQuestionImage(question.id, f);
                                          e.target.value = '';
                                        }}
                                      />
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => deleteQuestionImage(question.id)}
                                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11, textDecoration: 'underline', padding: 0 }}
                                    >
                                      Kaldır
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <label style={{
                                  display: 'inline-block',
                                  padding: '6px 10px',
                                  background: '#f1f5f9',
                                  border: '1px dashed #94a3b8',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontSize: 12,
                                  color: '#334155'
                                }}>
                                  + Görsel Ekle
                                  <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) uploadQuestionImage(question.id, f);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              )}
                            </td>
                            <td style={{ width: 110, textAlign: 'center' }}>
                              {loadingWeeklyTopics ? (
                                <span style={{ fontSize: '12px', color: '#9ca3af' }}>Yükleniyor...</span>
                              ) : weeklyTopics.length > 0 ? (
                                <select
                                  value={question.week_number || ''}
                                  title={(() => {
                                    const found = weeklyTopics.find(wt => wt.week === question.week_number);
                                    return found ? `Hafta ${found.week}${found.topic ? ` - ${found.topic}` : ''}` : '';
                                  })()}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                    setSavedManualQuestions(prev => prev.map(q =>
                                      String(q.id) === String(question.id) ? { ...q, week_number: val } : q
                                    ));
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    border: question.week_number ? '1px solid #6ee7b7' : '1px solid #d1d5db',
                                    fontSize: '12px',
                                    backgroundColor: question.week_number ? '#f0fdf4' : '#fff',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <option value="">Seçin...</option>
                                  {weeklyTopics.map(topic => (
                                    <option key={topic.week} value={topic.week} title={topic.topic ? `Hafta ${topic.week} - ${topic.topic}` : `Hafta ${topic.week}`}>
                                      Hafta {topic.week}{topic.topic ? ` - ${topic.topic.substring(0, 20)}${topic.topic.length > 20 ? '...' : ''}` : ''}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="number"
                                  min="1"
                                  max="16"
                                  placeholder="Hafta"
                                  value={question.week_number || ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                    setSavedManualQuestions(prev => prev.map(q =>
                                      String(q.id) === String(question.id) ? { ...q, week_number: val } : q
                                    ));
                                  }}
                                  style={{
                                    width: '70px',
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    border: question.week_number ? '1px solid #6ee7b7' : '1px solid #d1d5db',
                                    fontSize: '12px',
                                    backgroundColor: question.week_number ? '#f0fdf4' : '#fff',
                                    textAlign: 'center'
                                  }}
                                />
                              )}
                            </td>
                            <td className="td-oc">
                              {question.outcomes?.filter(o => o.startsWith('ÖÇ')).length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {question.outcomes.filter(o => o.startsWith('ÖÇ')).map((code, idx, arr) => {
                                    const mapping = outcomeMappings.find(m => m.code === code);
                                    const linkedPcs = mapping?.program_outcomes || [];
                                    const isLast = idx === arr.length - 1;
                                    return (
                                      <div key={code} style={{ 
                                          display: 'flex', 
                                          flexWrap: 'wrap', 
                                          alignItems: 'center', 
                                          gap: '6px',
                                          paddingBottom: isLast ? '0' : '8px',
                                          borderBottom: isLast ? 'none' : '1px dashed #cbd5e1'
                                        }}>
                                        <span className="oc-badge">{code}</span>
                                        {linkedPcs.map(pc => (
                                          <span key={pc.code} className="pc-badge">{pc.code}</span>
                                        ))}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>—</span>
                              )}
                            </td>
                            <td className="td-select">
                              {manualLoadingOutcomes ? (
                                <span style={{ color: '#6b7280', fontSize: '12px' }}>{t('common.loading')}</span>
                              ) : (
                                <Select
                                  isMulti
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  options={manualLearningOutcomes.map(lo => ({
                                    value: lo.id,
                                    label: `${lo.code}: ${lo.description}`,
                                    code: lo.code
                                  }))}
                                  value={manualLearningOutcomes
                                    .filter(lo => question.outcomes && question.outcomes.includes(lo.code))
                                    .map(lo => ({
                                      value: lo.id,
                                      label: `${lo.code}: ${lo.description}`,
                                      code: lo.code
                                    }))}
                                  onChange={async (selected) => {
                                    const selectedCodes = (selected || []).map(s => s.code);
                                    await saveManualQuestionOutcomes(question.id, question.text || '', selectedCodes);
                                  }}
                                  placeholder={t('pretest.ocPlaceholder')}
                                  styles={{
                                    control: (base) => ({ ...base, minHeight: '36px', fontSize: '12px', borderColor: '#023b58', '&:hover': { borderColor: '#023b58' } }),
                                    multiValue: (base) => ({ ...base, backgroundColor: '#dcfce7', fontSize: '11px' }),
                                    multiValueLabel: (base) => ({ ...base, color: '#0f172a', fontWeight: 600 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    placeholder: (base) => ({ ...base, fontSize: '12px' })
                                  }}
                                />
                              )}
                            </td>
                          </tr>
                          {yellowMatches.length > 0 && (
                            <tr className="yellow-row" style={{ background: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                              <td colSpan={5}></td>
                              <td style={{ paddingTop: '0', paddingBottom: '10px' }}>
                                <div className="yellow-suggestions-label">{t('pretest.moderateMatches')}</div>
                                <div className="yellow-suggestions">
                                  {yellowMatches.sort((a, b) => (b.match_percentage || 0) - (a.match_percentage || 0)).map((match, idx) => (
                                    <button
                                      key={idx}
                                      className="yellow-suggestion-btn"
                                      onClick={async () => {
                                        const outcome = manualLearningOutcomes.find(o => o.code === match.code);
                                        if (outcome) await updateManualQuestionOutcomes(outcome.id, true, question.id);
                                      }}
                                    >
                                      <span>{match.code}</span>
                                      <span className="ys-pct">%{match.match_percentage}</span>
                                      <span className="ys-ekle">{t('pretest.addMatch')}</span>
                                    </button>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>

                {/* Sınav Modülüne Aktar butonu */}
                {(() => {
                  const allMapped = savedManualQuestions.every(q => q.outcomes && q.outcomes.length > 0);
                  const unmappedCount = savedManualQuestions.filter(q => !q.outcomes || q.outcomes.length === 0).length;
                  const noWeekCount = savedManualQuestions.filter(q => !q.week_number).length;
                  const canTransfer = allMapped && noWeekCount === 0 && Boolean(manualQuestionCategory) && Boolean(manualExamWeightId);
                  return (
                    <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                      {!allMapped && (
                        <span style={{ fontSize: '13px', color: '#dc2626' }}>
                          ⚠️ {unmappedCount} {t('pretest.unmappedWarning')}
                        </span>
                      )}
                      {allMapped && noWeekCount > 0 && (
                        <span style={{ fontSize: '13px', color: '#dc2626' }}>
                          ⚠️ {noWeekCount} soru için hafta seçilmedi
                        </span>
                      )}
                      {!manualQuestionCategory && (
                        <span style={{ fontSize: '13px', color: '#dc2626' }}>
                          ⚠️ Soru kategorisi seçilmedi
                        </span>
                      )}
                      {!manualExamWeightId && (
                        <span style={{ fontSize: '13px', color: '#dc2626' }}>
                          ⚠️ Değerlendirme/sınav türü seçilmedi
                        </span>
                      )}
                      <button
                        className="btn btn-primary"
                        disabled={!canTransfer}
                        style={{
                          backgroundColor: canTransfer ? '#10b981' : '#9ca3af',
                          borderColor: canTransfer ? '#10b981' : '#9ca3af',
                          cursor: canTransfer ? 'pointer' : 'not-allowed',
                          opacity: canTransfer ? 1 : 0.7
                        }}
                        onClick={() => openExamPoolModal('manual')}
                      >
                        📤 {t('pretest.transferToExam')}
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kaydedilmiş Öğeler Görüntüleme */}
      {currentView === 'saved' && (
        <div className="pretest-form pretest-saved-view">
          <h3>{t('pretest.savedItems')}</h3>

          {/* Ders Seçimi */}
          <div className="form-group exam-course-field" style={{ marginBottom: '24px' }}>
            <label htmlFor="savedCourseSelect">{t('pretest.selectCourseLabel')}</label>
            <ExamCourseDropdown
              id="savedCourseSelect"
              value={selectedCourseId}
              onChange={(value) => handleCourseSelect({ target: { value } })}
              loading={loadingCourses}
              courses={assignedCourses}
              placeholder={t('pretest.selectCourse')}
              loadingPlaceholder={t('pretest.loadingCourses')}
              label={t('pretest.selectCourseLabel')}
            />
          </div>

          {!selectedCourseId ? (
            <div className="info-message" style={{
              padding: '20px',
              backgroundColor: '#f0f4ff',
              borderRadius: '8px',
              border: '1px solid #90caf9',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#1976d2', fontWeight: '500' }}>
                {t('pretest.selectCourseFirst')}
              </p>
            </div>
          ) : (
            <>
              {/* Tip Seçimi Dropdown */}
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label htmlFor="savedTypeSelect">{t('pretest.selectTypeLabel')}</label>
                <select
                  id="savedTypeSelect"
                  value={selectedSavedType}
                  onChange={(e) => {
                    setSelectedSavedType(e.target.value);
                    // Tip değiştiğinde seçimleri temizle
                    setSelectedPretests([]);
                    setSelectedStudyTopics([]);
                    setSelectedQuizzes([]);
                    setSelectedHomeworks([]);
                  }}
                  required
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '15px',
                    width: '100%',
                    maxWidth: '500px',
                    fontFamily: 'inherit',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="" disabled>
                    {t('pretest.selectTypePlaceholder')}
                  </option>
                  <option value="pretest">
                    {t('pretest.pretests')} ({savedPretests.length})
                  </option>
                  <option value="study_topics">
                    {t('pretest.studyTopicsLabel')} ({savedStudyTopics.length})
                  </option>
                  <option value="quiz">
                    {t('pretest.quizzes')} ({savedQuizzes.length})
                  </option>
                  <option value="midterm">
                    {t('pretest.midterms')} ({savedMidterms.length})
                  </option>
                  <option value="homework">
                    {t('pretest.homeworks')} ({savedHomeworks.length})
                  </option>
                </select>
              </div>

              {loadingSaved ? (
                <div className="loading-message" style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  <p>{t('common.loading')}</p>
                </div>
              ) : selectedSavedType === 'pretest' ? (
                <div className="saved-section" inert={archiveReadOnly ? '' : undefined}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#22304a' }}>
                      {t('pretest.pretests')} ({savedPretests.length})
                    </h3>
                    {savedPretests.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            if (selectedPretests.length === savedPretests.length) {
                              setSelectedPretests([]);
                            } else {
                              setSelectedPretests(savedPretests.map(p => p.id));
                            }
                          }}
                          className="btn btn-sm btn-secondary"
                        >
                          {selectedPretests.length === savedPretests.length ? t('pretest.deselectAll') : t('pretest.selectAll')}
                        </button>
                        {selectedPretests.length > 0 && (
                          <button
                            onClick={async () => {
                              const count = selectedPretests.length;
                              if (!window.confirm(`${count} öntesti silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                return;
                              }
                              try {
                                setLoading(true);
                                const deletePromises = selectedPretests.map(id =>
                                  axios.delete(`${API_BASE_URL}/api/delete_pretest/${id}`)
                                );
                                const results = await Promise.allSettled(deletePromises);

                                // Başarılı ve başarısız silmeleri kontrol et
                                const successful = results.filter(r => r.status === 'fulfilled' && r.value?.data?.success).length;
                                const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value?.data?.success)).length;

                                setSelectedPretests([]);
                                await loadSavedItems();

                                if (failed > 0) {
                                  alert(`${successful} öntest başarıyla silindi, ${failed} öntest silinemedi`);
                                } else {
                                  alert(`${count} öntest başarıyla silindi`);
                                }
                              } catch (err) {
                                console.error('Öntest silme hatası:', err);
                                setError(err.response?.data?.error || err.message || 'Öntestler silinirken hata oluştu');
                                alert('Öntestler silinirken bir hata oluştu: ' + (err.response?.data?.error || err.message || 'Bilinmeyen hata'));
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="btn btn-sm btn-danger"
                          >
                            {t('pretest.deleteSelected')} ({selectedPretests.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {savedPretests.length === 0 ? (
                    <p className="empty-message" style={{
                      padding: '20px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      {t('pretest.noPretestsSaved')}
                    </p>
                  ) : (
                    <div className="items-list" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '16px'
                    }}>
                      {savedPretests.map((pretest) => (
                        <div key={pretest.id} className="item-card" style={{
                          padding: '20px',
                          backgroundColor: selectedPretests.includes(pretest.id) ? '#fef2f2' : 'white',
                          borderRadius: '12px',
                          border: selectedPretests.includes(pretest.id) ? '2px solid #dc2626' : '1px solid #e5e7eb',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <input
                              type="checkbox"
                              checked={selectedPretests.includes(pretest.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedPretests([...selectedPretests, pretest.id]);
                                } else {
                                  setSelectedPretests(selectedPretests.filter(id => id !== pretest.id));
                                }
                              }}
                              style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <h4 style={{ margin: '0 0 12px 0', color: '#22304a', fontSize: '16px', fontWeight: '700' }}>
                                {pretest.subject}
                              </h4>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.questionCountShort')} <strong>{pretest.question_count}</strong>
                              </p>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.createdAt')} {new Date(pretest.created_at).toLocaleDateString('tr-TR')}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(API_ENDPOINTS.GET_PRETEST(pretest.id));
                                      if (res.data.success) {
                                        setSelectedPretest(res.data.pretest);
                                        const saved = res.data.pretest;
                                        const restored = pretestPreview(saved.parsed_questions);
                                        setResult({ ...saved, course_id: saved.course_id });
                                        setPreviewQuestions(restored);
                                        setOptionCount(saved.option_count);
                                        setPretestTransfer({ id: saved.transfer_id, pretestId: saved.id, version: saved.version,
                                          snapshot: pretestSignature(restored), transferred: true });
                                        setSavePretestSuccess(true);
                                        setSavePretestMessage('Sorular aktarıldı ✓');
                                        setCheckingPretestTransfer(false);
                                        setCurrentView('pretest');

                                      }
                                    } catch (err) {
                                      setError('Öntest yüklenirken hata oluştu');
                                    }
                                  }}
                                  className="btn btn-sm btn-primary"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.view')}
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`${pretest.subject}`)) {
                                      return;
                                    }
                                    try {
                                      setLoading(true);
                                      const res = await axios.delete(`${API_BASE_URL}/api/delete_pretest/${pretest.id}`);
                                      if (res.data.success) {
                                        await loadSavedItems();
                                        setSelectedPretests(selectedPretests.filter(id => id !== pretest.id));
                                      } else {
                                        throw new Error(res.data.error || '');
                                      }
                                    } catch (err) {
                                      setError(err.response?.data?.error || err.message || '');
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                  className="btn btn-sm btn-danger"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.delete')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : selectedSavedType === 'study_topics' ? (
                <div className="saved-section" inert={archiveReadOnly ? '' : undefined}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#22304a' }}>
                      {t('pretest.studyTopicsLabel')} ({savedStudyTopics.length})
                    </h3>
                    {savedStudyTopics.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            if (selectedStudyTopics.length === savedStudyTopics.length) {
                              setSelectedStudyTopics([]);
                            } else {
                              setSelectedStudyTopics(savedStudyTopics.map(t => t.id));
                            }
                          }}
                          className="btn btn-sm btn-secondary"
                        >
                          {selectedStudyTopics.length === savedStudyTopics.length ? t('pretest.deselectAll') : t('pretest.selectAll')}
                        </button>
                        {selectedStudyTopics.length > 0 && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`${selectedStudyTopics.length} çalışma konusunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                return;
                              }
                              try {
                                setLoading(true);
                                const deletePromises = selectedStudyTopics.map(id =>
                                  axios.delete(`${API_BASE_URL}/api/delete_study_topic/${id}`)
                                );
                                await Promise.all(deletePromises);
                                setSelectedStudyTopics([]);
                                await loadSavedItems();
                                alert(`${selectedStudyTopics.length} çalışma konusu başarıyla silindi`);
                              } catch (err) {
                                setError(err.response?.data?.error || err.message || 'Çalışma konuları silinirken hata oluştu');
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="btn btn-sm btn-danger"
                          >
                            {t('pretest.deleteSelected')} ({selectedStudyTopics.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {savedStudyTopics.length === 0 ? (
                    <p className="empty-message" style={{
                      padding: '20px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      {t('pretest.noStudyTopicsSaved')}
                    </p>
                  ) : (
                    <div className="items-list" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '16px'
                    }}>
                      {savedStudyTopics.map((topic) => (
                        <div key={topic.id} className="item-card" style={{
                          padding: '20px',
                          backgroundColor: selectedStudyTopics.includes(topic.id) ? '#fef2f2' : 'white',
                          borderRadius: '12px',
                          border: selectedStudyTopics.includes(topic.id) ? '2px solid #dc2626' : '1px solid #e5e7eb',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <input
                              type="checkbox"
                              checked={selectedStudyTopics.includes(topic.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStudyTopics([...selectedStudyTopics, topic.id]);
                                } else {
                                  setSelectedStudyTopics(selectedStudyTopics.filter(id => id !== topic.id));
                                }
                              }}
                              style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <h4 style={{ margin: '0 0 12px 0', color: '#22304a', fontSize: '16px', fontWeight: '700' }}>
                                {topic.subject}
                              </h4>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                Tür: <strong>{topic.question_type === 'open_ended' ? 'Açık Uçlu' : 'Çoktan Seçmeli'}</strong>
                              </p>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.createdAt')} {new Date(topic.created_at).toLocaleDateString('tr-TR')}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(API_ENDPOINTS.GET_STUDY_TOPIC(topic.id));
                                      if (res.data.success) {
                                        setSelectedStudyTopic(res.data.study_topic);
                                        setQuestionsResult({
                                          questions_content: res.data.study_topic.questions_content,
                                          parsed_questions: res.data.study_topic.parsed_questions,
                                          subject: res.data.study_topic.subject
                                        });
                                        setCurrentView('difficulty');
                                      }
                                    } catch (err) {
                                      setError('Çalışma konusu yüklenirken hata oluştu');
                                    }
                                  }}
                                  className="btn btn-sm btn-primary"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.view')}
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`${topic.subject} çalışma konusunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                      return;
                                    }
                                    try {
                                      setLoading(true);
                                      const res = await axios.delete(`${API_BASE_URL}/api/delete_study_topic/${topic.id}`);
                                      if (res.data.success) {
                                        await loadSavedItems();
                                        setSelectedStudyTopics(selectedStudyTopics.filter(id => id !== topic.id));
                                        alert('Çalışma konusu başarıyla silindi');
                                      } else {
                                        throw new Error(res.data.error || 'Silme başarısız');
                                      }
                                    } catch (err) {
                                      setError(err.response?.data?.error || err.message || 'Çalışma konusu silinirken hata oluştu');
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                  className="btn btn-sm btn-danger"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.delete')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : selectedSavedType === 'quiz' ? (
                <div className="saved-section" inert={archiveReadOnly ? '' : undefined}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#22304a' }}>
                      {t('pretest.quizzesTitle')} ({savedQuizzes.length})
                    </h3>
                    {savedQuizzes.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            if (selectedQuizzes.length === savedQuizzes.length) {
                              setSelectedQuizzes([]);
                            } else {
                              setSelectedQuizzes(savedQuizzes.map(q => q.id));
                            }
                          }}
                          className="btn btn-sm btn-secondary"
                        >
                          {selectedQuizzes.length === savedQuizzes.length ? t('pretest.deselectAll') : t('pretest.selectAll')}
                        </button>
                        {selectedQuizzes.length > 0 && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`${selectedQuizzes.length} kısa sınavı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                return;
                              }
                              try {
                                setLoading(true);
                                const deletePromises = selectedQuizzes.map(id =>
                                  axios.delete(`${API_BASE_URL}/api/delete_quiz/${id}`)
                                );
                                await Promise.all(deletePromises);
                                setSelectedQuizzes([]);
                                await loadSavedItems();
                                alert(`${selectedQuizzes.length} kısa sınav başarıyla silindi`);
                              } catch (err) {
                                setError(err.response?.data?.error || err.message || 'Kısa sınavlar silinirken hata oluştu');
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="btn btn-sm btn-danger"
                          >
                            {t('pretest.deleteSelected')} ({selectedQuizzes.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {savedQuizzes.length === 0 ? (
                    <p className="empty-message" style={{
                      padding: '20px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      {t('pretest.noQuizzesSaved')}
                    </p>
                  ) : (
                    <div className="items-list" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '16px'
                    }}>
                      {savedQuizzes.map((quiz) => (
                        <div key={quiz.id} className="item-card" style={{
                          padding: '20px',
                          backgroundColor: selectedQuizzes.includes(quiz.id) ? '#fef2f2' : 'white',
                          borderRadius: '12px',
                          border: selectedQuizzes.includes(quiz.id) ? '2px solid #dc2626' : '1px solid #e5e7eb',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <input
                              type="checkbox"
                              checked={selectedQuizzes.includes(quiz.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedQuizzes([...selectedQuizzes, quiz.id]);
                                } else {
                                  setSelectedQuizzes(selectedQuizzes.filter(id => id !== quiz.id));
                                }
                              }}
                              style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <h4 style={{ margin: '0 0 12px 0', color: '#22304a', fontSize: '16px', fontWeight: '700' }}>
                                {quiz.subject}
                              </h4>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.questionCountShort')} <strong>{quiz.question_count}</strong>
                              </p>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.createdAt')} {new Date(quiz.created_at).toLocaleDateString('tr-TR')}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(API_ENDPOINTS.GET_QUIZ(quiz.id));
                                      if (res.data.success) {
                                        setSelectedQuiz(res.data.quiz);
                                        setQuizResult({
                                          quiz_content: res.data.quiz.quiz_content,
                                          parsed_questions: res.data.quiz.parsed_questions,
                                          subject: res.data.quiz.subject
                                        });
                                        setCurrentView('quiz');
                                      }
                                    } catch (err) {
                                      setError('Kısa sınav yüklenirken hata oluştu');
                                    }
                                  }}
                                  className="btn btn-sm btn-primary"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.view')}
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`${quiz.subject} kısa sınavını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                      return;
                                    }
                                    try {
                                      setLoading(true);
                                      const res = await axios.delete(`${API_BASE_URL}/api/delete_quiz/${quiz.id}`);
                                      if (res.data.success) {
                                        await loadSavedItems();
                                        setSelectedQuizzes(selectedQuizzes.filter(id => id !== quiz.id));
                                        alert('Kısa sınav başarıyla silindi');
                                      } else {
                                        throw new Error(res.data.error || 'Silme başarısız');
                                      }
                                    } catch (err) {
                                      setError(err.response?.data?.error || err.message || 'Kısa sınav silinirken hata oluştu');
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                  className="btn btn-sm btn-danger"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.delete')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : selectedSavedType === 'midterm' ? (
                <div className="saved-section" inert={archiveReadOnly ? '' : undefined}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#22304a' }}>
                      {t('pretest.midtermsTitle')} ({savedMidterms.length})
                    </h3>
                    {savedMidterms.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            if (selectedMidterms.length === savedMidterms.length) {
                              setSelectedMidterms([]);
                            } else {
                              setSelectedMidterms(savedMidterms.map(m => m.id));
                            }
                          }}
                          className="btn btn-sm btn-secondary"
                        >
                          {selectedMidterms.length === savedMidterms.length ? t('pretest.deselectAll') : t('pretest.selectAll')}
                        </button>
                        {selectedMidterms.length > 0 && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`${selectedMidterms.length} ara sınavı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                return;
                              }
                              try {
                                setLoading(true);
                                const deletePromises = selectedMidterms.map(id =>
                                  axios.delete(`${API_BASE_URL}/api/delete_midterm/${id}`)
                                );
                                await Promise.all(deletePromises);
                                setSelectedMidterms([]);
                                await loadSavedItems();
                                alert(`${selectedMidterms.length} ara sınav başarıyla silindi`);
                              } catch (err) {
                                setError(err.response?.data?.error || err.message || 'Ara sınavlar silinirken hata oluştu');
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="btn btn-sm btn-danger"
                          >
                            {t('pretest.deleteSelected')} ({selectedMidterms.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {savedMidterms.length === 0 ? (
                    <p className="empty-message" style={{
                      padding: '20px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      {t('pretest.noMidtermsSaved')}
                    </p>
                  ) : (
                    <div className="items-list" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '16px'
                    }}>
                      {savedMidterms.map((midterm) => (
                        <div key={midterm.id} className="item-card" style={{
                          padding: '20px',
                          backgroundColor: selectedMidterms.includes(midterm.id) ? '#fef2f2' : 'white',
                          borderRadius: '12px',
                          border: selectedMidterms.includes(midterm.id) ? '2px solid #dc2626' : '1px solid #e5e7eb',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <input
                              type="checkbox"
                              checked={selectedMidterms.includes(midterm.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMidterms([...selectedMidterms, midterm.id]);
                                } else {
                                  setSelectedMidterms(selectedMidterms.filter(id => id !== midterm.id));
                                }
                              }}
                              style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <h4 style={{ margin: '0 0 12px 0', color: '#22304a', fontSize: '16px', fontWeight: '700' }}>
                                {midterm.subject}
                              </h4>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.questionCountShort')} <strong>{midterm.question_count}</strong>
                              </p>
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.createdAt')} {new Date(midterm.created_at).toLocaleDateString('tr-TR')}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(API_ENDPOINTS.GET_MIDTERM(midterm.id));
                                      if (res.data.success) {
                                        setSelectedMidterm(res.data.midterm);
                                        setMidtermResult({
                                          midterm_content: res.data.midterm.midterm_content,
                                          parsed_questions: res.data.midterm.parsed_questions,
                                          subject: res.data.midterm.subject,
                                          question_type: res.data.midterm.question_type || 'multiple_choice',
                                          multiple_choice_count: res.data.midterm.multiple_choice_count,
                                          classic_count: res.data.midterm.classic_count,
                                          classic_points: res.data.midterm.classic_points || 10
                                        });
                                        // State'leri de güncelle
                                        setMidtermQuestionType(res.data.midterm.question_type || 'multiple_choice');
                                        if (res.data.midterm.multiple_choice_count) {
                                          setMultipleChoiceCount(res.data.midterm.multiple_choice_count);
                                        }
                                        if (res.data.midterm.classic_count) {
                                          setClassicCount(res.data.midterm.classic_count);
                                        }
                                        if (res.data.midterm.classic_points) {
                                          setClassicPoints(res.data.midterm.classic_points);
                                        }
                                        setCurrentView('midterm');
                                      }
                                    } catch (err) {
                                      setError('Ara sınav yüklenirken hata oluştu');
                                    }
                                  }}
                                  className="btn btn-sm btn-primary"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.view')}
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`${midterm.subject} ara sınavını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                      return;
                                    }
                                    try {
                                      setLoading(true);
                                      const res = await axios.delete(`${API_BASE_URL}/api/delete_midterm/${midterm.id}`);
                                      if (res.data.success) {
                                        await loadSavedItems();
                                        setSelectedMidterms(selectedMidterms.filter(id => id !== midterm.id));
                                        alert('Ara sınav başarıyla silindi');
                                      } else {
                                        throw new Error(res.data.error || 'Silme başarısız');
                                      }
                                    } catch (err) {
                                      setError(err.response?.data?.error || err.message || 'Ara sınav silinirken hata oluştu');
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                  className="btn btn-sm btn-danger"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.delete')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : selectedSavedType === 'homework' ? (
                <div className="saved-section" inert={archiveReadOnly ? '' : undefined}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#22304a' }}>
                      {t('pretest.homeworksTitle')} ({savedHomeworks.length})
                    </h3>
                    {savedHomeworks.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            if (selectedHomeworks.length === savedHomeworks.length) {
                              setSelectedHomeworks([]);
                            } else {
                              setSelectedHomeworks(savedHomeworks.map(h => h.id));
                            }
                          }}
                          className="btn btn-sm btn-secondary"
                        >
                          {selectedHomeworks.length === savedHomeworks.length ? t('pretest.deselectAll') : t('pretest.selectAll')}
                        </button>
                        {selectedHomeworks.length > 0 && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`${selectedHomeworks.length} ödevi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                return;
                              }
                              try {
                                setLoading(true);
                                const deletePromises = selectedHomeworks.map(id =>
                                  axios.delete(`${API_BASE_URL}/api/delete_homework/${id}`)
                                );
                                await Promise.all(deletePromises);
                                setSelectedHomeworks([]);
                                await loadSavedItems();
                                alert(`${selectedHomeworks.length} ödev başarıyla silindi`);
                              } catch (err) {
                                setError(err.response?.data?.error || err.message || 'Ödevler silinirken hata oluştu');
                              } finally {
                                setLoading(false);
                              }
                            }}
                            className="btn btn-sm btn-danger"
                          >
                            {t('pretest.deleteSelected')} ({selectedHomeworks.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {savedHomeworks.length === 0 ? (
                    <p className="empty-message" style={{
                      padding: '20px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      {t('pretest.noHomeworksSaved')}
                    </p>
                  ) : (
                    <div className="items-list" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                      gap: '16px'
                    }}>
                      {savedHomeworks.map((homework) => (
                        <div key={homework.id} className="item-card" style={{
                          padding: '20px',
                          backgroundColor: selectedHomeworks.includes(homework.id) ? '#fef2f2' : 'white',
                          borderRadius: '12px',
                          border: selectedHomeworks.includes(homework.id) ? '2px solid #dc2626' : '1px solid #e5e7eb',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <input
                              type="checkbox"
                              checked={selectedHomeworks.includes(homework.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedHomeworks([...selectedHomeworks, homework.id]);
                                } else {
                                  setSelectedHomeworks(selectedHomeworks.filter(id => id !== homework.id));
                                }
                              }}
                              style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <h4 style={{ margin: '0 0 12px 0', color: '#22304a', fontSize: '16px', fontWeight: '700' }}>
                                {homework.subject}
                              </h4>
                              {homework.homework_title && (
                                <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                  Ödev Başlığı: <strong>{homework.homework_title}</strong>
                                </p>
                              )}
                              <p style={{ margin: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                                {t('pretest.createdAt')} {homework.created_at ? new Date(homework.created_at).toLocaleDateString('tr-TR') : '-'}
                              </p>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(API_ENDPOINTS.GET_HOMEWORK(homework.id));
                                      if (res.data.success) {
                                        setSelectedHomework(res.data.homework);
                                        setHomeworkResult({
                                          homework: res.data.homework.homework_content,
                                          homework_text: res.data.homework.homework_text,
                                          subject: res.data.homework.subject,
                                          ai_prompt: res.data.homework.ai_prompt,
                                          selected_weeks: res.data.homework.selected_weeks
                                        });
                                        setCurrentView('homework');
                                      }
                                    } catch (err) {
                                      setError('Ödev yüklenirken hata oluştu');
                                    }
                                  }}
                                  className="btn btn-sm btn-primary"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.view')}
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`${homework.subject} ödevini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
                                      return;
                                    }
                                    try {
                                      setLoading(true);
                                      const res = await axios.delete(`${API_BASE_URL}/api/delete_homework/${homework.id}`);
                                      if (res.data.success) {
                                        await loadSavedItems();
                                        setSelectedHomeworks(selectedHomeworks.filter(id => id !== homework.id));
                                        alert('Ödev başarıyla silindi');
                                      } else {
                                        throw new Error(res.data.error || 'Silme başarısız');
                                      }
                                    } catch (err) {
                                      setError(err.response?.data?.error || err.message || 'Ödev silinirken hata oluştu');
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                  className="btn btn-sm btn-danger"
                                  style={{ flex: 1 }}
                                >
                                  {t('pretest.delete')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="info-message" style={{
                  padding: '20px',
                  backgroundColor: '#f0f4ff',
                  borderRadius: '8px',
                  border: '1px solid #90caf9',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, color: '#1976d2', fontWeight: '500' }}>
                    {t('pretest.selectTypeFirst')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Öntest Oluşturma Formu */}
      {!pretestBlocked && currentView === 'pretest' && !result && (
        <div className="pretest-form" inert={archiveReadOnly ? '' : undefined}>
          <h3>{t('pretest.pretestTitle')}</h3>
          <p>{t('pretest.pretestSubtitle')}</p>

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group exam-course-field">
                <label htmlFor="courseSelect">{t('pretest.courseLabel')}</label>
                <ExamCourseDropdown
                  id="courseSelect"
                  value={selectedCourseId}
                  onChange={(value) => handleCourseSelect({ target: { value } })}
                  loading={loadingCourses}
                  courses={assignedCourses}
                  placeholder={t('pretest.selectCourse')}
                  loadingPlaceholder={t('pretest.loadingCourses')}
                  label={t('pretest.courseLabel')}
                />
                {courseError && (
                  <small className="form-error-text">{courseError}</small>
                )}
                {!courseError && !loadingCourses && assignedCourses.length === 0 && (
                  <small className="form-helper-text">
                    {t('pretest.noCourseAssigned')}
                  </small>
                )}
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    className={`english-toggle${generateInEnglish ? ' english-toggle--on' : ''}`}
                    onClick={() => setGenerateInEnglish(v => !v)}
                  >
                    <span className="english-toggle-box">{generateInEnglish ? '✓' : ''}</span>
                    {t('pretest.createInEnglish')}
                  </button>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="questionCount">{t('pretest.questionCount')}</label>
                <select
                  id="questionCount"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                >
                  {[5, 10].map(num => (
                    <option key={num} value={num}>{num} {t('pretest.questionCount')}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="optionCount">{t('pretest.optionCount')}</label>
                <select
                  id="optionCount"
                  value={optionCount}
                  onChange={(e) => setOptionCount(parseInt(e.target.value))}
                >
                  <option value={4}>{t('pretest.option4')}</option>
                  <option value={5}>{t('pretest.option5')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              {renderOutcomeMappingSection(
                t('pretest.pretestOutcomeContext')
              )}
            </div>

            <div className="form-group">
              <label htmlFor="pretestInstructions">{t('pretest.additionalInstructions')}</label>
              <textarea
                id="pretestInstructions"
                value={pretestInstructions}
                onChange={(e) => setPretestInstructions(e.target.value)}
                placeholder={t('pretest.pretestInstructionsPlaceholder')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  resize: 'vertical',
                  marginTop: '8px'
                }}
              />
              <small className="form-helper-text">
                {t('pretest.pretestInstructionsHelper')}
              </small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('pretest.creatingPretest') : t('pretest.pretestTitle')}
            </button>
          </form>
        </div>
      )}

      {/* Zorluk Seviyeli Sorular Formu */}
      {currentView === 'difficulty' && !questionsResult && (
        <div className="pretest-form" inert={archiveReadOnly ? '' : undefined}>
          <h3>{t('pretest.studyTopicsTitle')}</h3>
          <p>{t('pretest.studyTopicsSubtitle')}</p>

          <form onSubmit={handleDifficultySubmit}>
            <div className="form-row">
              <div className="form-group exam-course-field">
                <label htmlFor="difficultyCourseSelect">{t('pretest.courseLabel')}</label>
                <ExamCourseDropdown
                  id="difficultyCourseSelect"
                  value={selectedCourseId}
                  onChange={(value) => handleCourseSelect({ target: { value } })}
                  loading={loadingCourses}
                  courses={assignedCourses}
                  placeholder={t('pretest.selectCourse')}
                  loadingPlaceholder={t('pretest.loadingCourses')}
                  label={t('pretest.courseLabel')}
                />
                {loadingOutcomeMappings && (
                  <small className="form-helper-text">
                    Dersin öğrenme çıktıları eşleştirmeleri kontrol ediliyor...
                  </small>
                )}
                {outcomeMappingsError && (
                  <small className="form-error-text">{outcomeMappingsError}</small>
                )}
                {!loadingOutcomeMappings && !outcomeMappingsError && outcomeMappings.length > 0 && (
                  <small className="form-helper-text">
                    Bu ders {outcomeMappings.length} Dersin Öğrenme Çıktısı ile eşleştirilmiş.
                  </small>
                )}
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    className={`english-toggle${generateInEnglish ? ' english-toggle--on' : ''}`}
                    onClick={() => setGenerateInEnglish(v => !v)}
                  >
                    <span className="english-toggle-box">{generateInEnglish ? '✓' : ''}</span>
                    {t('pretest.createInEnglish')}
                  </button>
                </div>
              </div>
            </div>

            {/* Haftalık Konu Seçimi */}
            {selectedCourseId && weeklyTopics.length > 0 && (
              <div className="form-group weekly-topics-selector" style={{ marginBottom: '24px' }}>
                <label htmlFor="weeklyTopicsSelect" style={{ marginBottom: '12px', display: 'block', fontWeight: '700', fontSize: '15px', color: '#22304a' }}>
                  Haftalık Konu Seç *
                </label>
                <div className="weekly-topics-grid" style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#f8fafc',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                }}>
                  {loadingWeeklyTopics ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p>{t('pretest.weeklyTopicsLoading')}</p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '12px'
                    }}>
                      {weeklyTopics.map((topic) => {
                        const isSelected = selectedWeeklyTopics.includes(topic.week);
                        const hasNotes = !!topic.ders_notlari;
                        return (
                          <label
                            key={topic.week}
                            className="weekly-topic-item"
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              cursor: 'pointer',
                              padding: '12px',
                              borderRadius: '8px',
                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                              border: isSelected ? '2px solid #2196f3' : '1px solid #ddd',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = '#f0f4ff';
                                e.currentTarget.style.borderColor = '#90caf9';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.borderColor = '#ddd';
                              }
                            }}
                          >
                            <div style={{
                              position: 'relative',
                              marginRight: '12px',
                              marginTop: '2px',
                              flexShrink: 0
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newChecked = e.target.checked;
                                  handleWeeklyTopicChange(topic.week, newChecked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  opacity: 0,
                                  cursor: 'pointer',
                                  width: '20px',
                                  height: '20px',
                                  margin: 0,
                                  zIndex: 1
                                }}
                              />
                              <div style={{
                                width: '20px',
                                height: '20px',
                                border: isSelected ? '2px solid #2196f3' : '2px solid #9ca3af',
                                borderRadius: '4px',
                                backgroundColor: isSelected ? '#2196f3' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                boxShadow: isSelected ? '0 2px 4px rgba(33, 150, 243, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)'
                              }}>
                                {isSelected && (
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                      stroke: 'white',
                                      strokeWidth: '3',
                                      strokeLinecap: 'round',
                                      strokeLinejoin: 'round'
                                    }}
                                  >
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                )}
                              </div>
                            </div>
                            <span style={{ fontSize: '14px', lineHeight: '1.5', flex: 1 }}>
                              <strong style={{ color: isSelected ? '#1976d2' : '#22304a' }}>
                                Hafta {topic.week}:
                              </strong>{' '}
                              <span style={{ color: '#374151' }}>
                                {topic.topic || 'Konu yok'}
                              </span>
                              {hasNotes && (
                                <span style={{
                                  color: '#10b981',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu mevcut">
                                  📋
                                </span>
                              )}
                              {!hasNotes && (
                                <span style={{
                                  color: '#9ca3af',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu yok">
                                  📋
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <small className="form-helper-text" style={{ marginTop: '8px', display: 'block' }}>
                  Seçtiğiniz haftaların ders notları otomatik olarak dosya listesine eklenecektir.
                  {selectedWeeklyTopics.length > 0 && (
                    <strong style={{ color: '#2196f3', marginLeft: '4px' }}>
                      ({selectedWeeklyTopics.length} hafta seçildi)
                    </strong>
                  )}
                </small>
              </div>
            )}

            {selectedCourseId && weeklyTopics.length === 0 && !loadingWeeklyTopics && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <small className="form-helper-text" style={{ color: '#999' }}>
                  Bu ders için haftalık konu bulunamadı. Manuel olarak dosya yükleyebilirsiniz.
                </small>
              </div>
            )}

            <div className="file-upload-section">
              <label>{t('pretest.learningOutcomeSources')}</label>
              {renderOutcomeMappingSection(
                'Eşleştirilmiş {count} Dersin Öğrenme Çıktısı bağlama eklendi. Dilerseniz ek ders notu dosyaları yükleyebilirsiniz.'
              )}
              <div
                className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="drop-zone-content">
                  <p>{t('pretest.dropLectureNotes')}</p>
                  <p>{t('pretest.supportedFormats')}</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".docx,.pdf"
                onChange={(e) => handleFileSelect(e.target.files)}
                style={{ display: 'none' }}
              />

              {allSelectedFiles.length > 0 && (
                <div className="selected-files">
                  <h4>{t('pretest.selectedFiles')}</h4>
                  {allSelectedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <span>{file.name}</span>
                      <button type="button" onClick={() => removeFile(index)} className="remove-file">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="studyTopicsInstructions">{t('pretest.additionalInstructions')}</label>
              <textarea
                id="studyTopicsInstructions"
                value={studyTopicsInstructions}
                onChange={(e) => setStudyTopicsInstructions(e.target.value)}
                placeholder={t('pretest.studyTopicsInstructionsPlaceholder')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  resize: 'vertical',
                  marginTop: '8px'
                }}
              />
              <small className="form-helper-text">
                {t('pretest.studyTopicsInstructionsHelper')}
              </small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={creatingQuestions}>
              {creatingQuestions ? 'Çalışma Konulu Sorular Oluşturuluyor...' : 'Çalışma Konulu Sorular Oluştur'}
            </button>
          </form>
        </div>
      )}

      {/* Zorluk Seviyeli Sorular Sonuçları */}
      {currentView === 'difficulty' && questionsResult && displayQuestionsResult()}

      {/* Kısa Sınav Oluşturma Formu */}
      {currentView === 'quiz' && !quizResult && (
        <div className="pretest-form" inert={archiveReadOnly ? '' : undefined}>
          <h3>{t('pretest.quizTitle')}</h3>
          <p>{t('pretest.quizSubtitle')}</p>

          <form onSubmit={(e) => { e.preventDefault(); handleCreateQuiz(); }}>
            <div className="form-row">
              <div className="form-group exam-course-field">
                <label htmlFor="quizCourseSelect">{t('pretest.courseLabel')}</label>
                <ExamCourseDropdown
                  id="quizCourseSelect"
                  value={selectedCourseId}
                  onChange={(value) => handleCourseSelect({ target: { value } })}
                  loading={loadingCourses}
                  courses={assignedCourses}
                  placeholder={t('pretest.selectCourse')}
                  loadingPlaceholder={t('pretest.loadingCourses')}
                  label={t('pretest.courseLabel')}
                />
                {loadingOutcomeMappings && (
                  <small className="form-helper-text">
                    Dersin öğrenme çıktıları eşleştirmeleri kontrol ediliyor...
                  </small>
                )}
                {outcomeMappingsError && (
                  <small className="form-error-text">{outcomeMappingsError}</small>
                )}
                {!loadingOutcomeMappings && !outcomeMappingsError && outcomeMappings.length > 0 && (
                  <small className="form-helper-text">
                    Bu ders {outcomeMappings.length} Dersin Öğrenme Çıktısı ile eşleştirilmiş.
                  </small>
                )}
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    className={`english-toggle${generateInEnglish ? ' english-toggle--on' : ''}`}
                    onClick={() => setGenerateInEnglish(v => !v)}
                  >
                    <span className="english-toggle-box">{generateInEnglish ? '✓' : ''}</span>
                    {t('pretest.createInEnglish')}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="quiz-question-count">{t('pretest.questionCount')}</label>
                <select
                  id="quiz-question-count"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                >
                  {[5, 10].map(num => (
                    <option key={num} value={num}>{num} {t('pretest.questionCount')}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="quiz-option-count">{t('pretest.optionCount')}</label>
                <select
                  id="quiz-option-count"
                  value={optionCount}
                  onChange={(e) => setOptionCount(parseInt(e.target.value))}
                >
                  {[4, 5].map(num => (
                    <option key={num} value={num}>{num} {t('pretest.optionCount')}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Haftalık Konu Seçimi */}
            {selectedCourseId && weeklyTopics.length > 0 && (
              <div className="form-group weekly-topics-selector" style={{ marginBottom: '24px' }}>
                <label htmlFor="quizWeeklyTopicsSelect" style={{ marginBottom: '12px', display: 'block', fontWeight: '700', fontSize: '15px', color: '#22304a' }}>
                  Haftalık Konu Seç *
                </label>
                <div className="weekly-topics-grid" style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#f8fafc',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                }}>
                  {loadingWeeklyTopics ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p>{t('pretest.weeklyTopicsLoading')}</p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '12px'
                    }}>
                      {weeklyTopics.map((topic) => {
                        const isSelected = selectedWeeklyTopics.includes(topic.week);
                        const hasNotes = !!topic.ders_notlari;
                        return (
                          <label
                            key={topic.week}
                            className="weekly-topic-item"
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              cursor: 'pointer',
                              padding: '12px',
                              borderRadius: '8px',
                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                              border: isSelected ? '2px solid #2196f3' : '1px solid #ddd',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = '#f0f4ff';
                                e.currentTarget.style.borderColor = '#90caf9';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.borderColor = '#ddd';
                              }
                            }}
                          >
                            <div style={{
                              position: 'relative',
                              marginRight: '12px',
                              marginTop: '2px',
                              flexShrink: 0
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newChecked = e.target.checked;
                                  handleWeeklyTopicChange(topic.week, newChecked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  opacity: 0,
                                  cursor: 'pointer',
                                  width: '20px',
                                  height: '20px',
                                  margin: 0,
                                  zIndex: 1
                                }}
                              />
                              <div style={{
                                width: '20px',
                                height: '20px',
                                border: isSelected ? '2px solid #2196f3' : '2px solid #9ca3af',
                                borderRadius: '4px',
                                backgroundColor: isSelected ? '#2196f3' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                boxShadow: isSelected ? '0 2px 4px rgba(33, 150, 243, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)'
                              }}>
                                {isSelected && (
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                      stroke: 'white',
                                      strokeWidth: '3',
                                      strokeLinecap: 'round',
                                      strokeLinejoin: 'round'
                                    }}
                                  >
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                )}
                              </div>
                            </div>
                            <span style={{ fontSize: '14px', lineHeight: '1.5', flex: 1 }}>
                              <strong style={{ color: isSelected ? '#1976d2' : '#22304a' }}>
                                Hafta {topic.week}:
                              </strong>{' '}
                              <span style={{ color: '#374151' }}>
                                {topic.topic || 'Konu yok'}
                              </span>
                              {hasNotes && (
                                <span style={{
                                  color: '#10b981',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu mevcut">
                                  📋
                                </span>
                              )}
                              {!hasNotes && (
                                <span style={{
                                  color: '#9ca3af',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu yok">
                                  📋
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <small className="form-helper-text" style={{ marginTop: '8px', display: 'block' }}>
                  Seçtiğiniz haftaların ders notları otomatik olarak dosya listesine eklenecektir.
                  {selectedWeeklyTopics.length > 0 && (
                    <strong style={{ color: '#2196f3', marginLeft: '4px' }}>
                      ({selectedWeeklyTopics.length} hafta seçildi)
                    </strong>
                  )}
                </small>
              </div>
            )}

            {selectedCourseId && weeklyTopics.length === 0 && !loadingWeeklyTopics && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <small className="form-helper-text" style={{ color: '#999' }}>
                  Bu ders için haftalık konu bulunamadı. Manuel olarak dosya yükleyebilirsiniz.
                </small>
              </div>
            )}

            <div className="file-upload-section">
              <label>{t('pretest.learningOutcomeSources')}</label>
              {renderOutcomeMappingSection(
                'Toplam {count} Dersin Öğrenme Çıktısı bağlama eklendi. Kısa sınav için ek ders notu dosyaları yükleyebilirsiniz.'
              )}
              <div
                className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="drop-zone-content">
                  <p>{t('pretest.dropLectureNotes')}</p>
                  <p>{t('pretest.supportedFormats')}</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".docx,.pdf"
                onChange={(e) => handleFileSelect(e.target.files)}
                style={{ display: 'none' }}
              />

              {allSelectedFiles.length > 0 && (
                <div className="selected-files">
                  <h4>{t('pretest.selectedFiles')}</h4>
                  {allSelectedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <span>{file.name}</span>
                      <button type="button" onClick={() => removeFile(index)} className="remove-file">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="quizInstructions">{t('pretest.additionalInstructions')}</label>
              <textarea
                id="quizInstructions"
                value={quizInstructions}
                onChange={(e) => setQuizInstructions(e.target.value)}
                placeholder={t('pretest.quizInstructionsPlaceholder')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  resize: 'vertical',
                  marginTop: '8px'
                }}
              />
              <small className="form-helper-text">
                {t('pretest.quizInstructionsHelper')}
              </small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={creatingQuiz}>
              {creatingQuiz ? t('pretest.creatingQuiz') : t('pretest.quizTitle')}
            </button>
          </form>
        </div>
      )}

      {/* Sınav Oluşturma Formu */}
      {currentView === 'midterm' && !midtermResult && (
        <div className="pretest-form" inert={archiveReadOnly ? '' : undefined}>
          <h3>Sınav Oluştur</h3>
          <p>Seçtiğiniz haftalara ve sınav türüne göre sorular oluşturulur</p>

          <form onSubmit={(e) => { e.preventDefault(); handleCreateMidterm(); }}>
            <div className="form-row">
              <div className="form-group exam-course-field">
                <label htmlFor="midtermCourseSelect">{t('pretest.courseLabel')}</label>
                <ExamCourseDropdown
                  id="midtermCourseSelect"
                  value={selectedCourseId}
                  onChange={(value) => handleCourseSelect({ target: { value } })}
                  loading={loadingCourses}
                  courses={assignedCourses}
                  placeholder={t('pretest.selectCourse')}
                  loadingPlaceholder={t('pretest.loadingCourses')}
                  label={t('pretest.courseLabel')}
                />
                {loadingOutcomeMappings && (
                  <small className="form-helper-text">
                    Dersin öğrenme çıktıları eşleştirmeleri kontrol ediliyor...
                  </small>
                )}
                {outcomeMappingsError && (
                  <small className="form-error-text">{outcomeMappingsError}</small>
                )}
                {!loadingOutcomeMappings && !outcomeMappingsError && outcomeMappings.length > 0 && (
                  <small className="form-helper-text">
                    Bu ders {outcomeMappings.length} Dersin Öğrenme Çıktısı ile eşleştirilmiş.
                  </small>
                )}
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    className={`english-toggle${generateInEnglish ? ' english-toggle--on' : ''}`}
                    onClick={() => setGenerateInEnglish(v => !v)}
                  >
                    <span className="english-toggle-box">{generateInEnglish ? '✓' : ''}</span>
                    {t('pretest.createInEnglish')}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="midterm-question-count">{t('pretest.questionCount')}</label>
                <select
                  id="midterm-question-count"
                  value={questionCount}
                  onChange={(e) => {
                    const total = parseInt(e.target.value) || 0;
                    setQuestionCount(total);
                    // Karışık modda soru sayısı değiştiğinde oranları güncelle
                    if (total > 1 && midtermQuestionType === 'mixed') {
                      const mc = Math.floor(total * 0.6);
                      const cl = total - mc;
                      setMultipleChoiceCount(mc);
                      setClassicCount(cl);
                    }
                  }}
                >
                  {[5, 10].map(num => (
                    <option key={num} value={num}>{num} {t('pretest.questionCount')}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="midterm-option-count">{t('pretest.optionCount')}</label>
                <select
                  id="midterm-option-count"
                  value={optionCount}
                  onChange={(e) => setOptionCount(parseInt(e.target.value))}
                  disabled={midtermQuestionType === 'classic'}
                >
                  {[4, 5].map(num => (
                    <option key={num} value={num}>{num} {t('pretest.optionCount')}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="midterm-question-type">{t('pretest.questionType')}</label>
                <select
                  id="midterm-question-type"
                  value={midtermQuestionType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setMidtermQuestionType(newType);
                    // Karışık mod için varsayılan değerleri ayarla
                    if (newType === 'mixed') {
                      const total = questionCount;
                      if (total > 1) {
                        const mc = Math.floor(total * 0.6);
                        const cl = total - mc;
                        setMultipleChoiceCount(mc);
                        setClassicCount(cl);
                      }
                    }
                  }}
                >
                  <option value="multiple_choice">{t('pretest.multipleChoice')}</option>
                  <option value="classic">Klasik</option>
                  <option value="mixed">{t('pretest.mixed')}</option>
                </select>
              </div>
            </div>

            {/* Karışık Mod Oran Ayarları */}
            {midtermQuestionType === 'mixed' && (
              <div className="form-row" style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div className="form-group">
                  <label htmlFor="multiple-choice-count">{t('pretest.multipleChoiceCount')}</label>
                  <input
                    type="number"
                    id="multiple-choice-count"
                    min="1"
                    max={questionCount - 1}
                    value={multipleChoiceCount}
                    onChange={(e) => {
                      const mcCount = parseInt(e.target.value) || 0;
                      const remaining = questionCount - mcCount;
                      if (mcCount > 0 && remaining > 0) {
                        setMultipleChoiceCount(mcCount);
                        setClassicCount(remaining);
                      }
                    }}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="classic-count">{t('pretest.classicCount')}</label>
                  <input
                    type="number"
                    id="classic-count"
                    min="1"
                    max={questionCount - 1}
                    value={classicCount}
                    onChange={(e) => {
                      const cCount = parseInt(e.target.value) || 0;
                      const remaining = questionCount - cCount;
                      if (cCount > 0 && remaining > 0) {
                        setClassicCount(cCount);
                        setMultipleChoiceCount(remaining);
                      }
                    }}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '8px' }}>
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Toplam: {multipleChoiceCount + classicCount} / {questionCount}
                    {multipleChoiceCount + classicCount !== questionCount && (
                      <span style={{ color: '#dc2626', marginLeft: '8px' }}>
                        (Eksik: {questionCount - (multipleChoiceCount + classicCount)})
                      </span>
                    )}
                  </small>
                </div>
              </div>
            )}

            {/* Klasik Soru Ayarları */}
            {(midtermQuestionType === 'classic' || midtermQuestionType === 'mixed') && (
              <div className="form-row classic-settings-row">
                {/* Klasik Soru Puanı - Sadece karışık mod için göster */}
                {midtermQuestionType === 'mixed' && (
                  <div className="form-group">
                    <label htmlFor="classic-points">{t('pretest.classicPoints')}</label>
                    <input
                      type="number"
                      id="classic-points"
                      min="1"
                      value={classicPoints}
                      onChange={(e) => setClassicPoints(parseInt(e.target.value) || 10)}
                    />
                  </div>
                )}
                {/* Klasik mod için otomatik hesaplanan puan bilgisi */}
                {midtermQuestionType === 'classic' && (
                  <div className="form-group">
                    <label style={{ color: '#6b7280', fontSize: '0.9rem' }}>{t('pretest.classicPoints')}</label>
                    <div style={{
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      backgroundColor: '#f9fafb',
                      color: '#6b7280',
                      height: '48px',
                      boxSizing: 'border-box',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {classicPoints} puan (Otomatik: 100 / {questionCount})
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="include-sample-answer">{t('pretest.sampleAnswer')}</label>
                  <div
                    onClick={() => setIncludeSampleAnswer(!includeSampleAnswer)}
                    onMouseEnter={(e) => {
                      if (!includeSampleAnswer) {
                        e.currentTarget.style.borderColor = '#3498db';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(52, 152, 219, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!includeSampleAnswer) {
                        e.currentTarget.style.borderColor = '#023b58';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: 'white',
                      border: '2px solid #023b58',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      userSelect: 'none',
                      fontSize: '1rem',
                      minHeight: '48px',
                      boxSizing: 'border-box',
                      width: '100%'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1', minWidth: 0 }}>
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          border: `2px solid ${includeSampleAnswer ? '#023b58' : '#d1d5db'}`,
                          backgroundColor: includeSampleAnswer ? '#023b58' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        {includeSampleAnswer && (
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.5 4.5L6 12L2.5 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span style={{
                        fontSize: '1rem',
                        fontWeight: '400',
                        color: '#22304a',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        Örnek cevap/açıklama ekle
                      </span>
                    </div>
                    <span style={{
                      fontSize: '0.9rem',
                      color: includeSampleAnswer ? '#023b58' : '#6b7280',
                      fontWeight: '500',
                      flexShrink: 0,
                      marginLeft: '8px'
                    }}>
                      {includeSampleAnswer ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                  <input
                    id="include-sample-answer"
                    type="checkbox"
                    checked={includeSampleAnswer}
                    onChange={(e) => setIncludeSampleAnswer(e.target.checked)}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            )}

            {/* Haftalık Konu Seçimi */}
            {selectedCourseId && weeklyTopics.length > 0 && (
              <div className="form-group weekly-topics-selector" style={{ marginBottom: '24px' }}>
                <label htmlFor="midtermWeeklyTopicsSelect" style={{ marginBottom: '4px', display: 'block', fontWeight: '700', fontSize: '15px', color: '#22304a' }}>
                  Haftalık Konu Seç *
                </label>
                <small style={{ color: '#6b7280', display: 'block', marginBottom: '12px', fontSize: '13px' }}>
                  Soru oluşturulacak haftalara tıklayarak seçin
                </small>
                <div className="weekly-topics-grid" style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#f8fafc',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                }}>
                  {loadingWeeklyTopics ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p>{t('pretest.weeklyTopicsLoading')}</p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '12px'
                    }}>
                      {weeklyTopics.map((topic) => {
                        const isSelected = selectedWeeklyTopics.includes(topic.week);
                        const hasNotes = !!topic.ders_notlari;
                        return (
                          <label
                            key={topic.week}
                            className="weekly-topic-item"
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              cursor: 'pointer',
                              padding: '12px',
                              borderRadius: '8px',
                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                              border: isSelected ? '2px solid #2196f3' : '1px solid #ddd',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = '#f0f4ff';
                                e.currentTarget.style.borderColor = '#90caf9';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.borderColor = '#ddd';
                              }
                            }}
                          >
                            <div style={{
                              position: 'relative',
                              marginRight: '12px',
                              marginTop: '2px',
                              flexShrink: 0
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newChecked = e.target.checked;
                                  handleWeeklyTopicChange(topic.week, newChecked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  opacity: 0,
                                  cursor: 'pointer',
                                  width: '20px',
                                  height: '20px',
                                  margin: 0,
                                  zIndex: 1
                                }}
                              />
                              <div style={{
                                width: '20px',
                                height: '20px',
                                border: isSelected ? '2px solid #2196f3' : '2px solid #9ca3af',
                                borderRadius: '4px',
                                backgroundColor: isSelected ? '#2196f3' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                boxShadow: isSelected ? '0 2px 4px rgba(33, 150, 243, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)'
                              }}>
                                {isSelected && (
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                      stroke: 'white',
                                      strokeWidth: '3',
                                      strokeLinecap: 'round',
                                      strokeLinejoin: 'round'
                                    }}
                                  >
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                )}
                              </div>
                            </div>
                            <span style={{ fontSize: '14px', lineHeight: '1.5', flex: 1 }}>
                              <strong style={{ color: isSelected ? '#1976d2' : '#22304a' }}>
                                Hafta {topic.week}:
                              </strong>{' '}
                              <span style={{ color: '#374151' }}>
                                {topic.topic || 'Konu yok'}
                              </span>
                              {hasNotes && (
                                <span style={{
                                  color: '#10b981',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu mevcut">
                                  📋
                                </span>
                              )}
                              {!hasNotes && (
                                <span style={{
                                  color: '#9ca3af',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu yok">
                                  📋
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <small className="form-helper-text" style={{ marginTop: '8px', display: 'block' }}>
                  Seçtiğiniz haftaların ders notları otomatik olarak dosya listesine eklenecektir.
                  {selectedWeeklyTopics.length > 0 && (
                    <strong style={{ color: '#2196f3', marginLeft: '4px' }}>
                      ({selectedWeeklyTopics.length} hafta seçildi)
                    </strong>
                  )}
                </small>
              </div>
            )}

            {selectedCourseId && weeklyTopics.length === 0 && !loadingWeeklyTopics && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <small className="form-helper-text" style={{ color: '#999' }}>
                  Bu ders için haftalık konu bulunamadı. Manuel olarak dosya yükleyebilirsiniz.
                </small>
              </div>
            )}

            <div className="file-upload-section">
              <label>{t('pretest.learningOutcomeSources')}</label>
              {renderOutcomeMappingSection(
                'Toplam {count} Dersin Öğrenme Çıktısı bağlama eklendi. Sınav için ek ders notu dosyaları yükleyebilirsiniz.'
              )}
              <div
                className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="drop-zone-content">
                  <p>{t('pretest.dropLectureNotes')}</p>
                  <p>{t('pretest.supportedFormats')}</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".docx,.pdf"
                onChange={(e) => handleFileSelect(e.target.files)}
                style={{ display: 'none' }}
              />

              {allSelectedFiles.length > 0 && (
                <div className="selected-files">
                  <h4>{t('pretest.selectedFiles')}</h4>
                  {allSelectedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <span>{file.name}</span>
                      <button type="button" onClick={() => removeFile(index)} className="remove-file">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="midtermInstructions">{t('pretest.additionalInstructions')}</label>
              <textarea
                id="midtermInstructions"
                value={midtermInstructions}
                onChange={(e) => setMidtermInstructions(e.target.value)}
                placeholder={t('pretest.midtermInstructionsPlaceholder')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  resize: 'vertical',
                  marginTop: '8px'
                }}
              />
              <small className="form-helper-text">
                {t('pretest.midtermInstructionsHelper')}
              </small>
            </div>

            <button type="submit" className="btn btn-primary" disabled={creatingMidterm}>
              {creatingMidterm ? t('pretest.creatingMidterm') : t('pretest.midtermTitle')}
            </button>
          </form>
        </div>
      )}

      {/* Ödev Oluşturma Formu */}
      {currentView === 'homework' && !homeworkResult && (
        <div className="pretest-form" inert={archiveReadOnly ? '' : undefined}>
          <h3>{t('pretest.homeworkTitle')}</h3>
          <p>{t('pretest.homeworkSubtitle')}</p>

          <form onSubmit={(e) => { e.preventDefault(); handleCreateHomework(); }}>
            <div className="form-row">
              <div className="form-group exam-course-field">
                <label htmlFor="homeworkCourseSelect">{t('pretest.courseLabel')}</label>
                <ExamCourseDropdown
                  id="homeworkCourseSelect"
                  value={selectedCourseId}
                  onChange={(value) => handleCourseSelect({ target: { value } })}
                  loading={loadingCourses}
                  courses={assignedCourses}
                  placeholder={t('pretest.selectCourse')}
                  loadingPlaceholder={t('pretest.loadingCourses')}
                  label={t('pretest.courseLabel')}
                />
                {loadingOutcomeMappings && (
                  <small className="form-helper-text">
                    Dersin öğrenme çıktıları eşleştirmeleri kontrol ediliyor...
                  </small>
                )}
                {outcomeMappingsError && (
                  <small className="form-error-text">{outcomeMappingsError}</small>
                )}
                {!loadingOutcomeMappings && !outcomeMappingsError && outcomeMappings.length > 0 && (
                  <small className="form-helper-text">
                    Bu ders {outcomeMappings.length} Dersin Öğrenme Çıktısı ile eşleştirilmiş.
                  </small>
                )}
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    className={`english-toggle${generateInEnglish ? ' english-toggle--on' : ''}`}
                    onClick={() => setGenerateInEnglish(v => !v)}
                  >
                    <span className="english-toggle-box">{generateInEnglish ? '✓' : ''}</span>
                    {t('pretest.createInEnglish')}
                  </button>
                </div>
              </div>
            </div>

            {/* Haftalık Konu Seçimi */}
            {selectedCourseId && weeklyTopics.length > 0 && (
              <div className="form-group weekly-topics-selector" style={{ marginBottom: '24px' }}>
                <label htmlFor="homeworkWeeklyTopicsSelect" style={{ marginBottom: '12px', display: 'block', fontWeight: '700', fontSize: '15px', color: '#22304a' }}>
                  Ödeve Dahil Edilecek Haftalar *
                </label>
                <div className="weekly-topics-grid" style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#f8fafc',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
                }}>
                  {loadingWeeklyTopics ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p>{t('pretest.weeklyTopicsLoading')}</p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '12px'
                    }}>
                      {weeklyTopics.map((topic) => {
                        const isSelected = selectedWeeklyTopics.includes(topic.week);
                        const hasNotes = !!topic.ders_notlari;
                        return (
                          <label
                            key={topic.week}
                            className="weekly-topic-item"
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              cursor: 'pointer',
                              padding: '12px',
                              borderRadius: '8px',
                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                              border: isSelected ? '2px solid #2196f3' : '1px solid #ddd',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = '#f0f4ff';
                                e.currentTarget.style.borderColor = '#90caf9';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.borderColor = '#ddd';
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newChecked = e.target.checked;
                                handleWeeklyTopicChange(topic.week, newChecked);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                marginRight: '10px',
                                marginTop: '2px',
                                cursor: 'pointer',
                                width: '18px',
                                height: '18px',
                                flexShrink: 0
                              }}
                            />
                            <span style={{ fontSize: '14px', lineHeight: '1.5', flex: 1 }}>
                              <strong style={{ color: isSelected ? '#1976d2' : '#22304a' }}>
                                Hafta {topic.week}:
                              </strong>{' '}
                              <span style={{ color: '#374151' }}>
                                {topic.topic || 'Konu yok'}
                              </span>
                              {hasNotes && (
                                <span style={{
                                  color: '#10b981',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu mevcut">
                                  📋
                                </span>
                              )}
                              {!hasNotes && (
                                <span style={{
                                  color: '#9ca3af',
                                  marginLeft: '6px',
                                  fontSize: '16px'
                                }} title="Ders notu yok">
                                  📋
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <small className="form-helper-text" style={{ marginTop: '8px', display: 'block' }}>
                  Seçtiğiniz haftaların ders notları otomatik olarak dosya listesine eklenecektir.
                  {selectedWeeklyTopics.length > 0 && (
                    <strong style={{ color: '#2196f3', marginLeft: '4px' }}>
                      ({selectedWeeklyTopics.length} hafta seçildi)
                    </strong>
                  )}
                </small>
              </div>
            )}

            {selectedCourseId && weeklyTopics.length === 0 && !loadingWeeklyTopics && (
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <small className="form-helper-text" style={{ color: '#999' }}>
                  Bu ders için haftalık konu bulunamadı. Manuel olarak dosya yükleyebilirsiniz.
                </small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="homeworkInstructions">{t('pretest.additionalInstructions')}</label>
              <textarea
                id="homeworkInstructions"
                value={homeworkInstructions}
                onChange={(e) => setHomeworkInstructions(e.target.value)}
                placeholder={t('pretest.homeworkInstructionsPlaceholder')}
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
              <small className="form-helper-text">
                {t('pretest.homeworkInstructionsHelper')}
              </small>
            </div>

            <div className="file-upload-section">
              <label>{t('pretest.learningOutcomeSources')}</label>
              {renderOutcomeMappingSection(
                'Bu ders için {count} Dersin Öğrenme Çıktısı eşleştirildi ve ödev bağlamına eklendi. Gerekiyorsa ek referans dosyaları yükleyebilirsiniz.'
              )}
              <div
                className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="drop-zone-content">
                  <p>{t('pretest.dropHomeworkFiles')}</p>
                  <p>{t('pretest.supportedFormats')}</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".docx,.pdf"
                onChange={(e) => handleFileSelect(e.target.files)}
                style={{ display: 'none' }}
              />

              {allSelectedFiles.length > 0 && (
                <div className="selected-files">
                  <h4>{t('pretest.selectedFiles')}</h4>
                  {allSelectedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      <span>{file.name}</span>
                      <button type="button" onClick={() => removeFile(index)} className="remove-file">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" disabled={creatingHomework}>
              {creatingHomework ? t('pretest.creatingHomework') : t('pretest.homeworkTitle')}
            </button>
          </form>
        </div>
      )}

      {/* Öntest Sonuç Ekranı */}
      {!pretestBlocked && currentView === 'pretest' && result && (() => {
        // Önce previewQuestions, sonra parsed_questions, en son parseTestContent
        let questions = [];
        if (previewQuestions.length > 0) {
          questions = previewQuestions;
        } else if (result.parsed_questions && Array.isArray(result.parsed_questions) && result.parsed_questions.length > 0) {
          // Backend'den gelen parsed_questions'ı kullan
          questions = result.parsed_questions.map((q, idx) => {
            // Backend formatı: A, B, C, D, E ayrı alanlar olarak geliyor
            const opts = [];
            if (q.A) opts.push(`A) ${q.A}`);
            if (q.B) opts.push(`B) ${q.B}`);
            if (q.C) opts.push(`C) ${q.C}`);
            if (q.D) opts.push(`D) ${q.D}`);
            if (q.E) opts.push(`E) ${q.E}`);
            // Eğer options array olarak geldiyse onu kullan
            const finalOptions = opts.length > 0 ? opts : (q.options || []);
            return {
              number: idx + 1,
              question: q.question || q.text || '',
              options: finalOptions,
              answer: q.answer || q.correct_answer || ''
            };
          });
        } else {
          questions = parseTestContent(result.test_content);
        }

        return (
          <div className="pretest-results">
            <div className="success-message">
              <h2>{t('pretest.pretestCreated')}</h2>
              <p><strong>{t('pretest.subjectLabel')}</strong> {result.subject}</p>
              <p><strong>{t('pretest.questionCountLabel')}</strong> {questions.length}</p>
              <p><strong>{t('pretest.processedFiles')}</strong> {result.processed_files?.join(', ')}</p>
            </div>

            <div className="test-preview">
              <h3>{t('pretest.testPreview')}</h3>
              <div className="questions-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {questions.map((q, index) => (
                  <div key={q.number} className="preview-card" style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: '4px solid #023b58'
                  }}>
                    <div className="question-header" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                      <span className="question-chip" style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#023b58',
                        color: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '14px',
                        flexShrink: 0
                      }}>{q.number}</span>
                      <div className="question-text" style={{
                        flex: 1,
                        color: '#111827',
                        fontWeight: '600',
                        lineHeight: '1.6',
                        fontSize: '15px'
                      }}><MathText>{q.question}</MathText></div>
                    </div>
                    {q.options && q.options.length > 0 && (
                      <ul className="option-list" style={{
                        listStyle: 'none',
                        padding: '0',
                        margin: '12px 0 0 44px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {q.options.map((opt, idx) => (
                          <li key={idx} style={{
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            color: '#374151',
                            fontWeight: '500',
                            fontSize: '14px'
                          }}><MathText>{opt}</MathText></li>
                        ))}
                      </ul>
                    )}
                    {(() => {
                      const match = quizQuestionMatches.find((item) => item.question_index === index);
                      if (!match) return <div style={{ margin: '14px 0 0 44px', color: '#64748b', fontSize: '13px' }}>ÖÇ/PÇ eşleştirmesi hesaplanıyor…</div>;
                      const outcomes = match.learning_outcomes || [];
                      const programOutcomes = match.program_outcomes || [];
                      return <div style={{ margin: '14px 0 0 44px', padding: '12px', border: '1px solid #bfdbfe', borderRadius: '8px', background: '#f8fbff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '9px' }}>
                          <strong style={{ color: '#1e3a5f', fontSize: '13px' }}>ÖÇ / PÇ eşleştirmesi</strong>
                          <span style={{ color: outcomes.length && programOutcomes.length ? '#15803d' : '#b45309', fontSize: '12px', fontWeight: 700 }}>{outcomes.length && programOutcomes.length ? '✓ Aktarıma hazır' : 'Eşleştirme eksik'}</span>
                        </div>
                        {outcomes.length > 0 ? outcomes.map((outcome) => {
                          const pct = Math.max(0, Math.min(100, Number(outcome.match_percentage) || 0));
                          return <div key={outcome.lo_id} style={{ marginTop: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}><span style={{ color: '#075985', fontWeight: 700 }}>{outcome.lo_code}</span><span style={{ color: '#0369a1', fontWeight: 700 }}>%{pct} uyum</span></div>
                            <div style={{ height: '7px', background: '#dbeafe', borderRadius: '999px', overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', background: pct >= 70 ? '#16a34a' : pct >= 45 ? '#f59e0b' : '#dc2626', borderRadius: '999px' }} /></div>
                          </div>;
                        }) : <div style={{ color: '#b45309', fontSize: '12px' }}>Bu soru için ÖÇ bulunamadı.</div>}
                        {programOutcomes.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>{programOutcomes.map((outcome) => <span key={outcome.id} style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '999px', padding: '3px 8px', fontSize: '12px', fontWeight: 700 }}>PÇ{outcome.number}</span>)}</div>}
                      </div>;
                    })()}
                    {renderOutcomeAssignment(index)}
                    {q.answer && (
                      <div className="answer-tag" style={{
                        marginTop: '12px',
                        marginLeft: '44px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#ecfdf3',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        Doğru Cevap: <strong>{q.answer}</strong>
                      </div>
                    )}
                    <div className="ai-actions" style={{ marginTop: '12px', marginLeft: '44px' }}>
                      <button type="button" className="ai-btn" onClick={() => openAiModal(q, 'replace')} disabled={savingPretest || checkingPretestTransfer || archiveReadOnly} style={{
                        background: '#023b58',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}>
                        Düzenle
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="action-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
              <button onClick={() => transferPretestDirectly(false)} className="btn btn-success" disabled={savingPretest || checkingPretestTransfer || archiveReadOnly || pretestTransfer?.transferred || !selectedCourse || !result} style={{ backgroundColor: '#059669', borderColor: '#059669' }}>
                {checkingPretestTransfer ? 'Aktarım kontrol ediliyor…' : savingPretest ? (savePretestMessage.includes('eşleştirmesi yapılıyor') ? 'ÖÇ/PÇ eşleştiriliyor…' : 'Aktarılıyor…') : pretestTransfer?.transferred ? 'Sorular aktarıldı ✓' : savePretestMessage ? 'Yeniden Dene' : t('pretest.transferToExam')}
              </button>
              {pretestChanged && (
                <button onClick={() => transferPretestDirectly(true)} className="btn btn-primary"
                  disabled={savingPretest || checkingPretestTransfer || archiveReadOnly}>
                  {savingPretest ? 'Güncelleniyor…' : 'Değişiklikleri Sınav Modülüne Güncelle'}
                </button>
              )}
            </div>
            {savePretestMessage && (
              <p style={{ marginTop: '12px', textAlign: 'center', fontSize: '14px', color: savePretestSuccess ? '#059669' : '#dc2626' }}>
                {savePretestMessage}
              </p>
            )}
            {(savePretestSuccess || !!examPoolSuccess) && (
              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                <a href={EXAM_MODULE_URL} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', padding: '10px 20px', backgroundColor: '#1d4ed8', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
                  {t('pretest.goToExamModule')}
                </a>
              </div>
            )}
          </div>
        );
      })()}

      {/* Sınav Havuzuna Aktarma Modal */}
      {examPoolModalOpen && (
        <div className="ai-modal-backdrop" onClick={closeExamPoolModal}>
          <div className="ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="ai-modal-header">
              <h4>📤 {t('pretest.transferToExam')}</h4>
              <button className="ai-close" onClick={closeExamPoolModal}>×</button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                {examPoolSource === 'quiz'
                  ? t('pretest.transferQuizDesc')
                  : examPoolSource === 'midterm'
                  ? t('pretest.transferMidtermDesc')
                  : examPoolSource === 'manual'
                  ? t('pretest.transferManualDesc')
                  : t('pretest.transferPretestDesc')}
              </p>
              <div style={{
                padding: '12px',
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                color: '#92400e',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                lineHeight: '1.5'
              }}>
                ⚠️ <strong>{t('pretest.warning')}:</strong> {t('pretest.transferWarning')}
              </div>

              {examPoolSource === 'pretest' ? (
                <div style={{ marginBottom: '16px', padding: '10px 12px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', fontSize: '14px', color: '#065f46' }}>
                  <strong>Sınav türü:</strong> Öntest
                </div>
              ) : examPoolSource === 'manual' ? (
                <div style={{ marginBottom: '16px', display: 'grid', gap: '8px' }}>
                  <div style={{ padding: '10px 12px', background: '#f1f5f9', borderRadius: '8px', fontSize: '14px', color: '#374151' }}>
                    <strong>Soru kategorisi:</strong>{' '}
                    {QUESTION_CATEGORY_OPTIONS.find(item => item.value === manualQuestionCategory)?.label || '—'}
                  </div>
                  <div style={{ padding: '10px 12px', background: '#f1f5f9', borderRadius: '8px', fontSize: '14px', color: '#374151' }}>
                    <strong>Değerlendirme / Sınav Türü:</strong>{' '}
                    {examTypes.find(item => String(item.id) === String(manualExamWeightId))?.exam_type || '—'}
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                    {t('pretest.examTypeRequired')}
                  </label>
                  <select
                    value={selectedExamType}
                    onChange={(e) => setSelectedExamType(e.target.value)}
                    disabled={examPoolLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      backgroundColor: '#fff',
                      cursor: examPoolLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {examTypes.filter(et => et.exam_type).map((et, idx) => (
                      <option key={idx} value={et.exam_type}>
                        {et.exam_type} {et.weight ? `(${t('pretest.homeworkWeight')} %${et.weight})` : ''}
                      </option>
                    ))}
                  </select>
                  {!examPoolLoading && examTypes.filter(et => et.exam_type).length === 0 && (
                    <p style={{ marginTop: '8px', color: '#dc2626', fontSize: '13px' }}>
                      ⚠️ {t('pretest.examTypeNotDefined')}
                    </p>
                  )}
                  {!examPoolLoading && !selectedExamType && (
                    <p style={{ marginTop: '8px', color: '#dc2626', fontSize: '13px' }}>
                      ⚠️ {t('pretest.examTypeSelectRequired')}
                    </p>
                  )}
                </div>
              )}

              <div style={{
                padding: '12px',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#4b5563'
              }}>
                <strong>{t('pretest.subjectLabel')}</strong> {selectedCourse?.courseName || t('pretest.selectCourse')}<br />
                <strong>{t('pretest.transferQuestionCount')}</strong> {
                  examPoolSource === 'manual'
                    ? savedManualQuestions.length
                    : examPoolSource === 'quiz'
                    ? (quizResult?.parsed_questions?.questions?.length || 0)
                    : examPoolSource === 'midterm'
                    ? (midtermResult?.parsed_questions?.questions?.length || 0)
                    : (previewQuestions.length > 0
                      ? previewQuestions.length
                      : (result?.parsed_questions?.length || result?.question_count || 0))
                }
              </div>

              {examPoolSource === 'pretest' && (
                <div style={{ marginBottom: '16px', maxHeight: '330px', overflowY: 'auto', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', background: '#f8fbff' }}>
                  <strong style={{ display: 'block', marginBottom: '10px', color: '#1e3a5f' }}>Soru bazında ÖÇ / PÇ eşleştirmesi</strong>
                  {(previewQuestions.length ? previewQuestions : parseTestContent(result?.test_content || '')).map((question, index) => {
                    const match = quizQuestionMatches.find(item => item.question_index === index);
                    const outcomes = match?.learning_outcomes || [];
                    const pcs = match?.program_outcomes || [];
                    return <div key={index} style={{ padding: '9px 0', borderTop: index ? '1px solid #dbeafe' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 700, color: '#334155' }}><span>{index + 1}. soru</span><span style={{ color: outcomes.length && pcs.length ? '#15803d' : '#b45309' }}>{match ? (outcomes.length && pcs.length ? '✓ Aktarıma hazır' : 'Eşleştirme eksik') : 'Hesaplanıyor…'}</span></div>
                      {outcomes.map(outcome => { const pct = Math.max(0, Math.min(100, Number(outcome.match_percentage) || 0)); return <div key={outcome.lo_id} style={{ marginTop: '7px' }}><div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}><b style={{ color: '#075985' }}>{outcome.lo_code}</b><span>%{pct} uyum</span></div><div style={{ height: '6px', marginTop: '3px', background: '#dbeafe', borderRadius: '99px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${pct}%`, background: pct >= 70 ? '#16a34a' : pct >= 45 ? '#f59e0b' : '#dc2626' }} /></div></div>})}
                      {pcs.length > 0 && <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '7px' }}>{pcs.map(pc => <span key={pc.id} style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '999px', color: '#7e22ce', background: '#f3e8ff', fontWeight: 700 }}>PÇ{pc.number}</span>)}</div>}
                    </div>;
                  })}
                </div>
              )}

              {examPoolError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  whiteSpace: 'pre-line',
                  overflowWrap: 'anywhere'
                }}>
                  ⚠️ {examPoolError}
                </div>
              )}

              {examPoolSuccess && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0fdf4',
                  color: '#16a34a',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  ✅ {examPoolSuccess}
                </div>
              )}

              {(examPoolLoading || examPoolProgress > 0) && (
                <div style={{ margin: '0 0 16px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13, color: '#374151' }}>
                    <span>{examPoolProgressMsg}</span>
                    <span style={{ fontWeight: 600 }}>{examPoolProgress}%</span>
                  </div>
                  <div style={{ background: '#e5e7eb', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                    <div style={{
                      width: `${examPoolProgress}%`,
                      height: '100%',
                      background: examPoolProgress === 100
                        ? 'linear-gradient(90deg, #10b981, #059669)'
                        : 'linear-gradient(90deg, #3b82f6, #2563eb)',
                      borderRadius: 6,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={closeExamPoolModal}
                  className="btn btn-secondary"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6b7280',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {t('common.close')}
                </button>
                <button
                  onClick={examPoolSource === 'pretest' ? () => { closeExamPoolModal(); transferPretestDirectly(false); } : transferToExamPool}
                  className="btn btn-primary"
                  disabled={examPoolLoading || examPoolSuccess}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: examPoolSuccess ? '#9ca3af' : '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: examPoolLoading || examPoolSuccess ? 'not-allowed' : 'pointer'
                  }}
                >
                  {examPoolLoading ? t('pretest.transferring') : examPoolSuccess ? t('pretest.transferred') : t('pretest.transferToPool')}
                </button>
              </div>
              {examPoolSuccess && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <a
                    href={EXAM_MODULE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#1d4ed8',
                      color: '#fff',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {t('pretest.goToExamModule')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quiz Sonuçları */}
      {currentView === 'quiz' && quizResult && displayQuizResult()}

      {/* Ara Sınav Sonuçları */}
      {currentView === 'midterm' && midtermResult && displayMidtermResult()}

      {/* Ödev Sonuçları */}
      {currentView === 'homework' && homeworkResult && displayHomeworkResult()}

      {/* AI Modal - Tüm modüller için ortak */}
      {aiModalOpen && aiSelectedQuestion && (
        <div className="ai-modal-backdrop" onClick={closeAiModal}>
          <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <h4>{t('pretest.aiEditTitle')}</h4>
              <button className="ai-close" onClick={closeAiModal}>×</button>
            </div>
            <div className="ai-selected">
              {aiSelectedQuestion.number && (
                <div className="question-chip">{aiSelectedQuestion.number}</div>
              )}
              {aiSelectedQuestion.difficulty && (
                <div className="question-chip" style={{
                  backgroundColor:
                    aiSelectedQuestion.difficulty === 'KOLAY' ? '#28a745' :
                      aiSelectedQuestion.difficulty === 'ORTA' ? '#ffc107' : '#dc2626'
                }}>
                  {aiSelectedQuestion.difficulty}
                </div>
              )}
              <div className="question-text"><MathText>{aiSelectedQuestion.question}</MathText></div>
            </div>
            <div className="ai-mode-toggle">
              {currentView === 'pretest' ? (
                // Öntest modülü: Kolaylaştır, Zorlaştır, Değiştir ve Manuel Düzenle
                <>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'easy' ? 'active' : ''}`}
                    onClick={() => handleAiModify('easy')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'easy' ? t('pretest.aiEasifying') : t('pretest.aiEasify')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'hard' ? 'active' : ''}`}
                    onClick={() => handleAiModify('hard')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'hard' ? t('pretest.aiHardening') : t('pretest.aiHarden')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'replace' ? 'active' : ''}`}
                    onClick={() => handleAiModify('replace')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'replace' ? t('pretest.aiReplacing') : t('pretest.aiReplace')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'directEdit' ? 'active' : ''}`}
                    onClick={openManualEdit}
                    disabled={aiModifying}
                  >
                    {t('pretest.aiManualEdit')}
                  </button>
                </>
              ) : currentView === 'difficulty' ? (
                // Çalışma konuları modülü: Soruyu Değiştir + Manuel Düzenle
                <>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'replace' ? 'active' : ''}`}
                    onClick={() => handleAiModify('replace')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'replace' ? t('pretest.aiReplacing') : t('pretest.aiReplace')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'directEdit' ? 'active' : ''}`}
                    onClick={openManualEdit}
                    disabled={aiModifying}
                  >
                    {t('pretest.aiManualEdit')}
                  </button>
                </>
              ) : currentView === 'midterm' ? (
                // Ara sınav modülü: Tüm seçenekler + Manuel Düzenle
                <>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'easy' ? 'active' : ''}`}
                    onClick={() => handleAiModify('easy')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'easy' ? t('pretest.aiEasifying') : t('pretest.aiEasify')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'hard' ? 'active' : ''}`}
                    onClick={() => handleAiModify('hard')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'hard' ? t('pretest.aiHardening') : t('pretest.aiHarden')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'replace' ? 'active' : ''}`}
                    onClick={() => handleAiModify('replace')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'replace' ? t('pretest.aiReplacing') : t('pretest.aiReplace')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'directEdit' ? 'active' : ''}`}
                    onClick={openManualEdit}
                    disabled={aiModifying}
                  >
                    {t('pretest.aiManualEdit')}
                  </button>
                </>
              ) : (
                // Kısa sınav modülü: Tüm seçenekler + Manuel Düzenle
                <>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'easy' ? 'active' : ''}`}
                    onClick={() => handleAiModify('easy')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'easy' ? t('pretest.aiEasifying') : t('pretest.aiEasify')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'hard' ? 'active' : ''}`}
                    onClick={() => handleAiModify('hard')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'hard' ? t('pretest.aiHardening') : t('pretest.aiHarden')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'replace' ? 'active' : ''}`}
                    onClick={() => handleAiModify('replace')}
                    disabled={aiModifying}
                  >
                    {aiModifying && aiMode === 'replace' ? t('pretest.aiReplacing') : t('pretest.aiReplace')}
                  </button>
                  <button
                    type="button"
                    className={`ai-mode-btn ${aiMode === 'directEdit' ? 'active' : ''}`}
                    onClick={openManualEdit}
                    disabled={aiModifying}
                  >
                    {t('pretest.aiManualEdit')}
                  </button>
                </>
              )}
            </div>
            {aiMode === 'directEdit' && manualEditData && (
              <div className="manual-edit-form">
                <div className="manual-edit-field">
                  <label>{t('pretest.aiQuestionText')}</label>
                  <MathField
                    multiline
                    rows={4}
                    value={manualEditData.question}
                    onChange={(v) => setManualEditData({ ...manualEditData, question: v })}
                  />
                </div>
                {(currentView === 'quiz' || currentView === 'midterm') && manualEditData.options && manualEditData.option_keys && (
                  <>
                    <div className="manual-edit-options">
                      {manualEditData.option_keys.map(key => (
                        <div key={key} className="manual-edit-option" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <span className="manual-option-label" style={{ paddingTop: '6px' }}>{key})</span>
                          <div style={{ flex: 1 }}>
                            <MathField
                              value={manualEditData.options[key] || ''}
                              onChange={(v) => setManualEditData({
                                ...manualEditData,
                                options: { ...manualEditData.options, [key]: v }
                              })}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="manual-edit-field manual-edit-answer">
                      <label>{t('pretest.aiCorrectAnswer')}</label>
                      <select
                        value={manualEditData.correct_answer}
                        onChange={(e) => setManualEditData({ ...manualEditData, correct_answer: e.target.value })}
                      >
                        {manualEditData.option_keys.map(key => (
                          <option key={key} value={key}>{key}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                {currentView === 'pretest' && manualEditData.optionsArray && manualEditData.optionsArray.length > 0 && (
                  <>
                    <div className="manual-edit-options">
                      {manualEditData.optionsArray.map((opt, idx) => {
                        const key = ['A', 'B', 'C', 'D', 'E'][idx] || String(idx + 1);
                        return (
                          <div key={idx} className="manual-edit-option" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span className="manual-option-label" style={{ paddingTop: '6px' }}>{key})</span>
                            <div style={{ flex: 1 }}>
                              <MathField
                                value={opt}
                                onChange={(v) => {
                                  const newOpts = [...manualEditData.optionsArray];
                                  newOpts[idx] = v;
                                  setManualEditData({ ...manualEditData, optionsArray: newOpts });
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="manual-edit-field manual-edit-answer">
                      <label>{t('pretest.aiCorrectAnswer')}</label>
                      <select
                        value={manualEditData.answer}
                        onChange={(e) => setManualEditData({ ...manualEditData, answer: e.target.value })}
                      >
                        {manualEditData.optionsArray.map((_, idx) => {
                          const key = ['A', 'B', 'C', 'D', 'E'][idx] || String(idx + 1);
                          return <option key={key} value={key}>{key}</option>;
                        })}
                      </select>
                    </div>
                  </>
                )}
                <div className="ai-modal-actions">
                  <button type="button" className="ai-btn" onClick={applyManualEdit}>
                    {t('common.save')}
                  </button>
                  <button type="button" className="ai-btn secondary" onClick={closeAiModal}>
                    {t('common.close')}
                  </button>
                </div>
              </div>
            )}
            {aiMode === 'manual' && (
              <>
                <div className="ai-prompt-box">
                  <label>{t('pretest.aiNewQuestion')}</label>
                  <textarea
                    placeholder={`1. ${t('pretest.aiQuestionText')}...\nA) ...\nB) ...\nC) ...\nD) ...\n${t('pretest.aiCorrectAnswer')}: B`}
                    value={aiOutputText}
                    onChange={(e) => setAiOutputText(e.target.value)}
                    rows={8}
                  />
                </div>
                <div className="ai-modal-actions">
                  <button type="button" className="ai-btn" onClick={applyAiResultToQuestion}>
                    {t('pretest.aiApplyToQuestion')}
                  </button>
                  <button type="button" className="ai-btn secondary" onClick={closeAiModal}>
                    {t('common.close')}
                  </button>
                </div>
              </>
            )}
            {aiMode !== 'manual' && aiMode !== 'directEdit' && (
              <>
                {error && (
                  <div style={{
                    padding: '12px',
                    background: '#fee',
                    color: '#c33',
                    borderRadius: '6px',
                    marginTop: '12px',
                    fontSize: '14px'
                  }}>
                    {error}
                  </div>
                )}
                <div className="ai-modal-actions">
                  <button type="button" className="ai-btn secondary" onClick={closeAiModal} disabled={aiModifying}>
                    {t('common.close')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pretest;
